/**
 * Client API minimaliste. Utilise fetch natif + cookie credential.
 *
 * Le refresh token est dans un cookie HttpOnly (géré côté navigateur).
 * L'access token est en mémoire (variable module — pas localStorage pour XSS).
 *
 * Si une requête 401 arrive, on tente un refresh automatique une seule fois.
 */

import type {
  ApiError,
  InboxItem as InboxItemShared,
  GrcMessage as GrcMessageDetailShared,
  GrcAlerte as GrcAlerteShared,
  GrcAlerteDetail as GrcAlerteDetailShared,
  GrcAlertePreset as GrcAlertePresetShared,
  GrcIncident as GrcIncidentShared,
  GrcNewsletter as GrcNewsletterShared,
  GrcNewsletterDetail as GrcNewsletterDetailShared,
  GrcNewsletterArticle as GrcNewsletterArticleShared,
  VehiculeWithKpis as VehiculeWithKpisShared,
  VehiculeDetail as VehiculeDetailShared,
  VehiculeEntreeCarnet as VehiculeEntreeCarnetShared,
  OcrFactureResult as OcrFactureResultShared,
  MaterielCatalogue as MaterielCatalogueShared,
  MaterielReservation as MaterielReservationShared,
  TenantDetail as TenantDetailShared,
  UpdateTenantInput as UpdateTenantInputShared,
  SignalementPhoto as SignalementPhotoShared,
  DevisDemande as DevisDemandeShared,
  ChecklistItem as ChecklistItemShared,
  EtatCourantLieu as EtatCourantLieuShared,
  FichePhoto as FichePhotoShared,
  StRelecteur as StRelecteurShared,
  EdlTemplate as EdlTemplateShared,
  SignalementPhotoKind as SignalementPhotoKindShared,
  SignalementPhotoSignedUrlResponse as SignalementPhotoSignedUrlResponseShared,
  BackupStatusResponse as BackupStatusResponseShared,
} from '@roronoa-collectivites/shared-types';

type InboxItem = InboxItemShared;
type GrcMessageDetail = GrcMessageDetailShared;

const API_BASE = ''; // proxy Vite vers backend en dev, même origin en prod

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Télécharge un fichier (blob) depuis un endpoint authentifié et déclenche le download. */
/**
 * Télécharge un fichier authentifié (blob) depuis l'API.
 * Historiquement nommé `downloadCompta` (créé pour la comptabilité), il sert
 * désormais à tous les exports : plannings, documents, états. Exporté sous
 * l'alias `downloadFichier` en bas de fichier.
 */
async function downloadCompta(url: string, filename: string): Promise<void> {
  const token = getAccessToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' });
  if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: `HTTP ${res.status}`, code: 'DOWNLOAD_FAILED' })));
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(objectUrl);
}

export class ApiException extends Error {
  constructor(
    public status: number,
    public payload: ApiError
  ) {
    super(payload.error);
  }
}

async function refreshOnce(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return false;
    const data = (await res.json()) as { accessToken: string };
    accessToken = data.accessToken;
    return true;
  } catch {
    return false;
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  retryAfterRefresh = true
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  // 401 = pas/plus authentifié → refresh + retry.
  // 403 juste après (re)connexion = token encore absent en mémoire alors qu'une
  // session cookie valide existe : on tente UN refresh pour (re)poser le token,
  // puis on retente une seule fois. Si le 403 persiste, c'est un vrai refus de droit.
  //
  // ⚠️ Sécurité doublons : on ne rejoue JAMAIS automatiquement une requête NON
  // idempotente (POST). Un POST rejoué après refresh peut créer deux fois la même
  // ressource (cas vécu : associations créées en double). Pour ces méthodes, on
  // (re)pose juste le token via refresh SANS rejouer, et on laisse l'appel échouer
  // proprement — l'utilisateur réessaie avec un token désormais valide.
  const method = (options.method ?? 'GET').toUpperCase();
  const idempotent = method === 'GET' || method === 'HEAD' || method === 'PUT'
    || method === 'DELETE' || method === 'PATCH';
  const peutRetenter = retryAfterRefresh && path !== '/api/auth/refresh' && path !== '/api/auth/login';
  const token403Transitoire = res.status === 403 && !accessToken;
  if ((res.status === 401 || token403Transitoire) && peutRetenter) {
    const refreshed = await refreshOnce();
    if (refreshed && idempotent) {
      return apiFetch<T>(path, options, false);
    }
    // POST : token re-posé, mais on ne rejoue pas. L'erreur ci-dessous remonte.
  }

  if (!res.ok) {
    let payload: ApiError;
    try {
      payload = (await res.json()) as ApiError;
    } catch {
      payload = { error: `HTTP ${res.status}` };
    }
    if (payload.code === 'PLAN_INACTIF' || payload.code === 'PLAN_INACTIF_LECTURE_SEULE') {
      try { window.dispatchEvent(new CustomEvent('vpc:plan-inactif', { detail: payload })); } catch { /* SSR / non-DOM */ }
    }
    throw new ApiException(res.status, payload);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

/**
 * Variante d'apiFetch pour les uploads multipart (FormData).
 * Ne définit PAS de Content-Type : le navigateur ajoute automatiquement
 * le boundary correct quand body = FormData. Si on settait
 * 'application/json', le serveur ne pourrait pas parser le multipart.
 *
 * L13.8 — utilisé pour uploadPhoto.
 */
export async function apiFetchRaw<T>(
  path: string,
  options: RequestInit = {},
  retryAfterRefresh = true
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  // Surtout NE PAS définir Content-Type ici — le navigateur le calcule

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (res.status === 401 && retryAfterRefresh && path !== '/api/auth/refresh' && path !== '/api/auth/login') {
    const refreshed = await refreshOnce();
    if (refreshed) {
      return apiFetchRaw<T>(path, options, false);
    }
  }

  if (!res.ok) {
    let payload: ApiError;
    try {
      payload = (await res.json()) as ApiError;
    } catch {
      payload = { error: `HTTP ${res.status}` };
    }
    if (payload.code === 'PLAN_INACTIF' || payload.code === 'PLAN_INACTIF_LECTURE_SEULE') {
      try { window.dispatchEvent(new CustomEvent('vpc:plan-inactif', { detail: payload })); } catch { /* SSR / non-DOM */ }
    }
    throw new ApiException(res.status, payload);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

// ---------- API auth typée ----------

import type {
  RegisterRequest,
  LoginRequest,
  AuthResponse,
  MeResponse,
  TwoFactorSetupResponse,
  Signalement,
  CreateSignalementInput,
  UpdateSignalementStatutInput,
  SignalementListResponse,
  SignalementListFilters,
  SignalementEvenement,
  // Passe 2A.1
  RegisterAdministreRequest,
  RegisterAdministreResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  TenantPublicListResponse,
  LegalVersionsResponse,
  // Livraison 2 — Bloc A backoffice admin
  AdminUserListItem,
  AdminUserDetail,
  AdminUserListFilters,
  AdminUserListResponse,
  AdminUserUpdateRequest,
  AdminRoleMatrixResponse,
  AdminUpdateRolePermissionsRequest,
  AdminAuditLogFilters,
  AdminAuditLogResponse,
  // Livraison 3 — Bloc A.2 plateforme cross-tenant
  PlatformTenantListItem,
  PlatformTenantListFilters,
  PlatformTenantListResponse,
  PlatformTenantUpdateRequest,
  PlatformImpersonateResponse,
  PlatformAuditLogFilters,
  PlatformAuditLogResponse,
  // Livraison 5 — Bloc B compte utilisateur
  UpdateMyProfileRequest,
  ChangeMyPasswordRequest,
  // Livraison 6 — Bloc C invitations
  AdminInvitation,
  AdminInvitationListResponse,
  CreateInvitationRequest,
  VerifyInvitationResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  UserRole,
  // Module Scolaire
  ScolaireFamille, ScolaireFamilleDetail, ScolaireEnfant, ScolaireResponsable,
  DossierScolaire, DossierScolaireDetail, DossierScolaireStatut,
  DossierPieceDemandee, DossierPieceDeposee,
  ScolaireEcole, ScolaireClasse, ScolaireParametres, InscriptionStatut, InscriptionFenetre,
  Menu, MenuUpsertInput, MenuSyncConfig, MenuSyncConfigInput,
  CantineReservation, CantineCalendrierResponse,
  CreateFamilleInput, UpdateFamilleInput,
  CreateEnfantInput, UpdateEnfantInput, UpdateEnfantSensibleInput,
  CreateCantineInscriptionInput, CreateCantineInscriptionResult,
  ToggleReservationInput, ReservationStatut, ScolaireJourService,
  ScolaireImportResult,
  CantineListeJour, PeriscolaireListeJour,
  CreatePeriscolaireInscriptionInput, CreatePeriscolaireInscriptionResult,
  PeriscolaireInscription, PeriscolaireCalendrierResponse,
  PeriscolaireReservation, PeriscolaireEffectifJour,
  ScolairePlanningCreneau, ScolairePlanningType, ScolairePlanningStatut, ScolairePlanningPlage,
  ScolairePlanningProfil, ProfilPlage, GenererPlanningResult, JourNonTravaille,
  PlanningProfil, CreatePlanningProfilInput, GenererPlanningStInput,
  PlanningPresenceJour, PlanningAnneeResponse, PlanningWeekResponse,
  PlanningEquipe, CreatePlanningEquipeInput, AssignMultiInput,
  PlanningCreneau,
  PlanningTemplate, CreatePlanningTemplateInput,
  RhConsolidationResult,
  RhParametres, RhBaseConges, RhModeDecompte, RhSoldesResult, RhSoldeConges,
  RhHeuresSup, RhHeuresSupCompteur, RhAbsence, RhHoraireModifie,
  RhClotureParams, RhPeriodePaie, RhAgentFiltrable, RhPlanningsAgreges, RhPresenceAnnee, RhProfilAgrege, RhJustificatif, RhDocument,
  RhHabilitation, RhHabilitationType,
  GrcPlanningCreneau, GrcPlanningProfil, CreateGrcPlanningProfilInput, GenererGrcPlanningInput,
  RestaurationTarif, RestaurationTarifInput, FacturationApercu, Facture, FactureStatut,
  ServiceFacturable, IntegrationsStatut, QfRecupere, PayFipDemande, MoyenEncaissement,
  AssociationResume, AssociationDetail, UpsertAssociationInput, AssociationsDashboard,
  NdfTypeFrais, NdfBaremeKm, NdfNote, NdfLigne, NdfTiers, NdfStatut,
  AnnuaireTiers, TiersCategorieUsage,
  AssociationRepresentant, RepresentantFonction, MonAssociation,
  AssociationDossier, AssociationDossierDetail, AssociationDossierStatut,
  AssociationPieceDemandee, AssociationPieceDeposee,
  Subvention,
  SubventionCampagne, SubventionCampagneInput,
  RhVisiteMedicale, RhVisiteMedicaleInput, RhSuiviReglages,
  ErpVisiteSecurite, ErpASuivre, ErpAvisVisite,
  BatimentContrat, BatimentContratInput, ContratCategorie, ContratPeriodicite,
  PorteBatiment, CleSortie,
  ArchivesSerie, ArchivesCategorie, ArchivesContenant, ArchivesUnite,
  ArchivesRecolement, ArchivesRecolementLigne,
  RhAgentDossier, RhAgentDossierInput,
  RhReferentiels, RhGrade, RhCadreEmploi, RhServiceRef, RhOrdreMission,
  AgentsLienCategorie, AgentsLienUtile, AgentsContactUrgence, AgentsEspacePayload,
  AnnuaireInterneReponse, AnnuaireInterneReglages,
  DiffusionAAcquitter, SuiviDiffusion, ReglagePorteeDiffusion, RetourAttendu,
  DiffusionCibleType, PorteeDiffusion,
  BriqueDelegation, RegimeDelegation, ResponsableTraitement,
  ActeModele, ActeCircuitIntervenant,
  ActeRegistre, StatistiquesRegistre, StatutActe,
  ImportApercu, ImportResultat, SubventionStatut, SubventionUpsertInput,
  AssociationAnnuaireItem, AssociationAnnonce, AssociationPublicationType, AssociationPresenceReponse, AssociationPresenceRecap,
} from '@roronoa-collectivites/shared-types';

/**
 * Casquette visée par la notification (lot N1 — « règle des deux casquettes »).
 *
 *   · 'metier'     : notification de TRAVAIL (agent/manager/maire). Le
 *                    destinataire ne peut rien couper — c'est le maire ou la RH
 *                    qui décide, via le catalogue commune.
 *   · 'administre' : SERVICE rendu à l'habitant, qui choisit ses canaux.
 *
 * Une même personne porte les deux : un agent qui habite la commune reçoit les
 * deux types de notifications. La cloche les affiche ENSEMBLE — la distinction
 * sert au routage (qui peut couper quoi), pas à l'affichage.
 */
export type NotificationContexte = 'metier' | 'administre';


/** Machine à états (vague 4a) — la forme complète d'un parcours. */

/** Moteur de projet (vague 4b) — la fiche complète avec ses paliers datés. */

/** Machine à états — une instance vivante et ses actions possibles. */

/** Budget par secteur (vague 4d). */
export interface BudgetSuivi {
  annee: number;
  secteurs: Array<{ id: string; annee: number; secteur: string; montantAlloue: number; consomme: number; restant: number; enDepassement: boolean; note: string | null }>;
  global: { alloue: number; consomme: number; restant: number };
  mouvements?: BudgetMouvement[];
}
export interface BudgetMouvement {
  id: string; annee: number; secteur: string; sens: 'debit' | 'credit';
  montant: number; motif: string; entiteLabel: string | null; createdAt: string;
}

export interface WorkflowInstance {
  id: string;
  definitionId: string;
  definitionLibelle: string;
  etat: { id: string; code: string; libelle: string; estFinal: boolean; couleur: string | null };
  transitionsDisponibles: Array<{ id: string; libelle: string; pieceRequise: boolean; autorisee: boolean }>;
  /** M7 — toutes les étapes du parcours, dans l'ordre (frise graphique). */
  etats?: Array<{ id: string; code: string; libelle: string; ordre: number; estInitial: boolean; estFinal: boolean; couleur: string | null }>;
}

export interface ProjetDetail {
  id: string; intitule: string; description: string | null;
  secteur: string | null; responsableUserId: string | null; responsable: string | null;
  datePivot: string | null;
  lieuId: string | null; lieuNom: string | null;
  occupation: { dateDebut: string; dateFin: string } | null;
  definitionId: string; definitionLibelle: string;
  instance: {
    id: string; etat: { id: string; code: string; libelle: string; estFinal: boolean; couleur: string | null };
    transitionsDisponibles: Array<{ id: string; libelle: string; pieceRequise: boolean; autorisee: boolean }>;
  };
  paliers: Array<{
    etatId: string; code: string; libelle: string; ordre: number; couleur: string | null;
    estFinal: boolean; dateButoir: string | null; note: string | null;
    atteint: boolean; courant: boolean; enRetard: boolean;
  }>;
  progression: { faits: number; total: number };
  historique: Array<{ id: string; deEtat: string | null; versEtat: string; par: string | null; commentaire: string | null; aPiece: boolean; createdAt: string }>;
}

export interface EnvoiConfig {
  canal: 'interne' | 'email' | 'sms';
  gabaritSujet: string;
  gabaritCorps: string;
  validationRequise: boolean;
  validateurs: string[];
}
export interface EnvoiDestinataire {
  type: 'user' | 'tiers' | 'libre';
  id?: string;
  valeur?: string;
  label: string;
}
export interface WorkflowEnvoi {
  id: string;
  instanceId: string;
  flecheLibelle: string;
  canal: 'interne' | 'email' | 'sms';
  statut: 'a_preparer' | 'a_valider' | 'envoye' | 'echec' | 'refuse' | 'annule';
  sujet: string;
  corps: string;
  destinataires: EnvoiDestinataire[];
  validationRequise: boolean;
  validateurs: string[];
  peutValider: boolean;
  refusCommentaire: string | null;
  resultat: Array<{ label: string; ok: boolean; raison?: string }> | null;
  envoyeAt: string | null;
  createdAt: string;
}
export interface AnnuaireDestinataires {
  users: Array<{ id: string; label: string; role: string; service: string | null; aEmail: boolean; aTelephone: boolean }>;
  tiers: Array<{ id: string; label: string; aEmail: boolean; aTelephone: boolean }>;
}

export interface WorkflowDefinition {
  id: string; code: string; libelle: string; description: string | null;
  presetSource: string | null; actif: boolean; instances: number;
  etats: Array<{ id: string; code: string; libelle: string; ordre: number; estInitial: boolean; estFinal: boolean; couleur: string | null; echeanceJours: number | null }>;
  transitions: Array<{ id: string; deEtatId: string; versEtatId: string; libelle: string; roleMinimum: string; serviceRequis: string | null; pieceRequise: boolean; envoiConfig?: EnvoiConfig | null; ordre: number; recrediteBudget?: boolean; evenementNotification?: string | null }>;
}

export interface NotificationItem {
  id: string;
  /**
   * Miroir du référentiel `notification_types` (serveur, migration 189).
   * ⚠️ Cette liste était incomplète avant le lot N1 : elle n'en déclarait que 4
   * sur 16, alors que le serveur envoie bien les autres (urbanisme, association,
   * materiel_reservation…). Le front recevait donc des types que TS ignorait.
   */
  type:
    | 'signalement_statut' | 'alerte' | 'message_interne' | 'materiel_reservation'
    | 'dossier_scolaire' | 'association' | 'referendum' | 'urbanisme'
    | 'dechets_rappel' | 'rdv_rappel' | 'rdv_creneaux_vides'
    | 'concession_echeance' | 'concession_demande' | 'idee' | 'collecte_dossier'
    | 'sms_solde_bas' | 'ndf_a_valider';
  contexte: NotificationContexte;
  titre: string;
  corps: string | null;
  lien: string | null;
  signalementId: string | null;
  lu: boolean;
  createdAt: string;
}

/** Le périmètre d'un administrateur (« bascule DGS », migration 198). */
export interface AdminPerimetre {
  userId: string;
  nom: string;
  email: string;
  /** null = accès complet (le maire). Le défaut. */
  briques: string[] | null;
  vueMaire: boolean;
  parametres: boolean;
}

/** Un type de lieu (bac à sable, mig 203). `code` stable, `libelle` renommable. */
export interface LieuTypeDTO {
  id: string;
  code: string;
  libelle: string;
  ordre: number;
  actif: boolean;
  nbLieux: number;
}

/** Un établissement occupant un lieu (S4-b). */
export interface OccupantLieuDTO {
  id: string;
  nom: string;
  service: 'scolaire' | 'petite_enfance' | 'materiel';
  serviceLibelle: string;
}

/** Un lieu communal (référentiel, mig 203). */
export type { EdlTemplate, EdlTemplatePoint } from '@roronoa-collectivites/shared-types';

/**
 * 9.2 — les clés du bâtiment d'une salle réservée, vues depuis la réservation.
 *
 * `lieuRattache: false` = la salle n'a pas de lieu (cas des salles créées avant
 * le référentiel, ou d'une réservation de matériel). L'écran masque le bloc.
 * `cles: []` avec `lieuRattache: true` = le lieu existe mais aucune clé n'y a
 * encore été déclarée : on invite à le faire, on ne bloque pas.
 */
export interface CleDisponibleDTO {
  cleId: string;
  lieu: string;
  porte: string;
  exemplaire: number;
  numeroGrave: string | null;
  etat: string;
  sortie: boolean;
  pourCetteReservation: boolean;
  detenueParEmprunteur: string | null;
}

export interface ClesReservationVue {
  lieuRattache: boolean;
  lieuId: string | null;
  lieuNom: string | null;
  cles: CleDisponibleDTO[];
  remise: {
    remises: boolean;
    nbCles: number;
    remiseLe: string | null;
    parNom: string | null;
    parService: string | null;
  };
}

/** 9.1 — l'état du contingent de débits de boissons d'une association. */
export interface ContingentBuvetteDTO {
  associationId: string | null;
  associationNom: string | null;
  delivrees: number;
  contingent: number;
  prochainRang: number;
  depasse: boolean;
  annee: number;
}


/** 9.3 — un accompagnement proposé par la mairie, et ses pièces exigées. */
export interface ServiceMairieDTO {
  id: string;
  libelle: string;
  description: string | null;
  brique: string;
  actif: boolean;
  ordre: number;
  /** 9.5 — un événement est une offre avec une date et, parfois, des places. */
  typeOffre: 'accompagnement' | 'evenement';
  organisateurType: string;
  organisateurNom: string | null;
  lieu: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  inscriptionsJusquAu: string | null;
  placesMax: number | null;
  placesPrises: number;
  pieces: Array<{ id: string; libelle: string; obligatoire: boolean; ordre: number; actif: boolean }>;
}

/** Ce qu'on envoie pour créer ou modifier une offre — accompagnement ou événement. */
export interface OffreMairieInput {
  libelle: string;
  description?: string | null;
  brique?: string;
  actif?: boolean;
  ordre?: number;
  typeOffre?: 'accompagnement' | 'evenement';
  organisateurType?: 'mairie' | 'association' | 'acteur';
  organisateurId?: string | null;
  organisateurNom?: string | null;
  lieu?: string | null;
  dateDebut?: string | null;
  dateFin?: string | null;
  inscriptionsJusquAu?: string | null;
  placesMax?: number | null;
}

export interface DemandeServiceDTO {
  id: string;
  serviceId: string;
  serviceLibelle: string;
  statut: string;
  message: string | null;
  reponse: string | null;
  motifRefus: string | null;
  beneficiaireType: string;
  beneficiaireNom: string | null;
  demandeurNom: string | null;
  soumisAt: string | null;
  traiteAt: string | null;
  createdAt: string;
  pieces: Array<{ id: string; pieceId: string | null; nomFichier: string; createdAt: string }>;
}

export interface LieuDTO {
  id: string;
  nom: string;
  typeId: string | null;
  typeLibelle: string | null;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  categorieErp: number | null;
  typeErp: string | null;
  capaciteErp: number | null;
  surfaceM2: number | null;
  louable: boolean;
  actif: boolean;
  notes: string | null;
  briques: string[];
}

/** Champs de création/édition d'un lieu. */
export interface LieuInput {
  nom: string;
  typeId?: string | null;
  adresse?: string | null;
  codePostal?: string | null;
  ville?: string | null;
  categorieErp?: number | null;
  typeErp?: string | null;
  capaciteErp?: number | null;
  surfaceM2?: number | null;
  louable?: boolean;
  notes?: string | null;
  briques?: string[];
}

/** Une entrée du catalogue des notifications (/admin/notifications, lot N3). */
export interface CatalogueNotificationEntree {
  /** Code de l'ÉVÉNEMENT (plus fin que le type : `association` en porte 6). */
  code: string;
  libelle: string;
  contexte: NotificationContexte;
  declencheur: string;
  cibleLabel: string;
  /** Regroupement d'affichage (« Urbanisme », « Associations »…). */
  groupe: string;
  /** Canaux effectifs : défaut éditeur, ou dérogation de la commune. */
  canaux: { cloche: boolean; email: boolean; push: boolean };
  /** La commune a dérogé au défaut éditeur sur au moins un canal. */
  personnalise: boolean;
  /** Personnes réellement ciblées dans cette commune. null = cible dynamique. */
  destinatairesActuels: number | null;
  /** L'utilisateur courant s'est retiré de cet événement. */
  desabonne: boolean;
}

/** Un délai de rappel réglable : quantité + unité (socle Lot B, mig 211). */
export type UniteRappel = 'jours' | 'mois' | 'annees';
export interface RappelDelai {
  quantite: number;
  unite: UniteRappel;
}
/** Une échéance de l'écran « Échéances & rappels ». */
export interface EcheanceReglageEntree {
  code: string;
  libelle: string;
  declencheur: string;
  cibleLabel: string;
  groupe: string;
  ordre: number;
  rappelsDefaut: RappelDelai[];
  rappelsOverride: RappelDelai[] | null;
  rappelsEffectifs: RappelDelai[];
}

export interface CarnetEntry {
  id: string;
  nomComplet: string;
  email: string;
  role: string;
}

export interface MessageAlerteRef {
  id: string;
  titre: string;
  niveau: string;
}

export interface InboxMessage {
  id: string;
  objet: string;
  expediteurNom: string;
  createdAt: string;
  lu: boolean;
  /** Traitement individuel : propre à CE destinataire (mig 235). */
  traite: boolean;
  archive: boolean;
  alerte?: MessageAlerteRef | null;
}

export interface SentMessage {
  id: string;
  objet: string;
  destinataires: string;
  createdAt: string;
  statut: 'brouillon' | 'envoye';
  archive: boolean;
  /** « Où ça en est » : 2/3, pas un booléen. */
  traitesCount: number;
  destinatairesCount: number;
  alerte?: MessageAlerteRef | null;
  retourAttendu?: 'prise_connaissance' | 'action_faite' | null;
  acquittesCount?: number;
}

export interface MessagerieGroupe {
  id: string;
  nom: string;
  description: string | null;
  actif: boolean;
  membresCount: number;
  estMembre: boolean;
  createdAt: string;
}

export interface MessagerieGroupeDetail extends MessagerieGroupe {
  membres: Array<{ userId: string; nomComplet: string; email: string; role: string }>;
}

export interface MessagerieSujet {
  id: string;
  objet: string;
  auteurNom: string;
  createdAt: string;
  reponsesCount: number;
  dernierMessageAt: string;
  luParMoi: boolean;
}

export interface MessagerieFil {
  groupeId: string;
  groupeNom: string;
  objet: string;
  messages: Array<{ id: string; corps: string; auteurNom: string; createdAt: string }>;
}

/** Enveloppe de pagination des trois boîtes. */
export interface PageMessages<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface MessageDetail {
  id: string;
  expediteurUserId: string;
  objet: string;
  corps: string;
  createdAt: string;
  expediteurNom: string;
  expediteurEmail: string;
  destinataires: Array<{ nomComplet: string; email: string }>;
  estExpediteur: boolean;
  alerte?: MessageAlerteRef | null;
}

export interface DashboardKpi {
  label: string;
  value: string;
  tone?: 'neutral' | 'good' | 'warn' | 'danger' | 'info';
}
export interface DashboardModuleCard {
  key: 'tech' | 'grc' | 'ecole' | 'asso' | 'salle' | 'rh';
  label: string;
  color: string;
  kpis: DashboardKpi[];
}
export interface DashboardAttentionPoint {
  key: string;
  label: string;
  value: number;
  tone: 'danger' | 'warn' | 'info';
}
export interface DashboardConsolide {
  commune: string | null;
  generatedAt: string;
  attention: DashboardAttentionPoint[];
  modules: DashboardModuleCard[];
}

// Données du rapport conseil (miroir partiel du backend export-conseil.service).
export interface ExportConseilData {
  tenantNom: string;
  tenantVille: string | null;
  debut: string;
  fin: string;
  comparaison: 'periode_precedente' | 'annee_precedente';
  prevDebut: string;
  prevFin: string;
  syntheseTexte: string;
  modules: { key: string; label: string; color: string; analyse: string; kpis: unknown[] }[];
  attention: { niveau: string; texte: string }[];
  genereLe: string;
}

export interface DetailKpi {
  label: string;
  value: string;
  sub?: string;
  tone?: 'neutral' | 'accent' | 'good' | 'warn' | 'danger' | 'info';
  drill?: { type: 'modal'; key: string } | { type: 'navigate'; to: string };
}
export interface KpiDetailRow {
  label: string;
  value: string;
  meta?: string;
  tone?: 'neutral' | 'good' | 'warn' | 'danger' | 'info';
}
export interface KpiDetail {
  title: string;
  total?: string;
  rows: KpiDetailRow[];
  note?: string;
}
export interface TrendPoint {
  label: string;
  value: number;
  highlight?: boolean;
}
export interface BreakdownItem {
  label: string;
  value: number;
  pct: number;
  color?: string;
}
export interface MapPoint {
  lat: number;
  lng: number;
  tone: 'danger' | 'warn' | 'good';
}
export interface QuickDecision {
  id: string;
  kind: string;
  tag: string;
  tone: 'danger' | 'warn' | 'info';
  title: string;
  meta?: string;
}
export interface DashboardDetail {
  module: string;
  label: string;
  accent: string;
  kpis: DetailKpi[];
  /** M11 — activité mensuelle du service technique rapportée à l'effectif. */
  activiteMensuelle?: {
    titre: string;
    definitions: string[];
    mois: Array<{
      mois: string; label: string; interventions: number; heures: number;
      effectif: number; mobilises: number; joursAbsence: number;
      tauxPresence: number | null; interventionsParAgent: number | null;
    }>;
  };
  trend: { title: string; unit: string; points: TrendPoint[] };
  breakdown?: { title: string; meta?: string; items: BreakdownItem[] };
  map?: { title: string; points: MapPoint[] };
  decisions: QuickDecision[];
  decisionTypes?: string[];
}

export interface CahierNote {
  id: string;
  contenu: string;
  categorie: 'rappel' | 'info' | 'a_transmettre' | 'urgent';
  statut: 'a_faire' | 'fait';
  assigneService: string | null;
  assigneUserId: string | null;
  assigneUserNom: string | null;
  creePar: string | null;
  creeParNom: string | null;
  faitPar: string | null;
  faitParNom: string | null;
  faitAt: string | null;
  createdAt: string;
  updatedAt: string;
}

// ── Pôle Info (GRC, migration 116) ──────────────────────────────────────────
export interface ServiceHoraire {
  id: string;
  nom: string;
  horaires: string | null;
  /** Item 6.5 — horaires de remplacement pendant une période saisonnière. */
  horairesEte: string | null;
  /** TRUE quand `horaires` porte déjà la version saisonnière (période en cours). */
  saisonnier?: boolean;
  adresse: string | null;
  telephone: string | null;
  email: string | null;
  note: string | null;
  affiche: boolean;
  ordre: number;
}
export interface PeriodeSaisonniere {
  id: string;
  nom: string;
  dateDebut: string;
  dateFin: string;
  preavisJours: number;
  enCours: boolean;
}

export type EtatCivilType = 'mariage' | 'pacs' | 'parrainage_civil' | 'deces';
export type EtatCivilStatut =
  | 'constitution' | 'complet' | 'bans_publies' | 'programme'
  | 'celebre' | 'clos' | 'abandonne';
export interface EtatCivilDossier {
  id: string;
  type: EtatCivilType;
  intitule: string;
  interesses: { nom: string; prenom: string; telephone?: string | null; email?: string | null }[];
  statut: EtatCivilStatut;
  pieces: { libelle: string; recue: boolean }[];
  bansPubliesLe: string | null;
  dateEvenement: string | null;
  notes: string | null;
  transitionsDisponibles: EtatCivilStatut[];
}

export interface LienDemarcheManuel { url: string; label: string }

export interface Demarche {
  id: string;
  titre: string;
  /** Liens posés par la mairie — prioritaires sur la détection automatique. */
  liens: LienDemarcheManuel[];
  texte: string | null;
  pieces: string[];
  pdfNom: string | null;
  aPdf: boolean;
  affiche: boolean;
  ordre: number;
}
export interface PoleInfoMenu {
  date: string;
  typeMenu: string;
  entree: string | null;
  plat: string | null;
  accompagnement: string | null;
  laitage: string | null;
  dessert: string | null;
}
export interface PoleInfoEvenement {
  id: string;
  titre: string;
  source: 'mairie' | 'association';
  associationNom: string | null;
  categorie: string | null;
  dateEvent: string | null;
  lieu: string | null;
  horaires: string | null;
}
// ── Déchets (GRC, migration 125) ────────────────────────────────────────────
export type DechetFlux = 'om' | 'tri' | 'verre' | 'vegetaux' | 'encombrants';
export type DechetFrequence = 'hebdo' | 'paire' | 'impaire';
export interface DechetsSecteur {
  id: string;
  nom: string;
  description: string | null;
  affiche: boolean;
  ordre: number;
}
export interface DechetsCollecte {
  id: string;
  secteurId: string;
  flux: DechetFlux;
  jour: number;
  frequence: DechetFrequence;
  consigne: string | null;
}
export interface ProchaineCollecte {
  flux: DechetFlux;
  date: string;
  jour: number;
  consigne: string | null;
}
export interface PoiPoint {
  id: string;
  famille: 'securite' | 'entraide' | 'tourisme';
  type: string;
  nom: string;
  description: string | null;
  adresse: string | null;
  latitude: number | null;
  longitude: number | null;
  horaires: string | null;
  note: string | null;
  telephone: string | null;
  siteWeb: string | null;
  photoUrl: string | null;
  origine: 'commune' | 'geotag';
  affiche: boolean;
  ordre: number;
  createdAt: string;
}

export interface PoiInput {
  famille: 'securite' | 'entraide' | 'tourisme';
  type: string;
  nom: string;
  description?: string;
  adresse?: string;
  latitude?: number;
  longitude?: number;
  horaires?: string;
  note?: string;
  telephone?: string;
  siteWeb?: string;
  affiche?: boolean;
  ordre?: number;
}

export interface DechetsCitoyenPayload {
  secteurs: { id: string; nom: string; description: string | null }[];
  secteurChoisiId: string | null;
  rappelActif: boolean;
  prochaines: ProchaineCollecte[];
}
export interface DechetsEtatMairie {
  secteursCount: number;
  collectesCount: number;
  actif: boolean;
}

// ── Objets trouvés (GRC, migration 126) ─────────────────────────────────────
export type ObjetStatut = 'a_reclamer' | 'rendu' | 'archive';
export interface ObjetTrouve {
  id: string;
  libelle: string;
  description: string | null;
  lieu: string | null;
  dateTrouve: string;
  aPhoto: boolean;
  photoUrl: string | null;
  statut: ObjetStatut;
  renduA: string | null;
  renduAt: string | null;
}
export interface ObjetsEtatMairie {
  aReclamer: number;
  rendus: number;
  actif: boolean;
}

// ── RDV (GRC, migration 127) ────────────────────────────────────────────────
export type RdvStatut = 'reserve' | 'honore' | 'annule';
export interface RdvMotif {
  id: string;
  nom: string;
  description: string | null;
  lieu: string | null;
  dureeMin: number;
  actif: boolean;
  ordre: number;
}
export interface RdvDisponibilite {
  id: string;
  motifId: string;
  jour: number;
  heureDebut: string;
  heureFin: string;
}
export interface RdvCreneau {
  date: string;
  heureDebut: string;
  jour: number;
}
export interface RdvReservation {
  id: string;
  motifId: string;
  motifNom: string | null;
  lieu: string | null;
  dateRdv: string;
  heureDebut: string;
  dureeMin: number;
  statut: RdvStatut;
  nom: string | null;
  email: string | null;
  telephone: string | null;
  note: string | null;
  /** Item 13.2 — n° de pré-demande ANTS, présent seulement pour un RDV titre d'identité. */
  numeroPredemande: string | null;
  createdAt: string;
}

// --- Points de collecte & déchetterie (migration 136) -----------------------
export type CollecteType =
  | 'verre' | 'textile' | 'piles' | 'vegetaux'
  | 'encombrants' | 'huiles_deee' | 'dechetterie' | 'autre';
export type CollecteDossierStatut = 'nouvelle' | 'en_cours' | 'acceptee' | 'refusee';

export interface CollectePoint {
  id: string;
  nom: string;
  type: CollecteType;
  adresse: string | null;
  latitude: number | null;
  longitude: number | null;
  horaires: string | null;
  dechetsAcceptes: string[];
  note: string | null;
  affiche: boolean;
  ordre: number;
}
export interface CollectePieceAttendue {
  id: string;
  libelle: string;
  obligatoire: boolean;
  ordre: number;
}
export interface CollecteDossierPiece {
  id: string;
  libelle: string | null;
  nomFichier: string | null;
  taille: number | null;
  url: string | null;
}
export interface CollecteDossier {
  id: string;
  userId: string | null;
  demandeurNom: string | null;
  demandeurEmail: string | null;
  demandeurTel: string | null;
  demandeurAdresse: string | null;
  message: string | null;
  statut: CollecteDossierStatut;
  numeroCarte: string | null;
  reponseStaff: string | null;
  modeDelivrance: 'dematerialisee' | 'physique' | 'les_deux' | null;
  carteEmiseLe: string | null;
  attestationDisponible: boolean;
  pieces: CollecteDossierPiece[];
  createdAt: string;
  updatedAt: string;
}
export interface CollecteDechetterie {
  id: string;
  nom: string;
  adresse: string | null;
  latitude: number | null;
  longitude: number | null;
  horaires: string | null;
  dechetsAcceptes: string[];
  inscriptionActive: boolean;
  inscriptionIntro: string | null;
  aFormulaire: boolean;
  formulaireNom: string | null;
  affiche: boolean;
  cartePrefixe: string;
  carteProchainNumero: number;
  carteFormatLargeur: number;
  carteInclureAnnee: boolean;
  carteValiditeMois: number;
  modeDelivranceDefaut: 'dematerialisee' | 'physique' | 'les_deux';
  piecesAttendues: CollectePieceAttendue[];
}
/** Payload citoyen : carte (points + déchetterie injectée) + fiche déchetterie. */
export interface CollecteCitoyenPayload {
  points: CollectePoint[];
  dechetterie: {
    nom: string;
    adresse: string | null;
    latitude: number | null;
    longitude: number | null;
    horaires: string | null;
    dechetsAcceptes: string[];
    inscriptionActive: boolean;
    inscriptionIntro: string | null;
    aFormulaire: boolean;
    piecesAttendues: CollectePieceAttendue[];
    monDossier: CollecteDossier | null;
  } | null;
}

export interface RdvCitoyenPayload {
  motifs: { id: string; nom: string; description: string | null; lieu: string | null; dureeMin: number }[];
  mesRdv: RdvReservation[];
}
export interface RdvEtatMairie {
  motifsActifs: number;
  rdvAVenir: number;
  motifsSansCreneau: string[];
  actif: boolean;
}

// ── Identité mairie (Administration → Mairie, migration 132) ────────────────
/** Fond du tableau de bord (mig 294). */
export interface FondEcran {
  choix: 'aucun' | 'mer' | 'prairie' | 'montagne' | 'perso';
  url: string | null;
  /** Opacité du voile en % (mig 296) — 40 image très présente, 95 presque unie. */
  voile: number;
}

export interface IdentiteMairie {
  nom: string;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  telephone: string | null;
  emailContact: string | null;
  siret: string | null;
  logoUrl: string | null;
  logoMime: string | null;
  enteteUrl: string | null;
  enteteMime: string | null;
  piedTexte: string | null;
  /** Signataire par défaut des actes (mig 258). */
  maireNom: string | null;
  maireTitre: string | null;
  tribunalAdministratif: string | null;
}

// ── Identités prestataires (documents cantine/périscolaire/PE délégués, mig 210) ──

// ── Repas des adultes à la cantine (items 12.5/12.6, mig 250) ──
export type CategorieConvive = 'agent' | 'enseignant' | 'elu' | 'exterieur';
export interface CantineConvive {
  id: string;
  userId: string | null;
  nomComplet: string;
  categorie: CategorieConvive;
  joursSemaine: number[];
  dateDebut: string | null;
  dateFin: string | null;
  actif: boolean;
  estMoi: boolean;
}
export interface CantineRepasAdulte {
  id: string;
  conviveId: string;
  dateService: string;
  statut: 'reserve' | 'present' | 'absent' | 'absent_justifie';
  source: string;
  prixCentimes: number | null;
  modifiable: boolean;
}
// ── Justificatifs d'absence (item 12.3, mig 252) ──
export interface AbsenceJustificatif {
  id: string;
  cibleType: 'cantine' | 'periscolaire' | 'alsh' | 'adulte';
  cibleId: string;
  dateService: string;
  motif: string;
  nomFichier: string;
  statut: 'en_attente' | 'accepte' | 'refuse';
  commentaire: string | null;
  personne: string | null;
  deposeParNom: string | null;
  decideParNom: string | null;
  createdAt: string;
  decideAt: string | null;
}

export interface CantineEtatTransmis {
  id: string;
  periode: string;
  totalRepas: number;
  totalCentimes: number;
  lignes: Array<{ conviveId: string; nomComplet: string; categorie: CategorieConvive; nbRepas: number; montantCentimes: number }>;
  transmisAt: string;
  transmisParNom: string | null;
  commentaire: string | null;
}
export interface CantineEtatAdultes {
  periode: string;
  lignes: Array<{ conviveId: string; nomComplet: string; categorie: CategorieConvive; nbRepas: number; montantCentimes: number }>;
  totalRepas: number;
  totalCentimes: number;
}

export type BriqueDeleguable = 'scolaire_cantine' | 'periscolaire' | 'petite_enfance';
export interface DocumentIdentite {
  id: string;
  nom: string;
  adresse: string | null;
  codePostal: string | null;
  ville: string | null;
  telephone: string | null;
  emailContact: string | null;
  siret: string | null;
  mentionLegale: string | null;
  piedTexte: string | null;
  mentions: Record<string, unknown>;
  logoUrl: string | null;
  enteteUrl: string | null;
  actif: boolean;
  briques: BriqueDeleguable[];
}
export interface DocumentIdentiteInput {
  nom: string;
  adresse?: string | null; codePostal?: string | null; ville?: string | null;
  telephone?: string | null; emailContact?: string | null; siret?: string | null;
  mentionLegale?: string | null; piedTexte?: string | null;
  mentions?: Record<string, unknown> | null;
}
export interface BriqueDeleguableMeta { cle: BriqueDeleguable; libelle: string; }
export type ActeurCategorie = 'producteur' | 'commerce' | 'artisan' | 'service' | 'restauration' | 'sante' | 'profession_liberale' | 'autre';
export type ProduitType = 'panier' | 'produit' | 'abonnement';
export interface ActeurProduit {
  id: string; nom: string; description: string | null; type: ProduitType;
  prixEuros: number | null; unite: string | null; saison: string | null; disponible: boolean;
}
// API Entreprise — données associations certifiées (miroir shared-types).
export interface AssociationVerifiee {
  siren: string | null;
  rna: string | null;
  raisonSociale: string | null;
  formeJuridique: string | null;
  siretSiege: string | null;
  actif: boolean | null;
  source: 'api_entreprise' | 'mock';
  recupereLe: string;
}
export interface SubventionAsso {
  annee: number | null;
  dispositif: string | null;
  sousDispositif: string | null;
  montantDemande: number | null;
  montantAccorde: number | null;
  attribuant: string | null;
  objet: string | null;
}
export interface SubventionsAssoRecuperees {
  subventions: SubventionAsso[];
  source: 'api_entreprise' | 'mock';
  recupereLe: string;
}

export interface ActeurLocal {
  id: string; nom: string; categorie: ActeurCategorie; activite: string | null; description: string | null;
  email: string | null; telephone: string | null; adresse: string | null; codePostal: string | null; ville: string | null;
  siteWeb: string | null; horaires: string | null; latitude: number | null; longitude: number | null;
  gereParUserId: string | null; publie: boolean; actif: boolean;
  statutValidation: 'valide' | 'en_attente' | 'refuse'; motifRefus: string | null;
  produits?: ActeurProduit[];
}
export interface ActeursEtatMairie { total: number; producteurs: number; enAttente: number; actif: boolean; }
export interface ActeursCitoyenPayload { acteurs: ActeurLocal[]; }

// ── Boîte à idées (GRC, migration 130) ──────────────────────────────────────
export type IdeeCategorie = 'cadre_de_vie' | 'voirie' | 'environnement' | 'culture' | 'sport' | 'jeunesse' | 'solidarite' | 'commerce' | 'autre';
export type IdeeStatut = 'nouvelle' | 'a_l_etude' | 'retenue' | 'realisee' | 'refusee';
export interface Idee {
  id: string; titre: string; description: string; categorie: IdeeCategorie;
  auteurNom: string | null; statut: IdeeStatut; reponse: string | null;
  soutiens: number; jeSoutiens: boolean; estMonIdee: boolean; masquee?: boolean; createdAt: string;
}
export interface IdeesEtatMairie { total: number; nouvelles: number; actif: boolean; }

// ── Agenda municipal (GRC, migration 129) ───────────────────────────────────
export type AgendaCategorie = 'conseil' | 'ceremonie' | 'marche' | 'fete' | 'culture' | 'sport' | 'reunion' | 'autre';
export interface AgendaEvenement {
  id: string;
  titre: string;
  description: string | null;
  categorie: AgendaCategorie;
  lieu: string | null;
  dateDebut: string;
  heureDebut: string | null;
  dateFin: string | null;
  heureFin: string | null;
  publie: boolean;
  /** Trace de la dernière diffusion (mig 293). */
  derniereDiffusionAt: string | null;
  derniereDiffusionResume: string | null;
}

// ── Cimetière (GRC, migration 128) ──────────────────────────────────────────
export type TypeDuree = 'temporaire' | 'trentenaire' | 'cinquantenaire' | 'perpetuelle';
export type ConcessionStatut = 'active' | 'echue' | 'delai_reprise' | 'reprise';
export type TypeEmplacement = 'pleine_terre' | 'caveau' | 'columbarium' | 'cavurne' | 'jardin_souvenir';

export interface Cimetiere {
  id: string; nom: string; adresse: string | null; actif: boolean;
}
export interface ConcessionDefunt {
  id: string; nom: string; prenom: string | null;
  dateNaissance: string | null; dateDeces: string | null; dateInhumation: string | null;
  typeInhumation: 'inhumation' | 'depot_urne' | 'dispersion'; numeroActeDeces: string | null; notes: string | null;
}
export interface Concession {
  id: string; cimetiereId: string; cimetiereNom: string | null; numero: string;
  emplacementCarre: string | null; emplacementRang: string | null; emplacementNumero: string | null;
  typeEmplacement: TypeEmplacement;
  concessionnaireNom: string; concessionnaireAdresse: string | null; concessionnaireEmail: string | null; concessionnaireTel: string | null;
  ayantDroitNom: string | null; ayantDroitEmail: string | null; ayantDroitTel: string | null;
  concessionnaireUserId: string | null;
  typeDuree: TypeDuree; dureeAnnees: number | null; dateDebut: string; dateEcheance: string | null; tarifEuros: number | null;
  statut: ConcessionStatut; statutEffectif: ConcessionStatut; notes: string | null;
  defunts?: ConcessionDefunt[];
}
export interface ConcessionDemande {
  id: string; typeDemande: 'nouvelle' | 'renouvellement'; concessionId: string | null;
  concessionNumero: string | null; message: string | null;
  statut: 'nouvelle' | 'en_cours' | 'traitee' | 'refusee'; reponse: string | null;
  demandeurNom: string | null; demandeurEmail: string | null; demandeurTel: string | null; createdAt: string;
}
export interface CimetiereEtatMairie {
  cimetieres: number; concessionsActives: number; aEcheance: number; demandesEnAttente: number; actif: boolean;
}
export interface CimetiereCitoyenPayload {
  cimetieres: Cimetiere[];
  mesDemandes: ConcessionDemande[];
}

export interface PoleInfoPayload {
  services: ServiceHoraire[];
  demarches: Demarche[];
  menusSemaine: PoleInfoMenu[];
  evenements: PoleInfoEvenement[];
  dechets: DechetsCitoyenPayload | null;
  objetsTrouves: ObjetTrouve[] | null;
  rdv: RdvCitoyenPayload | null;
  cimetiere: CimetiereCitoyenPayload | null;
  collecte: CollecteCitoyenPayload | null;
  modules: { cantineActif: boolean; associationsActif: boolean };
}

// ── Référendums (GRC, migration 117) ────────────────────────────────────────
export type ReferendumStatut = 'draft' | 'open' | 'closed';
export type ReferendumMode = 'indicatif' | 'officiel';
export interface ReferendumOption {
  id: string;
  libelle: string;
  ordre: number;
  voix?: number;
}
export interface Referendum {
  id: string;
  question: string;
  description: string | null;
  statut: ReferendumStatut;
  ouvertureAt: string;
  clotureAt: string;
  mode: ReferendumMode;
  resultatsPublies: boolean;
  options: ReferendumOption[];
  totalVotants: number;
  resultatsVisibles: boolean;
  monVoteOptionId?: string | null;
  monVoteModifiable?: boolean;
  fcVerifie?: boolean;
}

// ── Urbanisme (migration 119) ───────────────────────────────────────────────
export type UrbaType = 'CU' | 'DP' | 'PC' | 'PA';
export type UrbaStatut = 'deposee' | 'complete' | 'incomplete' | 'instruction' | 'accordee' | 'refusee' | 'annulee';
export interface UrbaPiece {
  id: string;
  nomFichier: string | null;
  mimeType: string | null;
  tailleOctets: number | null;
}
export interface UrbaDemande {
  id: string;
  numero: string;
  typeDemande: UrbaType;
  cuNature: 'informatif' | 'operationnel' | null;
  pcmi: boolean;
  statut: UrbaStatut;
  parcelle: string | null;
  surfaceM2: number | null;
  adresseTerrain: string | null;
  description: string | null;
  deposantNom: string | null;
  deposantEmail: string | null;
  decisionAt: string | null;
  motifDecision: string | null;
  delaiMois: number | null;
  dateLimite: string | null;
  completAt: string | null;
  joursRestants: number | null;
  piecesDemandees: string | null;
  piecesMessage: string | null;
  arreteGenereAt: string | null;
  signatureStatut: 'non_requise' | 'en_attente' | 'en_cours' | 'signe';
  parcelleSection: string | null;
  parcelleNumero: string | null;
  parcelleGeojson: unknown | null;
  parcelleSurfaceCad: number | null;
  parcelleCentroide: { lat: number; lng: number } | null;
  zonagePlu: string | null;
  cadastreMajAt: string | null;
  actesTransmisAt: string | null;
  createdAt: string;
  updatedAt: string;
  pieces?: UrbaPiece[];
  avis?: UrbaAvis[];
}
export type UrbaAvisSens = 'favorable' | 'defavorable' | 'favorable_reserves' | 'en_attente';
export interface UrbaAvis {
  id: string;
  service: string;
  sens: UrbaAvisSens;
  commentaire: string | null;
  saisiAt: string | null;
  reponseAt: string | null;
}
export interface UrbaDocument {
  id: string;
  titre: string;
  typeDoc: string;
  typeLibre: string | null;
  nomFichier: string | null;
  mimeType: string | null;
  tailleOctets: number | null;
  lienGpu: string | null;
  urlExterne: string | null;
  cerfaNumero: string | null;
  aFichier: boolean;
  affiche: boolean;
  ordre: number;
}

export interface ComptaPoste {
  id: string;
  libelle: string;
  actif: boolean;
  ordre: number;
}
export interface ComptaVentilationLigne {
  posteId: string;
  posteLibelle: string;
  montant: number;
}
export interface ComptaEncaissement {
  id: string;
  objet: string;
  montant: number;
  moyen: 'cb_payfip' | 'sepa' | 'cheque' | 'espece' | 'virement';
  reference: string | null;
  source: 'cantine' | 'petite_enfance' | 'salle' | 'asso' | 'grc' | 'rh' | 'tech' | 'urba' | 'divers';
  commentaire: string | null;
  pieceJointeUrl: string | null;
  depose: boolean;
  rapproche: boolean;
  ventile: boolean;
  ventilation: ComptaVentilationLigne[];
  dateEncaissement: string;
  encaisseParNom: string | null;
  statut: 'provisoire' | 'definitif';
}
export interface ComptaDepot {
  id: string;
  numeroBordereau: string;
  montantTotal: number;
  nbCheques: number;
  nbEspeces: number;
  dateDepot: string;
}
export interface ComptaJournalLigne {
  id: string;
  date: string;
  objet: string;
  montant: number;
  moyen: ComptaEncaissement['moyen'];
  source: ComptaEncaissement['source'];
  reference: string | null;
  depose: boolean;
  rapproche: boolean;
  ventilation: ComptaVentilationLigne[];
}
export interface ComptaJournalResult {
  lignes: ComptaJournalLigne[];
  totalEncaisse: number;
  totalRapproche: number;
  nbOperations: number;
}
export type PeStructureType =
  | 'multi_accueil' | 'micro_creche' | 'halte_garderie' | 'creche_collective' | 'creche_familiale';
export interface PeStructure {
  id: string;
  nom: string;
  type: PeStructureType;
  capacite: number;
  amplitudeDebut: string | null;
  amplitudeFin: string | null;
  actif: boolean;
  /** Lieu du référentiel rattaché (S4-b). */
  lieuId?: string | null;
}
export interface PeDossier {
  id: string;
  enfantId: string;
  enfantPrenom: string;
  enfantNom: string;
  enfantDateNaissance: string | null;
  familleId: string;
  medecinNom: string | null;
  medecinTel: string | null;
  vaccinationsAJour: boolean;
  paiDetail: string | null;
  marche: boolean;
  autorisePhoto: boolean;
  autoriseSortie: boolean;
  personnesAutorisees: string | null;
  notes: string | null;
}
export interface PeDashboardData {
  nbStructures: number;
  capaciteTotale: number;
  nbEnfantsInscrits: number;
  nbDossiers: number;
}
export interface PeEnfantDisponible {
  enfantId: string;
  prenom: string;
  nom: string;
  dateNaissance: string | null;
  age: number | null;
  familleId: string;
  familleNom: string;
  tranche: 'moins_4' | '4_6' | 'inconnu';
  servicesActuels: string[];
}
export interface PeImportLigne { ligne: number; statut: 'cree' | 'ignore' | 'erreur'; message: string }
export interface PeImportResult {
  enfantsCrees: number; famillesCreees: number; ignores: number; erreurs: number;
  details: PeImportLigne[];
}
export interface PieceType {
  id: string;
  code: string;
  libelle: string;
  portee: 'commun' | 'specifique';
  module: string | null;
  actif: boolean;
  ordre: number;
}
export interface FamillePiece {
  id: string;
  pieceTypeId: string;
  pieceTypeCode: string;
  pieceTypeLibelle: string;
  version: number;
  nomFichier: string;
  mimeType: string;
  tailleBytes: number;
  conforme: boolean | null;
  dateExpiration: string | null;
  createdAt: string;
}
/** Pièce de la liste UNIFIÉE administré : demandée (donc obligatoire) + état fourni. */
export interface PieceRequise {
  pieceTypeId: string;
  code: string;
  libelle: string;
  portee: 'commun' | 'specifique';
  module: string | null;
  dejaFournie: boolean;
  conforme: boolean | null;
  obligatoire: true;
}
export type PeContratType = 'regulier' | 'occasionnel' | 'urgence';
export type PeContratStatut = 'actif' | 'suspendu' | 'clos';
export interface PeContrat {
  id: string;
  enfantId: string;
  enfantPrenom: string;
  enfantNom: string;
  structureId: string;
  structureNom: string;
  type: PeContratType;
  dateDebut: string;
  dateFin: string | null;
  joursSemaine: number[];
  heureDebut: string | null;
  heureFin: string | null;
  heuresMensualisees: number;
  tarifHoraireEuros: number;
  statut: PeContratStatut;
  notes: string | null;
}
export interface PePresence {
  id: string;
  enfantId: string;
  enfantPrenom: string;
  enfantNom: string;
  structureId: string;
  contratId: string | null;
  dateJour: string;
  arrivee: string | null;
  depart: string | null;
  minutesRealisees: number;
  absent: boolean;
  marche: boolean;
}
export interface PeEncadrementJour {
  structureId: string;
  structureNom: string;
  dateJour: string;
  nbEncadrants: number;
  nbEnfantsPresents: number;
  nbMarchent: number;
  nbNeMarchentPas: number;
  placesAgreees: number;
  capaciteReglementaire: number;
  conforme: boolean;
  tauxOccupation: number;
  tauxOccupationPmi: number;
}
export type PePlanningStatut = 'planifie' | 'en_cours' | 'termine' | 'absent';
export interface PePlanningCreneau {
  id: string;
  agentUserId: string;
  agentNom: string;
  structureId: string;
  structureNom: string;
  dateDebut: string;
  dateFin: string;
  titre: string;
  type: string;
  statut: PePlanningStatut;
  couleurMarker: string | null;
}
export interface PeParametres {
  psuEnabled: boolean;
  tarifHoraireDefautEuros: number;
  facturationUniteMinutes: number;
  facturationArrondi: 'sup' | 'proche';
  amplitudeDebut: string | null;
  amplitudeFin: string | null;
  absenceDelaiPrevenanceJours: number;
  absenceTraitement: 'facture' | 'non_facture' | 'non_facture_si_justificatif';
  absenceDelaiJustificatifJours: number;
  autoFacturationActive: boolean;
  autoFacturationJour: number;
  autoFacturationHeure: number;
  autoFacturationPeriode: 'mois_ecoule' | 'mois_en_cours';
  autoFacturationEnvoiEmail: boolean;
  autoFacturationLastRun: string | null;
}
export interface PeJourOuverture {
  id: string;
  structureId: string;
  date: string;
  ouvert: boolean;
  motif: string | null;
}
export interface PeBaremePsuLigne {
  id: string;
  annee: number;
  nbEnfants: number;
  tauxEffortPct: number;
  plancherEuros: number;
  plafondEuros: number;
}
export interface PeFactureApercuLigne {
  enfantId: string;
  enfantPrenom: string;
  enfantNom: string;
  familleId: string;
  contratId: string;
  type: PeContratType;
  mode: 'mensualise' | 'realise' | 'psu';
  heures: number;
  tarifHoraireEuros: number;
  montantEuros: number;
  detail: string;
}
export interface PeFactureApercu {
  periodeDebut: string;
  periodeFin: string;
  mode: string;
  lignes: PeFactureApercuLigne[];
  totalEuros: number;
}
export interface PeFacture {
  id: string;
  numero: string;
  familleId: string;
  periodeDebut: string;
  periodeFin: string;
  montantEuros: number;
  statut: string;
  dateEmission: string;
}
export interface PeAssmat {
  id: string;
  prenom: string;
  nom: string;
  telephone: string | null;
  email: string | null;
  commune: string | null;
  adresse: string | null;
  placesAgreees: number;
  placesDispo: number;
  ageMinMois: number | null;
  ageMaxMois: number | null;
  visibleAnnuaire: boolean;
  actif: boolean;
  notes: string | null;
}
export interface PeAtelier {
  id: string;
  titre: string;
  description: string | null;
  dateAtelier: string;
  heureDebut: string | null;
  heureFin: string | null;
  lieu: string | null;
  places: number;
  statut: 'ouvert' | 'complet' | 'annule' | 'termine';
  nbInscrits: number;
}
export interface PeAtelierInscription {
  id: string;
  assmatId: string | null;
  assmatNom: string | null;
  enfantNom: string | null;
  nbEnfants: number;
}
export type PePreinscStatut = 'deposee' | 'en_attente' | 'proposee' | 'acceptee' | 'refusee' | 'annulee';
export interface PePreinscription {
  id: string;
  familleId: string | null;
  enfantPrenom: string | null;
  enfantNom: string | null;
  dateNaissance: string | null;
  ne: boolean;
  parentNom: string | null;
  parentTel: string | null;
  parentEmail: string | null;
  structureId: string | null;
  structureNom: string | null;
  dateAccueilSouhaitee: string | null;
  typeSouhaite: PeContratType;
  joursSouhaites: number[];
  communeResidence: boolean;
  fratrieAccueillie: boolean;
  parentActif: boolean;
  quotientFamilial: number | null;
  statut: PePreinscStatut;
  dateDepot: string;
  commentaire: string | null;
  contratId: string | null;
  score: number;
  heuresSouhaitees: number;
  tarifHoraireSouhaiteEuros: number;
  motifAttribution: string | null;
  destinataire: string | null;
}
export interface PeEspaceParent {
  enfants: {
    enfantId: string; prenom: string; nom: string;
    structureNom: string | null; typeContrat: PeContratType | null; statutContrat: string | null;
  }[];
  factures: PeFacture[];
  preinscriptions: { id: string; enfantPrenom: string | null; enfantNom: string | null; statut: PePreinscStatut; dateDepot: string; motifAttribution: string | null }[];
  annuaire: { id: string; prenom: string; nom: string; commune: string | null; telephone: string | null; placesDispo: number }[];
  structures: { id: string; nom: string; type: PeStructureType; amplitudeDebut: string | null; amplitudeFin: string | null }[];
  familleId: string | null;
}
export interface ComptaDashboardData {
  encaisseMois: number;
  aDeposer: number;
  cautionsDetenues: number;
  enAttente: number;
  tresorerie: { especes: number; cheques: number; deposeMois: number };
  parSource: { source: ComptaEncaissement['source']; montant: number }[];
  parPoste: { posteId: string; libelle: string; montant: number }[];
  aValiderNb: number;
  aValiderMontant: number;
}

export interface FactureManuelleLigne { libelle: string; quantite: number; prixUnitaire: number; montant: number; }
export interface FactureRecue {
  id: string; chorusIdFacture: number; numeroFacture: string | null;
  fournisseurNom: string | null; fournisseurSiret: string | null;
  dateDepot: string | null; dateEcheance: string | null;
  montantHt: number; montantTtc: number; devise: string;
  statutChorus: string | null; statutTraitement: string; motifRejet: string | null;
}
export interface FactureManuelle {
  id: string; numero: string; objet: string | null; origineCode: string | null;
  clientNom: string | null; clientAdresse: string | null; tiersId: string | null;
  clientEmail?: string | null; clientSiret?: string | null; clientCodePostal?: string | null; clientVille?: string | null;
  dateEmission: string; dateEcheance: string | null; montantTtc: number; statut: string;
  typeDestinataire?: 'particulier' | 'entreprise' | 'entreprise_sans_chorus' | 'public';
  numeroEngagement?: string | null; codeService?: string | null;
  lignes: FactureManuelleLigne[];
}
export interface FactureTemplate {
  id: string; libelle: string; origineCode: string; objetDefaut: string | null;
  lignesDefaut: { libelle: string; quantite: number; prixUnitaire: number }[]; actif: boolean; ordre: number;
}
export interface FactureAEncaisser { id: string; numero: string; clientNom: string | null; objet: string | null; service: string; montantTtc: number; }
export interface CreerFactureManuelleBody {
  origineCode: string; objet?: string;
  tiersId?: string | null; responsableUserId?: string | null;
  clientNom?: string | null; clientAdresse?: string | null;
  clientEmail?: string | null; clientSiret?: string | null;
  clientCodePostal?: string | null; clientVille?: string | null; clientTelephone?: string | null;
  clientType?: 'prestataire' | 'intervenant' | 'beneficiaire' | 'autre';
  clientCategorie?: 'client' | 'fournisseur' | 'les_deux';
  creerTiers?: boolean;
  dateEmission?: string; dateEcheance?: string | null;
  typeDestinataire?: 'particulier' | 'entreprise' | 'entreprise_sans_chorus' | 'public';
  numeroEngagement?: string | null; codeService?: string | null;
  lignes: { libelle: string; quantite: number; prixUnitaire: number }[];
}
export interface TiersRecherche {
  id: string; nom: string; type: string; categorie: string;
  email: string | null; siret: string | null; adresse: string | null;
  codePostal: string | null; ville: string | null; telephone: string | null;
}
export interface EffectifLigne {
  nom: string; prenom: string; detail: string | null; statut: string; pointe: boolean;
}
export interface EffectifSection {
  cle: 'cantine' | 'periscolaire_matin' | 'periscolaire_soir' | 'alsh' | 'petite_enfance';
  libelle: string; attendus: number; pointes: number; presents: number; lignes: EffectifLigne[];
}
export interface EffectifsJourResult {
  dateService: string; sections: EffectifSection[]; totalAttendus: number; totalPresents: number;
}
export interface TemplateFactureBody {
  libelle: string; origineCode: string; objetDefaut?: string | null;
  lignesDefaut: { libelle: string; quantite: number; prixUnitaire: number }[];
  ordre?: number; actif?: boolean;
}
export type DecaissementMoyen = 'virement' | 'cheque' | 'prelevement' | 'cb' | 'espece';
export interface Decaissement {
  id: string; objet: string; montant: number; moyen: DecaissementMoyen;
  reference: string | null; tiersId: string | null; fournisseurNom: string | null;
  posteId: string | null; posteLibelle: string | null;
  pieceJointeUrl: string | null; commentaire: string | null;
  dateDecaissement: string; creeParNom: string | null;
}
export interface DecaissementBody {
  objet: string; montant: number; moyen: DecaissementMoyen;
  reference?: string | null; tiersId?: string | null; fournisseurNom?: string | null;
  posteId?: string | null; pieceJointeUrl?: string | null; commentaire?: string | null;
  dateDecaissement?: string | null;
  factureId?: string | null; // facture (sens=depense) réglée par ce décaissement
}
export interface LivreLigne {
  id: string; date: string; sens: 'entree' | 'sortie'; objet: string;
  montant: number; moyen: string; reference: string | null; tiers: string | null;
}
export interface LivreResult {
  lignes: LivreLigne[]; totalEntrees: number; totalSorties: number; solde: number;
  nbOperations: number; periode: { du: string | null; au: string | null };
}
export interface UpdatePayfipConfigInputShared {
  payfipActif?: boolean; payfipNumcli?: string | null;
  payfipSecret?: string | null; modeEfacture?: 'manuel' | 'helios' | 'chorus_direct';
  chorusStructureId?: string | null; chorusTechLogin?: string | null;
  chorusTechPassword?: string | null;
  heliosCodeCollectivite?: string | null; heliosDepotUrl?: string | null; heliosIdPoste?: string | null;
  fcClientId?: string | null; fcClientSecret?: string | null;
  apiParticulierToken?: string | null; apiEntrepriseToken?: string | null;
}
export interface EfactureResultat {
  efacture?: boolean; canal: 'email' | 'helios' | 'chorus_direct'; statut: string;
  identifiant: string | null; mock: boolean; message: string;
}

/**
 * Prépare le multipart d'une habilitation (item 5.3).
 *
 * Le document scanné accompagne les champs — d'où FormData plutôt que JSON.
 * ⚠️ En multipart, tout est chaîne : `rappelOffsets` part en « 90,30 » et le
 * serveur le re-parse (cf. `parseHabilitationBody` dans rh.routes.ts).
 */
function habilitationFormData(data: Record<string, unknown>, fichier?: File): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(data)) {
    if (v === undefined || v === null || v === '') continue;
    fd.append(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  if (fichier) fd.append('piece', fichier);
  return fd;
}

/** Créneau de la grille unique pour les briques simples (urbanisme, comptabilité). */
export interface SimpleGrilleCreneau {
  id: string;
  agentUserId: string;
  agentNom?: string;
  dateDebut: string;
  dateFin: string;
  titre: string;
  description: string | null;
  type: string;
  couleurMarker: string | null;
  statut: 'planifie' | 'absent';
  transmisPaie: boolean;
  profilId: string | null;
}
export interface SimpleCreneauInput {
  agentUserId: string;
  dateDebut: string;
  dateFin: string;
  titre: string;
  description?: string;
  type?: string;
  couleurMarker?: string | null;
}
export interface SimplePoste {
  id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null;
}

/** Corps d'écriture d'un lien utile (mig 253). */
export interface LienUtilePayload {
  categorieId?: string | null;
  libelle: string;
  url: string;
  description?: string | null;
  ordre?: number;
  actif?: boolean;
}

/** Corps d'écriture d'un contact d'urgence PCS (mig 253). */
export interface ContactUrgencePayload {
  libelle: string;
  organisme?: string | null;
  telephone: string;
  telephoneSecondaire?: string | null;
  rolePcs?: string | null;
  note?: string | null;
  ordre?: number;
  actif?: boolean;
}

/** Chantier 7, étape 3 (mig 321) — carnet du local. */
export interface PointCarnet { id: string; libelle: string; etat: 'ok' | 'a_surveiller' | 'degrade'; note?: string | null;
  coutEstime?: number | null; piece?: string | null; ficheId?: string | null; repareLe?: string | null }
export interface CarnetDTO {
  edl: { id: string; numero: string } | null;
  points: Array<PointCarnet & { fiche: { id: string; numero: string; statut: string; etapeCircuit: string | null } | null }>;
  passages: Array<{ id: string; sens: 'entree' | 'sortie'; occupant: string; datePassage: string; constats: string | null; degrades: number; aSurveiller: number }>;
}

/** Chantier 7 (mig 319) — documents d'un bâtiment. */
export interface DocumentLieuDTO { id: string; type: 'photo' | 'plan_evacuation'; libelle: string | null; mimeType: string; actif: boolean; createdAt: string; url: string | null }
export interface DocumentsLieuDTO { photos: DocumentLieuDTO[]; planEvacuation: DocumentLieuDTO | null; plansArchives: DocumentLieuDTO[] }

/** M23 + M24 — l'état d'un bâtiment, en une réponse. */
export interface EtatDesLocauxDTO {
  lieu: { id: string; nom: string; planUrl: string | null };
  portes: Array<{ id: string; libelle: string; emplacement: string | null; planX: number | null; planY: number | null; nbCles: number; nbSorties: number }>;
  clesSorties: Array<{ pretId: string; porteId: string; porte: string; exemplaire: number | null; numeroGrave: string | null; emprunteur: string; remiseLe: string | null; retourPrevuLe: string | null }>;
  interventions: Array<{ id: string; numero: string; sujet: string; urgence: string; statut: string; x: number | null; y: number | null }>;
  etatsDesLieux: Array<{ id: string; numero: string; sujet: string; statut: string; createdAt: string }>;
}

/** M3 — élimination réglementaire des archives. */
export interface UniteEliminableDTO {
  id: string; cote: string | null; intitule: string; categorie: string | null;
  anneeDebut: number | null; anneeFin: number | null; metrage: number | null;
  duaAnnees: number | null; sortFinal: string | null; eliminableDes: string | null;
  contenant: string | null; surBordereau: boolean;
}
export interface BordereauDTO {
  id: string; numero: string; statut: 'brouillon' | 'envoye' | 'vise' | 'elimine' | 'annule';
  serviceVersant: string | null; dateEnvoi: string | null; visaDate: string | null; visaReference: string | null;
  dateDestruction: string | null; modeDestruction: string | null; nbUnites: number; metrage: number;
}

/** Refonte UX, chantier 5 — automatismes (règles réelles). */
export interface AutomatismesDTO {
  fleches: Array<{ definitionId: string; circuit: string; actif: boolean; transitionId: string; libelle: string; de: string; vers: string;
    roleMinimum: string; serviceRequis: string | null; recrediteBudget: boolean; envoiConfig: EnvoiConfig | null;
    notification: { code: string; libelle: string | null; cible: string | null; email: boolean; cloche: boolean } | null }>;
  delais: Array<{ definitionId: string; circuit: string; actif: boolean; etape: string; jours: number }>;
  echeances: Array<{ code: string; libelle: string; cible: string | null; rappels: unknown; email: boolean; groupe: string | null }>;
}

/** Refonte UX, chantier 4 — gouvernance. */
export type ProfilGouv = 'administrateur' | 'maire' | 'adjoint_delegue';
export type BriqueGouv = 'grc' | 'service_technique' | 'scolaire_cantine' | 'associations' | 'urbanisme' | 'comptable' | 'petite_enfance' | 'rh' | 'agents';
export interface ProfilGouvDTO {
  id: string; nom: string; role: string; service: string | null; profil: ProfilGouv | null;
  delegationArreteRef: string | null; lignes: Array<{ brique: BriqueGouv; mode: 'lecture' | 'gestion' }>; adminComplet: boolean;
}
export interface DeportDTO {
  id: string; eluUserId: string; eluNom: string; voie: 'arrete' | 'deliberation'; objet: string;
  suppleantUserId: string; suppleantNom: string; arreteReference: string | null; deliberationId: string | null;
  entites: Array<{ type: string; id: string; libelle?: string }>; actif: boolean; debutLe: string;
}

/** M22 — suivi sur le dossier d'un agent. */
export type TypeSuivi = 'suivi_medical' | 'suivi_dossier' | 'entretien' | 'accompagnement' | 'disciplinaire' | 'autre';
export interface SuiviAgentDTO {
  id: string; agentId: string; type: TypeSuivi; raison: string; objectif: string | null;
  statut: 'en_cours' | 'clos'; conclusion: string | null; creeParNom: string | null;
  createdAt: string; closAt: string | null;
  circuit: { instanceId: string; libelle: string; etat: string } | null;
  echeance: { id: string; date: string; statut: string } | null;
}

/** M28 — déplacement / ordre de mission. */
export type TransportDeplacement = 'vehicule_service' | 'vehicule_personnel' | 'train' | 'covoiturage' | 'autre';
export interface DeplacementInput {
  objet: string; lieu: string; dateDepart: string; dateRetour: string;
  transport?: TransportDeplacement; kmEstimes?: number | null; commentaire?: string | null;
  demandeFormationId?: string | null; formationId?: string | null;
}
export interface DeplacementDTO extends Required<Pick<DeplacementInput, 'objet' | 'lieu' | 'dateDepart' | 'dateRetour'>> {
  id: string; agentUserId: string; agentNom: string | null;
  transport: TransportDeplacement; kmEstimes: number | null; commentaire: string | null;
  statut: 'a_valider' | 'valide' | 'refuse' | 'annule'; motifRefus: string | null; valideParNom: string | null;
  demandeFormationId: string | null; formationId: string | null;
  noteFraisId: string | null; noteFraisStatut: string | null;
}

/** M6 — échéance personnalisée. */
export interface EcheancePersoInput {
  titre: string; description?: string | null; dateEcheance: string;
  rappels: Array<{ quantite: number; unite: 'jours' | 'mois' | 'annees' }>;
  destinataireIds: string[];
}
export interface EcheancePersoDTO extends Omit<EcheancePersoInput, 'destinataireIds'> {
  id: string; destinataires: Array<{ id: string; nom: string }>;
  statut: 'active' | 'faite' | 'annulee'; creeParId: string; creeParNom: string | null;
}

/** M27 — demande de formation d'un agent (souhait instruit par la RH). */
export interface DemandeFormationDTO {
  id: string; agentUserId: string; agentNom: string | null;
  catalogueId: string | null; intitule: string; organisme: string | null;
  periodeDu: string | null; periodeAu: string | null; commentaire: string | null;
  statut: 'en_attente' | 'acceptee' | 'refusee' | 'annulee';
  reponse: string | null; traiteParNom: string | null; traiteAt: string | null;
  createdAt: string;
}

export interface ActeIntervenantVue {
  id: string; role: 'relecteur' | 'valideur'; nom: string; email: string | null; ordre: number;
  solliciteAt: string | null; responduAt: string | null; avis: string | null; commentaire: string | null;
  userId: string | null; avisOrigine: 'vpc' | 'lien' | 'email' | null; avisSigne: boolean; empreinte: string | null;
  surVersionAnterieure: boolean;
}

export const api = {
  auth: {
    register: (body: RegisterRequest) =>
      apiFetch<AuthResponse>('/api/auth/register', { method: 'POST', body: JSON.stringify(body) }),
    login: (body: LoginRequest) =>
      apiFetch<AuthResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify(body) }),
    logout: () => apiFetch<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
    me: () => apiFetch<MeResponse>('/api/auth/me'),
    refresh: () => apiFetch<AuthResponse>('/api/auth/refresh', { method: 'POST' }, false),
    // ---------- Passe 2A.1 ----------
    registerAdministre: (body: RegisterAdministreRequest) =>
      apiFetch<RegisterAdministreResponse>('/api/auth/register-administre', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    forgotPassword: (body: ForgotPasswordRequest) =>
      apiFetch<ForgotPasswordResponse>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    resetPassword: (body: ResetPasswordRequest) =>
      apiFetch<ResetPasswordResponse>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    verifyEmail: (token: string) =>
      apiFetch<VerifyEmailResponse>(`/api/auth/verify-email?token=${encodeURIComponent(token)}`),
    resendVerification: (body: ResendVerificationRequest) =>
      apiFetch<ResendVerificationResponse>('/api/auth/resend-verification', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    // ---------- Livraison 5 — Bloc B (compte utilisateur self-service) ----------
    updateMyProfile: (body: UpdateMyProfileRequest) =>
      apiFetch<MeResponse>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    changeMyPassword: (body: ChangeMyPasswordRequest) =>
      apiFetch<{ ok: true }>('/api/auth/me/change-password', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    requestEmailChange: (body: { newEmail: string; currentPassword: string }) =>
      apiFetch<{ ok: true; message: string }>('/api/auth/me/email-change', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    confirmEmailChange: (token: string) =>
      apiFetch<{ ok: true; email: string }>('/api/auth/confirm-email-change', {
        method: 'POST',
        body: JSON.stringify({ token }),
      }),
    deleteMyAccount: () =>
      apiFetch<{ ok: true }>('/api/auth/me', { method: 'DELETE' }),
    logoutAll: () =>
      apiFetch<{ ok: true }>('/api/auth/me/logout-all', { method: 'POST' }),
    /** Déclenche le téléchargement direct du JSON RGPD. */
    exportMyData: async (): Promise<void> => {
      const token = getAccessToken();
      const res = await fetch('/api/auth/me/export', {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Export failed' })));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') ?? '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? `vpc-export-rgpd-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
  tenants: {
    /** Liste des tenants publics (filtrable par code postal) — pas d'auth requise. */
    public: (codePostal?: string) =>
      apiFetch<TenantPublicListResponse>(
        `/api/tenants/public${codePostal ? `?codePostal=${encodeURIComponent(codePostal)}` : ''}`
      ),
  },
  legal: {
    /** Versions courantes des documents légaux. */
    versions: () => apiFetch<LegalVersionsResponse>('/api/legal/versions'),
  },
  twoFactor: {
    setup: () => apiFetch<TwoFactorSetupResponse>('/api/2fa/setup', { method: 'POST' }),
    verify: (code: string) =>
      apiFetch<{ enabled: true }>('/api/2fa/verify', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    disable: () => apiFetch<{ enabled: false }>('/api/2fa/disable', { method: 'POST' }),
  },
  /** Refonte UX, chantier 4 (mig 315) — profils, reprise, déports. Routeur /api/admin/gouvernance. */
  gouvernance: {
    profils: () => apiFetch<{ profils: ProfilGouvDTO[] }>('/api/admin/gouvernance/profils'),
    appliquerProfil: (userId: string, body: { profil: ProfilGouv; briques?: Array<{ brique: BriqueGouv; mode: 'lecture' | 'gestion' }>; delegationArreteRef?: string | null }) =>
      apiFetch<{ ok: true }>(`/api/admin/gouvernance/profils/${userId}`, { method: 'PUT', body: JSON.stringify(body) }),
    reprise: (motif: string) => apiFetch<{ ok: true; prevenus: number }>('/api/admin/gouvernance/reprise', { method: 'POST', body: JSON.stringify({ motif }) }),
    retourProfilMaire: () => apiFetch<{ ok: true }>('/api/admin/gouvernance/retour-profil-maire', { method: 'POST', body: '{}' }),
    deports: () => apiFetch<{ deports: DeportDTO[] }>('/api/admin/gouvernance/deports'),
    creerDeport: (body: {
      eluUserId: string; voie: 'arrete' | 'deliberation'; objet: string; suppleantUserId: string;
      arreteReference?: string | null; deliberationId?: string | null; entites: Array<{ type: string; id: string; libelle?: string }>;
    }) => apiFetch<{ id: string }>('/api/admin/gouvernance/deports', { method: 'POST', body: JSON.stringify(body) }),
    cloreDeport: (id: string) => apiFetch<void>(`/api/admin/gouvernance/deports/${id}/clore`, { method: 'POST', body: '{}' }),
  },
  // ---------- Livraison 2 — Backoffice admin (Bloc A) ----------
  /** Délibérations — D0 : les référentiels (mig 267). */
  deliberations: {
    referentiels: (tous = false) =>
      apiFetch<{
        matieres: Array<{ code: string; libelle: string; ordre: number }>;
        sujets: Array<{
          id: string; code: string | null; libelle: string;
          matiereCode: string | null; matiereLibelle: string | null;
          ordre: number; actif: boolean;
        }>;
        conseillers: Array<{
          id: string; userId: string | null; nom: string; prenom: string;
          qualite: 'maire' | 'adjoint' | 'conseiller';
          rang: number | null; dateDebut: string | null; dateFin: string | null;
          actif: boolean;
        }>;
      }>(`/api/deliberations/referentiels${tous ? '?tous=true' : ''}`),
    // ── D1 : séances et registre (mig 268) ──
    seances: () =>
      apiFetch<{ seances: Array<{
        id: string; dateSeance: string; heure: string | null;
        lieuId: string | null; lieuNom: string | null; typeSeance: string;
        presents: number | null; excuses: number | null; pouvoirs: number | null;
        statut: 'prevue' | 'tenue' | 'annulee'; note: string | null;
        nbDeliberations: number;
      }> }>('/api/deliberations/seances'),
    creerSeance: (body: {
      dateSeance: string; heure?: string | null; lieuId?: string | null;
      typeSeance?: 'ordinaire' | 'extraordinaire'; note?: string | null;
    }) => apiFetch<unknown>('/api/deliberations/seances',
      { method: 'POST', body: JSON.stringify(body) }),
    majSeance: (id: string, body: Record<string, unknown>) =>
      apiFetch<unknown>(`/api/deliberations/seances/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) }),
    registre: (filtres: { seanceId?: string; sujetId?: string; q?: string; annee?: number } = {}) => {
      const p = new URLSearchParams();
      if (filtres.seanceId) p.set('seanceId', filtres.seanceId);
      if (filtres.sujetId) p.set('sujetId', filtres.sujetId);
      if (filtres.q) p.set('q', filtres.q);
      if (filtres.annee) p.set('annee', String(filtres.annee));
      const qs = p.toString();
      return apiFetch<{ deliberations: Array<{
        // ⚠️ `numero`, `annee`, `rang` et `seanceId` sont NULLABLES depuis la
        // mig 272 : un projet non programmé n'a pas encore de numéro. Ce type
        // était resté non-nullable, le typecheck du CI l'a attrapé (29/07).
        id: string; numero: string | null; annee: number | null; rang: number | null;
        seanceId: string | null; dateSeance: string | null; objet: string;
        rapporteurNom: string | null; matiereCode: string | null;
        matiereLibelle: string | null;
        resultat: string;
        pour: number | null; contre: number | null; abstentions: number | null;
        // Les vraies dates du parcours (mig 273).
        redactionDebutLe: string;
        redactionTermineeLe: string | null;
        inscriteOdjLe: string | null;
        sujets: Array<{ id: string; libelle: string }>;
      }> }>(`/api/deliberations/registre${qs ? `?${qs}` : ''}`);
    },
    creerDeliberation: (body: {
      /** Facultative (mig 272) : sans séance, c'est un PROJET, sans numéro. */
      seanceId?: string | null; objet: string; rapporteurId?: string | null;
      matiereCode?: string | null; texte?: string | null; sujetIds?: string[];
    }) => apiFetch<{ id: string; numero: string | null }>('/api/deliberations/registre',
      { method: 'POST', body: JSON.stringify(body) }),
    /**
     * Programme un projet à une séance — c'est CE geste qui attribue le numéro
     * au registre. 409 `ANNEE_REGISTRE_DIFFERENTE` si on tente de reporter une
     * délibération déjà numérotée vers une séance d'une autre année.
     */
    programmerDeliberation: (id: string, seanceId: string) =>
      apiFetch<{ id: string; numero: string | null }>(
        `/api/deliberations/registre/${id}/seance`,
        { method: 'PUT', body: JSON.stringify({ seanceId }) }),
    majDeliberation: (id: string, body: Record<string, unknown>) =>
      apiFetch<unknown>(`/api/deliberations/registre/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) }),
    // ── D5 : ce qui découle d'une délibération ──
    suites: (deliberationId: string) =>
      apiFetch<{
        disponible: boolean; motif: string | null;
        matiereCode: string | null; matiereLibelle: string | null;
        suites: Array<{ modeleId: string; libelle: string; brique: string | null; lien: string }>;
      }>(`/api/deliberations/registre/${deliberationId}/suites`),

    // ── D2-02 : le référentiel des pièces attendues ──
    piecesAttendues: () =>
      apiFetch<{ attendues: Array<{
        id: string; code: string; libelle: string;
        obligatoire: boolean; ordre: number; actif: boolean;
      }> }>('/api/deliberations/pieces-attendues'),
    creerPieceAttendue: (body: { libelle: string; ordre?: number }) =>
      apiFetch<unknown>('/api/deliberations/pieces-attendues',
        { method: 'POST', body: JSON.stringify(body) }),
    majPieceAttendue: (id: string, body: Record<string, unknown>) =>
      apiFetch<unknown>(`/api/deliberations/pieces-attendues/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) }),

    // ── D3 : le parcours ──
    parcours: (deliberationId: string) =>
      apiFetch<{
        parcours: {
          id: string;
          definitionLibelle: string;
          etat: { id: string; code: string; libelle: string; couleur: string | null };
          /** `autorisee` = l'appelant a le rôle et le service exigés. */
          transitionsDisponibles: Array<{
            id: string; libelle: string; roleMinimum: string;
            pieceRequise: boolean; autorisee: boolean;
          }>;
        } | null;
        calendrier: Array<{
          code: string; libelle: string; ordre: number;
          dateAttendue: string | null; depassee: boolean;
        }>;
      }>(`/api/deliberations/registre/${deliberationId}/parcours`),
    ouvrirParcours: (deliberationId: string) =>
      apiFetch<unknown>(`/api/deliberations/registre/${deliberationId}/parcours`,
        { method: 'POST' }),
    avancerParcours: async (deliberationId: string, transitionId: string, piece?: File) => {
      const fd = new FormData();
      fd.append('transitionId', transitionId);
      if (piece) fd.append('piece', piece);
      return apiFetchRaw<unknown>(
        `/api/deliberations/registre/${deliberationId}/parcours/avancer`,
        { method: 'POST', body: fd });
    },

    // ── D2 : les pièces (mig 269) ──
    pieces: (deliberationId: string) =>
      apiFetch<{ pieces: Array<{
        attendueId: string | null; code: string | null; libelle: string;
        obligatoire: boolean;
        /** D2-03 — exigée à partir de l'étape atteinte par le parcours. */
        exigee: boolean;
        deposees: Array<{
          id: string; nomFichier: string; mimeType: string;
          tailleOctets: number; createdAt: string;
        }>;
      }> }>(`/api/deliberations/registre/${deliberationId}/pieces`),
    deposerPiece: async (deliberationId: string, fichier: File, attendueId?: string | null) => {
      const fd = new FormData();
      fd.append('fichier', fichier);
      if (attendueId) fd.append('attendueId', attendueId);
      // apiFetchRaw, PAS apiFetch : ce dernier force `application/json` et le
      // serveur ne saurait plus lire le multipart.
      return apiFetchRaw<{ id: string }>(
        `/api/deliberations/registre/${deliberationId}/pieces`,
        { method: 'POST', body: fd });
    },
    urlPiece: (pieceId: string) =>
      apiFetch<{ url: string }>(`/api/deliberations/pieces/${pieceId}/url`),
    retirerPiece: (pieceId: string) =>
      apiFetch<void>(`/api/deliberations/pieces/${pieceId}`, { method: 'DELETE' }),

    creerSujet: (body: {
      libelle: string; code?: string | null;
      matiereCode?: string | null; ordre?: number;
    }) => apiFetch<unknown>('/api/deliberations/sujets',
      { method: 'POST', body: JSON.stringify(body) }),
    majSujet: (id: string, body: Record<string, unknown>) =>
      apiFetch<unknown>(`/api/deliberations/sujets/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) }),
    /** Supprime une délibération sur laquelle RIEN n'a été fait (409 sinon). */
    supprimerDeliberation: (id: string) =>
      apiFetch<void>(`/api/deliberations/registre/${id}`, { method: 'DELETE' }),
    /** Supprime une séance vide (409 si des délibérations y sont inscrites). */
    supprimerSeance: (id: string) =>
      apiFetch<void>(`/api/deliberations/seances/${id}`, { method: 'DELETE' }),
    /**
     * Revient à l'étape précédente du parcours. Le motif est OBLIGATOIRE : ce
     * n'est pas un effacement, c'est un événement de plus dans l'historique.
     */
    retourEtapePrecedente: (id: string, motif: string) =>
      apiFetch<unknown>(`/api/deliberations/registre/${id}/parcours/retour`,
        { method: 'POST', body: JSON.stringify({ motif }) }),
    /** Qui a fait quoi, quand — la trace existait depuis la mig 244. */
    historiqueParcours: (id: string) =>
      apiFetch<{ historique: Array<{
        le: string | null; de: string | null; vers: string;
        par: string | null; commentaire: string | null;
      }> }>(`/api/deliberations/registre/${id}/parcours/historique`),
    /**
     * Supprime un sujet créé par erreur. Renvoie 409 (`SUJET_UTILISE`) si le
     * sujet est déjà rattaché à une délibération : dans ce cas seule la
     * désactivation est possible, l'historique doit rester lisible.
     */
    supprimerSujet: (id: string) =>
      apiFetch<void>(`/api/deliberations/sujets/${id}`, { method: 'DELETE' }),
    creerConseiller: (body: {
      nom: string; prenom: string;
      qualite?: 'maire' | 'adjoint' | 'conseiller';
      rang?: number | null;
    }) => apiFetch<unknown>('/api/deliberations/conseillers',
      { method: 'POST', body: JSON.stringify(body) }),
    majConseiller: (id: string, body: Record<string, unknown>) =>
      apiFetch<unknown>(`/api/deliberations/conseillers/${id}`,
        { method: 'PATCH', body: JSON.stringify(body) }),
  },

  /** Diagnostic du stockage R2 — l'aller-retour que les tests ne peuvent pas faire. */
  storageDiagnostic: () =>
    apiFetch<{
      configure: boolean; operationnel: boolean; premiere_rupture: string | null;
      etapes: Array<{
        etape: string; ce_qui_est_verifie: string; ok: boolean;
        detail: string | null; duree_ms: number | null;
      }>;
    }>('/api/admin/storage/diagnostic'),

  admin: {
    /**
     * Périmètres d'administration (« bascule DGS »).
     * ⚠️ Réservé à un admin COMPLET — garde serveur `requireAdminComplet`.
     */
    perimetres: {
      list: () => apiFetch<{ briquesDisponibles: string[]; admins: AdminPerimetre[] }>(
        '/api/admin/perimetres'),
      set: (userId: string, data: { briques: string[] | null; vueMaire?: boolean; parametres?: boolean }) =>
        apiFetch<AdminPerimetre>(`/api/admin/perimetres/${userId}`, {
          method: 'PUT', body: JSON.stringify(data),
        }),
    },
    /** Catalogue des notifications (lot N3). */
    catalogueNotifications: () =>
      apiFetch<{ entrees: CatalogueNotificationEntree[] }>('/api/admin/notifications'),
    /** Règle un canal pour la commune. `null` = revenir au défaut éditeur. */
    setCanalNotification: (evenement: string, canal: 'email' | 'push', valeur: boolean | null) =>
      apiFetch<{ ok: true }>(`/api/admin/notifications/${evenement}/canal`, {
        method: 'PUT', body: JSON.stringify({ canal, valeur }),
      }),
    /** Mesure l'impact d'un désabonnement AVANT de l'appliquer (avertissement). */
    impactDesabonnement: (evenement: string) =>
      apiFetch<{ restant: number; dernier: boolean; contexteAdministre: boolean }>(
        `/api/admin/notifications/${evenement}/desabonnement/impact`),
    /** Se retire (ou se remet) d'un événement. `motif` requis si dernier destinataire. */
    setDesabonnementNotification: (evenement: string, desabonne: boolean, motif?: string) =>
      apiFetch<{ ok: true }>(`/api/admin/notifications/${evenement}/desabonnement`, {
        method: 'PUT', body: JSON.stringify({ desabonne, ...(motif ? { motif } : {}) }),
      }),
    /** Échéances & rappels (socle Lot B, mig 211). */
    echeances: () => apiFetch<{ entrees: EcheanceReglageEntree[] }>('/api/admin/echeances'),
    /** Règle les délais d'une échéance (1 à 2 rappels). Liste vide = défaut éditeur. */
    setReglageEcheance: (evenement: string, rappels: RappelDelai[]) =>
      apiFetch<EcheanceReglageEntree>(`/api/admin/echeances/${evenement}`, {
        method: 'PUT', body: JSON.stringify({ rappels }),
      }),
    // ── Archives communales (item 11.2, mig 275) ──
    archives: {
      series: (toutes = false) =>
        apiFetch<{ series: ArchivesSerie[] }>(
          `/api/admin/archives/series${toutes ? '?toutes=true' : ''}`),
      majSerie: (id: string, body: { libelle?: string; ordre?: number; actif?: boolean }) =>
        apiFetch<{ series: ArchivesSerie[] }>(`/api/admin/archives/series/${id}`,
          { method: 'PATCH', body: JSON.stringify(body) }),
      contenants: (inactifs = false) =>
        apiFetch<{ contenants: ArchivesContenant[] }>(
          `/api/admin/archives/contenants${inactifs ? '?inactifs=1' : ''}`),
      creerContenant: (body: {
        code: string; libelle: string; typeContenant: string;
        lieuId?: string | null; local?: string | null;
        metrageLineaire?: number | null; notes?: string | null; actif: boolean;
        /** Encombrement au sol (mig 281) — sert au plan, pas à la capacité. */
        largeurSol?: number | null; profondeurSol?: number | null;
      }) => apiFetch<{ id: string }>('/api/admin/archives/contenants',
        { method: 'POST', body: JSON.stringify(body) }),
      majContenant: (id: string, body: Record<string, unknown>) =>
        apiFetch<{ ok: true }>(`/api/admin/archives/contenants/${id}`,
          { method: 'PUT', body: JSON.stringify(body) }),
      supprimerContenant: (id: string) =>
        apiFetch<void>(`/api/admin/archives/contenants/${id}`, { method: 'DELETE' }),
      /** M4 — photo du contenant (imprimée sur le PV de récolement). */
      photoContenant: {
        lien: (id: string) => apiFetch<{ url: string | null }>(`/api/admin/archives/contenants/${id}/photo`),
        deposer: (id: string, f: File) => {
          const fd = new FormData(); fd.append('photo', f);
          return apiFetchRaw<{ ok: true }>(`/api/admin/archives/contenants/${id}/photo`, { method: 'POST', body: fd });
        },
        retirer: (id: string) =>
          apiFetch<void>(`/api/admin/archives/contenants/${id}/photo`, { method: 'DELETE' }),
      },
      unites: (contenantId: string) =>
        apiFetch<{ unites: ArchivesUnite[] }>(
          `/api/admin/archives/contenants/${contenantId}/unites`),
      creerUnite: (body: Record<string, unknown>) =>
        apiFetch<{ id: string }>('/api/admin/archives/unites',
          { method: 'POST', body: JSON.stringify(body) }),
      majUnite: (id: string, body: Record<string, unknown>) =>
        apiFetch<{ ok: true }>(`/api/admin/archives/unites/${id}`,
          { method: 'PUT', body: JSON.stringify(body) }),
      supprimerUnite: (id: string) =>
        apiFetch<void>(`/api/admin/archives/unites/${id}`, { method: 'DELETE' }),
      recolements: () =>
        apiFetch<{ recolements: ArchivesRecolement[] }>('/api/admin/archives/recolements'),
      ouvrirRecolement: (body: { libelle: string; motif?: string | null; debutLe?: string }) =>
        apiFetch<{ id: string }>('/api/admin/archives/recolements',
          { method: 'POST', body: JSON.stringify(body) }),
      etatRecolement: (id: string) =>
        apiFetch<{ etat: ArchivesRecolementLigne[] }>(
          `/api/admin/archives/recolements/${id}/etat`),
      /** UPSERT : repasser corrige le constat, ça n'empile pas deux observations. */
      enregistrerPassage: (id: string, body: {
        contenantId: string; constat: 'conforme' | 'ecart' | 'introuvable';
        observations?: string | null; metrageConstate?: number | null;
      }) => apiFetch<{ ok: true }>(`/api/admin/archives/recolements/${id}/passage`,
        { method: 'POST', body: JSON.stringify(body) }),
      /** Refusé tant qu'un contenant actif n'a pas été vu. */
      cloreRecolement: (id: string) =>
        apiFetch<{ ok: true }>(`/api/admin/archives/recolements/${id}/clore`,
          { method: 'POST' }),

      // ── Catégories : la nomenclature de LA MAIRIE (mig 276) ──
      categories: (toutes = false) =>
        apiFetch<{ categories: ArchivesCategorie[] }>(
          `/api/admin/archives/categories${toutes ? '?toutes=true' : ''}`),
      creerCategorie: (body: { code: string; libelle: string; couleur?: string | null; actif: boolean }) =>
        apiFetch<{ id: string }>('/api/admin/archives/categories',
          { method: 'POST', body: JSON.stringify(body) }),
      majCategorie: (id: string, body: Record<string, unknown>) =>
        apiFetch<{ ok: true }>(`/api/admin/archives/categories/${id}`,
          { method: 'PATCH', body: JSON.stringify(body) }),
      supprimerCategorie: (id: string) =>
        apiFetch<void>(`/api/admin/archives/categories/${id}`, { method: 'DELETE' }),

      // ── Le scan : recherche par CODE, celui imprimé sous le QR ──
      scanContenant: (code: string) =>
        apiFetch<{
          contenant: {
            id: string; code: string; libelle: string; typeContenant: string;
            lieuNom: string | null; local: string | null;
            metrageLineaire: number | null; notes: string | null;
          };
          unites: ArchivesUnite[];
          campagneEnCours: { id: string; libelle: string; dejaVu: boolean; constat: string | null } | null;
        }>(`/api/admin/archives/scan/contenant/${encodeURIComponent(code)}`),
      scanCategorie: (code: string) =>
        apiFetch<{
          categorie: { id: string; libelle: string; code: string };
          unites: Array<{
            id: string; numeroDossier: string | null; numeroArchives: string | null;
            intitule: string; anneeDebut: number | null; anneeFin: number | null;
            metrageLineaire: number | null;
            contenantCode: string; contenantLibelle: string;
            lieuNom: string | null; local: string | null;
          }>;
        }>(`/api/admin/archives/scan/categorie/${encodeURIComponent(code)}`),

      // ── Impression et échanges (URL directes : téléchargements) ──
      // Mêmes raisons que ci-dessus : le jeton est en mémoire, un lien nu
      // renvoie 401. Tous les téléchargements passent par `downloadCompta`.
      etiquettes: (quoi: 'armoires' | 'categories' | 'tout' = 'tout') =>
        downloadCompta(`/api/admin/archives/etiquettes.pdf?quoi=${quoi}`,
          'etiquettes-archives.pdf'),
      exportRecolementXlsx: (id: string) =>
        downloadCompta(`/api/admin/archives/recolements/${id}/export.xlsx`,
          'recolement-archives.xlsx'),
      // ── M5 — fiches fantômes : sortie / retour d'un dossier ──
      sortirUnite: (id: string, body: { emprunteurNom?: string | null; motif?: string | null; retourPrevu?: string | null }) =>
        apiFetch<{ id: string }>(`/api/admin/archives/unites/${id}/sortie`, { method: 'POST', body: JSON.stringify(body) }),
      rendreUnite: (id: string) => apiFetch<void>(`/api/admin/archives/unites/${id}/retour`, { method: 'POST', body: '{}' }),
      // ── M3 — DUA échue → bordereau d'élimination → visa des AD ──
      eliminables: () => apiFetch<{ eliminables: UniteEliminableDTO[]; aVenir: UniteEliminableDTO[] }>('/api/admin/archives/eliminables'),
      bordereaux: () => apiFetch<{ bordereaux: BordereauDTO[] }>('/api/admin/archives/bordereaux'),
      creerBordereau: (body: { uniteIds: string[]; serviceVersant?: string | null }) =>
        apiFetch<{ id: string; numero: string }>('/api/admin/archives/bordereaux', { method: 'POST', body: JSON.stringify(body) }),
      bordereauPdf: (id: string, numero: string) =>
        downloadCompta(`/api/admin/archives/bordereaux/${id}/pdf`, `bordereau-elimination-${numero}.pdf`),
      envoyerBordereau: (id: string) => apiFetch<void>(`/api/admin/archives/bordereaux/${id}/envoyer`, { method: 'POST', body: '{}' }),
      visaBordereau: (id: string, body: { visaDate: string; visaReference?: string | null }) =>
        apiFetch<void>(`/api/admin/archives/bordereaux/${id}/visa`, { method: 'POST', body: JSON.stringify(body) }),
      destructionBordereau: (id: string, body: { dateDestruction: string; modeDestruction: string }) =>
        apiFetch<void>(`/api/admin/archives/bordereaux/${id}/destruction`, { method: 'POST', body: JSON.stringify(body) }),
      annulerBordereau: (id: string) => apiFetch<void>(`/api/admin/archives/bordereaux/${id}/annuler`, { method: 'POST', body: '{}' }),
      exportRecolementPdf: (id: string) =>
        downloadCompta(`/api/admin/archives/recolements/${id}/export.pdf`,
          'proces-verbal-recolement.pdf'),
      modeleImport: () =>
        downloadCompta('/api/admin/archives/import/modele.xlsx',
          'modele-import-archives.xlsx'),
      // ── Le plan de salle (mig 276) ──
      locaux: () => apiFetch<{ locaux: Array<{
        id: string; libelle: string; lieuId: string | null; lieuNom: string | null;
        largeur: number; hauteur: number; nbContenants: number;
      }> }>('/api/admin/archives/locaux'),
      creerLocal: (body: { libelle: string; lieuId?: string | null; largeur?: number; hauteur?: number }) =>
        apiFetch<{ id: string }>('/api/admin/archives/locaux',
          { method: 'POST', body: JSON.stringify(body) }),
      planLocal: (id: string) => apiFetch<{
        local: {
          libelle: string; largeur: number; hauteur: number;
          largeurM: number | null; hauteurM: number | null;
        };
        traits: Array<{
          id: string; x1: number; y1: number; x2: number; y2: number;
          type: string; libelle: string | null;
        }>;
        totalMetrage: number;
        contenants: Array<{
          id: string; code: string; libelle: string;
          planX: number | null; planY: number | null;
          planLargeur: number | null; planHauteur: number | null;
          metrageLineaire: number | null; metrageOccupe: number; nbUnites: number;
          /** Encombrement au sol (mig 281) — donne sa taille au meuble sur le plan. */
          largeurSol: number | null; profondeurSol: number | null;
        }>;
      }>(`/api/admin/archives/locaux/${id}/plan`),
      /** Coordonnées en POURCENTAGE : le plan doit rester juste sur mobile. */
      positionnerContenant: (id: string, body: {
        planX: number | null; planY: number | null; localId?: string | null;
      }) => apiFetch<{ ok: true }>(`/api/admin/archives/contenants/${id}/position`,
        { method: 'PUT', body: JSON.stringify(body) }),
      /** Les dimensions réelles donnent l'échelle du quadrillage. */
      majDimensionsLocal: (id: string, body: { largeurM: number | null; hauteurM: number | null }) =>
        apiFetch<{ ok: true }>(`/api/admin/archives/locaux/${id}/dimensions`,
          { method: 'PUT', body: JSON.stringify(body) }),
      ajouterTrait: (localId: string, body: {
        x1: number; y1: number; x2: number; y2: number; type: string;
      }) => apiFetch<{ id: string }>(`/api/admin/archives/locaux/${localId}/traits`,
        { method: 'POST', body: JSON.stringify(body) }),
      supprimerTrait: (id: string) =>
        apiFetch<void>(`/api/admin/archives/traits/${id}`, { method: 'DELETE' }),

      importer: (fichier: File) => {
        const fd = new FormData();
        fd.append('fichier', fichier);
        // `apiFetchRaw` et non `apiFetch` : ce dernier force
        // `Content-Type: application/json`, le serveur ne saurait plus lire le
        // multipart. Vérifié dans l'en-tête d'`api.ts`, pas supposé.
        return apiFetchRaw<{
          contenantsCrees: number; unitesCreees: number; categoriesCreees: number;
          ignorees: number; erreurs: string[];
        }>('/api/admin/archives/import', { method: 'POST', body: fd });
      },
    },
    // ── Import / export du parc bâtimentaire, par ORDRE de dépendance ──
    parcImport: {
      /** La séquence à suivre. L'étape est un NUMÉRO, la couleur ne fait que renforcer. */
      ordre: () => apiFetch<{
        rappel: string;
        etapes: Array<{
          numero: number; titre: string; sousTitre: string; couleur: string;
          // Les QUATORZE thèmes, une seule source de vérité côté serveur.
          // `famille` dit quel point d'API sert le modèle et l'export : le parc
          // a les siens (listes déroulantes), les thèmes historiques les leurs.
          themes: Array<{
            theme: string; titre: string; desc: string;
            etape: number; dependances: string[];
            famille: 'parc' | 'legacy' | 'rh'; modeleDispo: boolean;
          }>;
        }>;
      }>('/api/admin/parc-import/ordre'),
      /**
       * ⚠️ TÉLÉCHARGEMENT AUTHENTIFIÉ, PAS UN LIEN.
       *
       * Le jeton vit EN MÉMOIRE (protection XSS, voir l'en-tête de ce fichier) :
       * un `<a href>` ouvre l'URL sans en-tête `Authorization` et récupère un
       * **401**. C'est exactement ce qui s'est produit le 30/07 sur tous les
       * boutons Modèle et Exporter — écran noir avec `{"error":"Authentification
       * requise"}`.
       *
       * `downloadFichier` fait le fetch avec le jeton, puis déclenche le
       * téléchargement depuis le blob. C'est le seul chemin valable ici.
       */
      modele: (theme: string) =>
        downloadCompta(`/api/admin/parc-import/${theme}/modele`, `modele-${theme}.xlsx`),
      /** L'export sort au FORMAT DU MODÈLE : exporter, corriger, réimporter. */
      exporter: (theme: string) =>
        downloadCompta(`/api/admin/parc-import/${theme}/export`, `${theme}.xlsx`),
      importer: (theme: string, fichier: File) => {
        const fd = new FormData();
        fd.append('fichier', fichier);
        return apiFetchRaw<{
          theme: string; crees: number; ignorees: number;
          lieux: { rattaches: number; crees: number; nomsCrees: string[] };
          erreurs: string[];
        }>(`/api/admin/parc-import/${theme}`, { method: 'POST', body: fd });
      },
    },
    /** Référentiel de lieux communaux (socle S4-a, mig 203). */
    lieux: {
    // ── Visites de sécurité ERP (item 4.5) ──
    // ⚠️ `lieuxRouter` est monté sur /api/admin : ces routes-là l'avaient oublié,
    // d'où « Route inconnue » et deux 404 sur l'onglet Sécurité ERP (28/07).
    erpASuivre: () => apiFetch<ErpASuivre[]>('/api/admin/lieux/visites-securite/erp'),
    visitesSecurite: (lieuId?: string) =>
      apiFetch<ErpVisiteSecurite[]>(`/api/admin/lieux/visites-securite${lieuId ? `?lieuId=${lieuId}` : ''}`),
    enregistrerVisiteSecurite: (body: {
      lieuId: string; dateVisite: string; avis: ErpAvisVisite;
      prescriptions?: string | null; referencePv?: string | null;
    }) => apiFetch<{ visite: ErpVisiteSecurite; prochaine: string | null; prolongee: boolean }>(
      '/api/admin/lieux/visites-securite', { method: 'POST', body: JSON.stringify(body) }),
    supprimerVisiteSecurite: (id: string) =>
      apiFetch<void>(`/api/admin/lieux/visites-securite/${id}`, { method: 'DELETE' }),
    reglerPeriodiciteVisite: (lieuId: string, periodiciteMois: number | null) =>
      apiFetch<{ prochaine: string | null }>(`/api/admin/lieux/${lieuId}/visite-periodicite`,
        { method: 'PUT', body: JSON.stringify({ periodiciteMois }) }),

    // ── Contrats & baux des bâtiments (item 7.5, mig 271) ──
    // Lisible par le service technique OU par le comptable depuis sa brique
    // (`requireAnyPermission` côté serveur) ; modifiable par le seul ST.
    contrats: (f: { sens?: string; categorie?: string; lieuId?: string; inactifs?: boolean } = {}) => {
      const p = new URLSearchParams();
      if (f.sens) p.set('sens', f.sens);
      if (f.categorie) p.set('categorie', f.categorie);
      if (f.lieuId) p.set('lieuId', f.lieuId);
      if (f.inactifs) p.set('inactifs', '1');
      const qs = p.toString();
      return apiFetch<{
        contrats: BatimentContrat[];
        categories: ContratCategorie[];
        periodicites: ContratPeriodicite[];
      }>(`/api/admin/lieux/contrats${qs ? `?${qs}` : ''}`);
    },
    contrat: (id: string) => apiFetch<BatimentContrat>(`/api/admin/lieux/contrats/${id}`),
    /** Les contrats d'un bâtiment — alimente la fiche lieu. */
    contratsDuLieu: (lieuId: string) =>
      apiFetch<{ contrats: BatimentContrat[] }>(`/api/admin/lieux/${lieuId}/contrats`),
    creerContrat: (body: BatimentContratInput) =>
      apiFetch<BatimentContrat>('/api/admin/lieux/contrats',
        { method: 'POST', body: JSON.stringify(body) }),
    majContrat: (id: string, body: BatimentContratInput) =>
      apiFetch<BatimentContrat>(`/api/admin/lieux/contrats/${id}`,
        { method: 'PUT', body: JSON.stringify(body) }),
    supprimerContrat: (id: string) =>
      apiFetch<void>(`/api/admin/lieux/contrats/${id}`, { method: 'DELETE' }),

    // ── Clés des bâtiments (item 11.1, mig 274) ──
    // Lisible par le service technique OU la GRC depuis sa brique.
    cles: (lieuId?: string) =>
      apiFetch<{ portes: PorteBatiment[] }>(
        `/api/admin/lieux/cles${lieuId ? `?lieuId=${lieuId}` : ''}`),
    clesSorties: () =>
      apiFetch<{ sorties: CleSortie[] }>('/api/admin/lieux/cles/sorties'),
    /**
     * 9.2 — déclarer des clés depuis la fiche d'un lieu, sans parler de porte.
     * La porte « Accès principal » est créée par le serveur si le lieu n'en a
     * aucune : c'est le raccourci de mise en service d'une salle.
     */
    declarerClesDuLieu: (lieuId: string, numeros: string[]) =>
      apiFetch<{ portes: PorteBatiment[] }>(`/api/admin/lieux/cles/lieu/${lieuId}`,
        { method: 'POST', body: JSON.stringify({ numeros }) }),
    /** Efface une clé. Le rang libéré est repris par la prochaine de la porte. */
    supprimerCle: (id: string) =>
      apiFetch<void>(`/api/admin/lieux/cles/exemplaires/${id}`, { method: 'DELETE' }),
    /**
     * La feuille de correspondance à mettre dans la boîte à clés.
     * Passe par `downloadFichier` : le jeton est en mémoire, un `<a href>`
     * renverrait 401 et une page noire.
     */
    clesDuLieuPdf: (lieuId: string, nomFichier: string) =>
      downloadCompta(`/api/admin/lieux/cles/lieu/${lieuId}/pdf`, nomFichier),
    /** Toutes les clés de la commune, pour le classeur du bureau. */
    clesCommunePdf: () =>
      downloadCompta('/api/admin/lieux/cles/pdf', 'cles-commune.pdf'),
    creerPorte: (body: {
      lieuId: string; libelle: string;
      emplacement?: string | null; notes?: string | null; actif: boolean;
    }) => apiFetch<PorteBatiment>('/api/admin/lieux/cles/portes',
      { method: 'POST', body: JSON.stringify(body) }),
    majPorte: (id: string, body: {
      lieuId: string; libelle: string;
      emplacement?: string | null; notes?: string | null; actif: boolean;
    }) => apiFetch<PorteBatiment>(`/api/admin/lieux/cles/portes/${id}`,
      { method: 'PUT', body: JSON.stringify(body) }),
    supprimerPorte: (id: string) =>
      apiFetch<void>(`/api/admin/lieux/cles/portes/${id}`, { method: 'DELETE' }),
    /** Le numéro d'exemplaire est attribué par le serveur, jamais envoyé. */
    ajouterCle: (porteId: string, body: {
      numeroGrave?: string | null; detenteur?: string | null; notes?: string | null;
    }) => apiFetch<PorteBatiment>(`/api/admin/lieux/cles/portes/${porteId}/exemplaires`,
      { method: 'POST', body: JSON.stringify(body) }),
    changerEtatCle: (cleId: string, etat: 'disponible' | 'perdue' | 'detruite', notes?: string) =>
      apiFetch<{ ok: true }>(`/api/admin/lieux/cles/${cleId}/etat`,
        { method: 'PUT', body: JSON.stringify({ etat, ...(notes ? { notes } : {}) }) }),
    preterCle: (cleId: string, body: {
      emprunteur: string; motif?: string | null;
      remiseLe?: string; retourPrevuLe?: string | null; reservationId?: string | null;
    }) => apiFetch<{ id: string }>(`/api/admin/lieux/cles/${cleId}/pret`,
      { method: 'POST', body: JSON.stringify(body) }),
    rendreCle: (cleId: string, retourLe?: string) =>
      apiFetch<{ ok: true }>(`/api/admin/lieux/cles/${cleId}/retour`,
        { method: 'POST', body: JSON.stringify(retourLe ? { retourLe } : {}) }),
    // ── Plan du bâtiment (clés) — même composant que les archives ──
    planCles: (lieuId: string) => apiFetch<{
      portes: Array<{
        id: string; libelle: string; emplacement: string | null;
        planX: number | null; planY: number | null;
        nbCles: number; nbSorties: number;
      }>;
      traits: Array<{
        id: string; x1: number; y1: number; x2: number; y2: number;
        type: string; libelle: string | null;
      }>;
    }>(`/api/admin/lieux/cles/plan/${lieuId}`),
    ajouterTraitCles: (lieuId: string, body: {
      x1: number; y1: number; x2: number; y2: number; type: string;
    }) => apiFetch<{ id: string }>(`/api/admin/lieux/cles/plan/${lieuId}/traits`,
      { method: 'POST', body: JSON.stringify(body) }),
    supprimerTraitCles: (id: string) =>
      apiFetch<void>(`/api/admin/lieux/cles/plan/traits/${id}`, { method: 'DELETE' }),
    // ── M23 + M24 — « État des locaux » ──
    etatDesLocaux: (lieuId: string) => apiFetch<EtatDesLocauxDTO>(`/api/admin/lieux/${lieuId}/etat-des-locaux`),
    /** Chantier 7 (mig 319) — photos du bâtiment et plan d'évacuation versionné. */
    documents: (lieuId: string) => apiFetch<DocumentsLieuDTO>(`/api/admin/lieux/${lieuId}/documents`),
    ajouterDocument: (lieuId: string, type: 'photo' | 'plan_evacuation', f: File, libelle?: string) => {
      const fd = new FormData(); fd.append('type', type); fd.append('fichier', f); if (libelle) fd.append('libelle', libelle);
      return apiFetchRaw<{ id: string }>(`/api/admin/lieux/${lieuId}/documents`, { method: 'POST', body: fd });
    },
    retirerPhoto: (docId: string) => apiFetch<void>(`/api/admin/lieux/documents/${docId}`, { method: 'DELETE' }),
    /** Chantier 7, étape 3 (mig 321) — le carnet du local. */
    carnet: (lieuId: string) => apiFetch<CarnetDTO>(`/api/admin/lieux/${lieuId}/carnet`),
    enregistrerPassage: (edlId: string, body: { sens: 'entree' | 'sortie'; occupant: string; datePassage?: string; checklist: PointCarnet[]; constats?: string | null }) =>
      apiFetch<{ id: string }>(`/api/service-technique/signalements/${edlId}/passages`, { method: 'POST', body: JSON.stringify(body) }),
    declarerFiche: (edlId: string, pointId: string, body: { note?: string | null; urgence?: 'normal' | 'rapide' | 'urgent' }) =>
      apiFetch<{ ficheId: string; numero: string }>(`/api/service-technique/signalements/${edlId}/points/${encodeURIComponent(pointId)}/fiche`, { method: 'POST', body: JSON.stringify(body) }),
    deposerPlan: (lieuId: string, f: File) => {
      const fd = new FormData(); fd.append('plan', f);
      return apiFetchRaw<{ ok: true }>(`/api/admin/lieux/${lieuId}/plan`, { method: 'POST', body: fd });
    },
    retirerPlan: (lieuId: string) => apiFetch<void>(`/api/admin/lieux/${lieuId}/plan`, { method: 'DELETE' }),
    situerIntervention: (signalementId: string, body: { lieuId: string | null; x?: number | null; y?: number | null }) =>
      apiFetch<{ ok: true }>(`/api/admin/lieux/interventions/${signalementId}/situation`, { method: 'PUT', body: JSON.stringify(body) }),
    positionnerPorte: (id: string, body: { planX: number | null; planY: number | null }) =>
      apiFetch<{ ok: true }>(`/api/admin/lieux/cles/portes/${id}/position`,
        { method: 'PUT', body: JSON.stringify(body) }),
    historiqueCle: (cleId: string) =>
      apiFetch<{ prets: Array<{
        id: string; emprunteur: string; motif: string | null;
        remiseLe: string; retourPrevuLe: string | null; retourLe: string | null;
        reservationId: string | null;
      }> }>(`/api/admin/lieux/cles/${cleId}/historique`),

      list: (inclureInactifs = false) =>
        apiFetch<{ lieux: LieuDTO[] }>(`/api/admin/lieux${inclureInactifs ? '?inactifs=1' : ''}`),
      create: (data: LieuInput) =>
        apiFetch<{ id: string }>('/api/admin/lieux', { method: 'POST', body: JSON.stringify(data) }),
      update: (id: string, data: Partial<LieuInput> & { actif?: boolean }) =>
        apiFetch<{ ok: boolean }>(`/api/admin/lieux/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      briques: () => apiFetch<{ briques: string[] }>('/api/admin/lieux/briques'),
      occupants: (id: string) =>
        apiFetch<{
          occupants: OccupantLieuDTO[];
          occupations: Array<{ id: string; dateDebut: string; dateFin: string; motif: string; sourceType: string }>;
        }>(`/api/admin/lieux/${id}/occupants`),
      types: {
        list: () => apiFetch<{ types: LieuTypeDTO[] }>('/api/admin/lieux/types'),
        create: (data: { libelle: string; ordre?: number }) =>
          apiFetch<LieuTypeDTO>('/api/admin/lieux/types', { method: 'POST', body: JSON.stringify(data) }),
        update: (id: string, data: { libelle?: string; ordre?: number; actif?: boolean }) =>
          apiFetch<{ ok: boolean }>(`/api/admin/lieux/types/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
      },
    },
    modules: {
      list: () => apiFetch<{ modules: { moduleKey: string; actif: boolean; socle: boolean }[] }>('/api/admin/modules'),
      set: (key: string, actif: boolean) =>
        apiFetch<{ moduleKey: string; actif: boolean; socle: boolean }>(`/api/admin/modules/${key}`, { method: 'PUT', body: JSON.stringify({ actif }) }),
      listVisibiliteAdministre: () => apiFetch<{ visibilite: { key: string; visible: boolean }[] }>('/api/admin/modules-administre'),
      setVisibiliteAdministre: (key: string, visible: boolean) =>
        apiFetch<{ key: string; visible: boolean }>(`/api/admin/modules-administre/${key}`, { method: 'PUT', body: JSON.stringify({ visible }) }),
    },
    users: {
      list: (filters?: AdminUserListFilters) => {
        const qs = filters
          ? '?' +
            new URLSearchParams(
              Object.entries(filters)
                .filter(([, v]) => v !== undefined && v !== null && v !== '')
                .map(([k, v]) => [k, String(v)])
            ).toString()
          : '';
        return apiFetch<AdminUserListResponse>(`/api/admin/users${qs}`);
      },
      get: (id: string) => apiFetch<AdminUserDetail>(`/api/admin/users/${id}`),
      update: (id: string, body: AdminUserUpdateRequest) =>
        apiFetch<AdminUserDetail>(`/api/admin/users/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      suspend: (id: string) =>
        apiFetch<AdminUserDetail>(`/api/admin/users/${id}/suspend`, { method: 'POST' }),
      reactivate: (id: string) =>
        apiFetch<AdminUserDetail>(`/api/admin/users/${id}/reactivate`, { method: 'POST' }),
      unlock: (id: string) =>
        apiFetch<AdminUserDetail>(`/api/admin/users/${id}/unlock`, { method: 'POST' }),
      forcePasswordReset: (id: string) =>
        apiFetch<{ ok: true; emailSent: boolean }>(
          `/api/admin/users/${id}/force-password-reset`,
          { method: 'POST' }
        ),
      delete: (id: string) =>
        apiFetch<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),
    },
    roles: {
      matrix: () => apiFetch<AdminRoleMatrixResponse>('/api/admin/roles'),
      updatePermissions: (role: UserRole, body: AdminUpdateRolePermissionsRequest) =>
        apiFetch<AdminRoleMatrixResponse>(`/api/admin/roles/${role}/permissions`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
    },
    auditLog: (filters?: AdminAuditLogFilters) => {
      const qs = filters
        ? '?' +
          new URLSearchParams(
            Object.entries(filters)
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => [k, String(v)])
          ).toString()
        : '';
      return apiFetch<AdminAuditLogResponse>(`/api/admin/audit-log${qs}`);
    },
    /** Export Excel du journal d'audit — Lot C 2.7. Mêmes filtres, sans pagination. */
    auditLogExportExcel: (filters?: AdminAuditLogFilters) => {
      const qs = filters
        ? '?' +
          new URLSearchParams(
            Object.entries(filters)
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => [k, String(v)])
          ).toString()
        : '';
      return downloadCompta(`/api/admin/audit-log/export/excel${qs}`, 'journal-audit.xlsx');
    },
    /** Export généralisé par thème — Lot C 2.6 (miroir de l'import Excel). */
    exportThemes: () =>
      apiFetch<{ themes: { theme: string; feuille: string }[] }>('/api/admin/export/themes'),
    exportThemeExcel: (theme: string) =>
      downloadCompta(`/api/admin/export/${theme}/excel`, `export-${theme}.xlsx`),

    /**
     * Import de référentiels par la commune — symétrique de l'export.
     *
     * Flux : modèle → prévisualisation (dry-run, n'écrit rien) → validation.
     * Le tenant vient du token côté serveur : impossible d'écrire ailleurs.
     */
    importThemes: () => apiFetch<{ themes: string[] }>('/api/admin/import/themes'),
    importModele: (theme: string) =>
      downloadCompta(`/api/admin/import/${theme}/modele`, `modele-${theme}.xlsx`),
    importPreview: (theme: string, fichier: File) => {
      const fd = new FormData();
      fd.append('fichier', fichier);
      return apiFetchRaw<ImportApercu>(`/api/admin/import/${theme}/preview`, { method: 'POST', body: fd });
    },
    importCommit: (theme: string, fichier: File) => {
      const fd = new FormData();
      fd.append('fichier', fichier);
      return apiFetchRaw<ImportResultat>(`/api/admin/import/${theme}/commit`, { method: 'POST', body: fd });
    },
    // ---------- L13.7 — Profil tenant éditable ----------
    tenant: {
      /** GET /api/me/tenant — détail complet du tenant courant (lecture). */
      get: () => apiFetch<TenantDetailShared>('/api/me/tenant'),
      /** GET /api/me/fond-ecran — fond du tableau de bord (mig 294), tout connecté. */
      fondEcran: () => apiFetch<FondEcran>('/api/me/fond-ecran'),
      /** PUT /api/admin/tenant — mise à jour des champs éditables (admin only). */
      update: (body: UpdateTenantInputShared) =>
        apiFetch<TenantDetailShared>('/api/admin/tenant', {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      /** PUT /api/admin/payfip-config — PayFiP + mode e-facture (perm payfip.manage). */
      payfipConfig: (body: UpdatePayfipConfigInputShared) =>
        apiFetch<TenantDetailShared>('/api/admin/payfip-config', {
          method: 'PUT',
          body: JSON.stringify(body),
        }),
      /** Identité mairie : logo / en-tête / pied (migration 132). */
      identite: {
        get: () => apiFetch<IdentiteMairie>('/api/admin/identite'),
        setPied: (texte: string | null) =>
          apiFetch<IdentiteMairie>('/api/admin/identite/pied', { method: 'PUT', body: JSON.stringify({ texte }) }),
        /** Signataire et juridiction (mig 258) — alimentent {{maire_nom}},
            {{maire_titre}} et {{tribunal_administratif}} des modèles d'actes. */
        setSignataire: (body: {
          maireNom?: string | null; maireTitre?: string | null; tribunalAdministratif?: string | null;
        }) => apiFetch<IdentiteMairie>('/api/admin/identite/signataire',
          { method: 'PUT', body: JSON.stringify(body) }),
        uploadLogo: (file: File | Blob, filename?: string) => {
          const fd = new FormData();
          fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'logo'));
          return apiFetchRaw<IdentiteMairie>('/api/admin/identite/logo', { method: 'POST', body: fd });
        },
        uploadEntete: (file: File | Blob, filename?: string) => {
          const fd = new FormData();
          fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'entete'));
          return apiFetchRaw<IdentiteMairie>('/api/admin/identite/entete', { method: 'POST', body: fd });
        },
        removeLogo: () => apiFetch<IdentiteMairie>('/api/admin/identite/logo', { method: 'DELETE' }),
        removeEntete: () => apiFetch<IdentiteMairie>('/api/admin/identite/entete', { method: 'DELETE' }),
        /** Fond d'écran du tableau de bord (mig 294). */
        setFondEcran: (choix: 'aucun' | 'mer' | 'prairie' | 'montagne') =>
          apiFetch<FondEcran>('/api/admin/identite/fond-ecran', { method: 'PUT', body: JSON.stringify({ choix }) }),
        /** Intensité du voile (mig 296) : 40 (photo très présente) → 95 (presque unie). */
        setFondVoile: (voile: number) =>
          apiFetch<FondEcran>('/api/admin/identite/fond-ecran', { method: 'PUT', body: JSON.stringify({ voile }) }),
        uploadFondEcran: (file: File | Blob, filename?: string) => {
          const fd = new FormData();
          fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'fond'));
          return apiFetchRaw<FondEcran>('/api/admin/identite/fond-ecran', { method: 'POST', body: fd });
        },
      },
      /** Identités prestataires : documents cantine/périscolaire/PE délégués (mig 210). */
      identitesPrestataires: {
        briques: () => apiFetch<BriqueDeleguableMeta[]>('/api/admin/identites-prestataires/briques'),
        list: () => apiFetch<DocumentIdentite[]>('/api/admin/identites-prestataires'),
        get: (id: string) => apiFetch<DocumentIdentite>(`/api/admin/identites-prestataires/${id}`),
        create: (body: DocumentIdentiteInput) =>
          apiFetch<DocumentIdentite>('/api/admin/identites-prestataires', { method: 'POST', body: JSON.stringify(body) }),
        update: (id: string, body: DocumentIdentiteInput) =>
          apiFetch<DocumentIdentite>(`/api/admin/identites-prestataires/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
        setActif: (id: string, actif: boolean) =>
          apiFetch<DocumentIdentite>(`/api/admin/identites-prestataires/${id}/actif`, { method: 'POST', body: JSON.stringify({ actif }) }),
        setRattachement: (brique: BriqueDeleguable, identiteId: string | null) =>
          apiFetch<{ ok: boolean }>(`/api/admin/identites-prestataires/rattachement/${brique}`, { method: 'PUT', body: JSON.stringify({ identiteId }) }),
        uploadLogo: (id: string, file: File | Blob, filename?: string) => {
          const fd = new FormData();
          fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'logo'));
          return apiFetchRaw<DocumentIdentite>(`/api/admin/identites-prestataires/${id}/logo`, { method: 'POST', body: fd });
        },
        uploadEntete: (id: string, file: File | Blob, filename?: string) => {
          const fd = new FormData();
          fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'entete'));
          return apiFetchRaw<DocumentIdentite>(`/api/admin/identites-prestataires/${id}/entete`, { method: 'POST', body: fd });
        },
        removeLogo: (id: string) => apiFetch<DocumentIdentite>(`/api/admin/identites-prestataires/${id}/logo`, { method: 'DELETE' }),
        removeEntete: (id: string) => apiFetch<DocumentIdentite>(`/api/admin/identites-prestataires/${id}/entete`, { method: 'DELETE' }),
      },
    },
    // ---------- Réglage « les agents voient le planning du service » (mig 209) ----------
    planningVisibilite: {
      /** GET — état du réglage global (+ surcharges par brique, usage futur). */
      get: () =>
        apiFetch<{ agentsVoientService: boolean; parBrique: Record<string, boolean> }>(
          '/api/admin/planning-visibilite'
        ),
      /** PUT — bascule le réglage global de la commune. */
      setGlobal: (agentsVoientService: boolean) =>
        apiFetch<{ ok: true }>('/api/admin/planning-visibilite', {
          method: 'PUT',
          body: JSON.stringify({ agentsVoientService }),
        }),
    },
    // ---------- L13.9 — Statut sauvegardes BDD ----------
    backups: {
      /** GET /api/admin/backups/status — liste des derniers backups + alerte si en retard. */
      status: (limit = 30) =>
        apiFetch<BackupStatusResponseShared>(`/api/admin/backups/status?limit=${limit}`),
    },
    // ---------- Chorus Pro — diagnostic & test de connexion (super_admin) ----------
    chorus: {
      /** GET /api/admin/chorus/config — état config (présence des secrets, URLs). */
      config: () =>
        apiFetch<{
          actif: boolean; enabled: boolean; baseUrl: string; oauthUrl: string;
          secretsPresents: { clientId: boolean; clientSecret: boolean };
          configManquante: string[]; environnement: string;
        }>('/api/admin/chorus/config'),
      /** POST /api/admin/chorus/ping — test bout-en-bout GLOBAL (identifiants OFAD, super_admin). */
      ping: () =>
        apiFetch<{
          ok: boolean; source: 'mock' | 'reel'; codeRetour: number; libelle: string;
          structures: Array<{ idStructure: number; identifiantStructure: string; typeIdentifiantStructure: string; designationStructure: string; statutUtilisateur: string }>;
        }>('/api/admin/chorus/ping', { method: 'POST' }),
      /** POST /api/admin/chorus/ping/:tenantId — test avec le compte technique d'une commune (admin, hotline). */
      pingTenant: (tenantId: string) =>
        apiFetch<{
          ok: boolean; message?: string; source?: 'mock' | 'reel'; codeRetour?: number; libelle?: string;
          structures?: Array<{ idStructure: number; identifiantStructure: string; typeIdentifiantStructure: string; designationStructure: string; statutUtilisateur: string }>;
        }>(`/api/admin/chorus/ping/${tenantId}`, { method: 'POST' }),
    },
    // ---------- L13.13 — Promotion temporaire/définitive de rôle ----------
    roleOverrides: {
      /** GET /api/admin/role-overrides — historique global du tenant */
      list: (activeOnly = false) =>
        apiFetch<{ items: unknown[] }>(
          `/api/admin/role-overrides${activeOnly ? '?activeOnly=true' : ''}`
        ),
      /** GET /api/admin/users/:userId/role-overrides — historique d'un user */
      forUser: (userId: string, activeOnly = false) =>
        apiFetch<{ items: unknown[] }>(
          `/api/admin/users/${userId}/role-overrides${activeOnly ? '?activeOnly=true' : ''}`
        ),
      /** POST /api/admin/users/:userId/role-overrides — créer un override */
      create: (
        userId: string,
        body: {
          newRole: string;
          newService?: string | null;
          expiresAt?: string | null;
          reason?: string;
        }
      ) =>
        apiFetch<unknown>(`/api/admin/users/${userId}/role-overrides`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      /** DELETE /api/admin/role-overrides/:overrideId — révoquer (body: { reason }) */
      revoke: (overrideId: string, reason: string) =>
        apiFetch<unknown>(`/api/admin/role-overrides/${overrideId}`, {
          method: 'DELETE',
          body: JSON.stringify({ reason }),
        }),
    },
    // ---------- Livraison 6 — Invitations magic-link (Bloc C) ----------
    invitations: {
      list: () => apiFetch<AdminInvitationListResponse>('/api/admin/invitations'),
      create: (body: CreateInvitationRequest) =>
        apiFetch<AdminInvitation>('/api/admin/invitations', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      revoke: (id: string) =>
        apiFetch<{ ok: true }>(`/api/admin/invitations/${id}/revoke`, { method: 'POST' }),
      resend: (id: string) =>
        apiFetch<AdminInvitation>(`/api/admin/invitations/${id}/resend`, { method: 'POST' }),
      // L13.29 — import d'équipe via fichier Excel
      importEquipe: async (file: File) => {
        const form = new FormData();
        form.append('fichier', file);
        const token = getAccessToken();
        const res = await fetch(`${API_BASE}/api/admin/invitations/import`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: form,
          credentials: 'include',
        });
        const payload = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        if (!res.ok) throw new ApiException(res.status, payload);
        return payload as {
          total: number;
          created: { ligne: number; email: string }[];
          errors: { ligne: number; email: string; raison: string }[];
        };
      },
      importTemplate: async () => {
        const token = getAccessToken();
        const res = await fetch(`${API_BASE}/api/admin/invitations/import/template`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          credentials: 'include',
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}` });
        return res.blob();
      },
    },
  },
  // ---------- Livraison 6 — Invitations publiques (acceptation) ----------
  invitations: {
    verify: (token: string) =>
      apiFetch<VerifyInvitationResponse>(
        `/api/invitations/${encodeURIComponent(token)}/verify`,
        {},
        false
      ),
    accept: (token: string, body: AcceptInvitationRequest) =>
      apiFetch<AcceptInvitationResponse>(
        `/api/invitations/${encodeURIComponent(token)}/accept`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
        false
      ),
  },
  // ---------- Phase 2A core L8/L9 — Signalements publics anonymes ----------
  public: {
    signalements: {
      /** POST /api/public/tenants/:slug/signalements — création anonyme par citoyen */
      create: (slug: string, body: {
        sujet: string;
        description: string;
        categorie: string;
        urgence?: 'normal' | 'rapide' | 'urgent';
        localisationAdresse?: string;
        localisationLat?: number;
        localisationLng?: number;
        localisationQuartier?: string;
        email: string;
        nom?: string;
        cguAccepted: true;
        photos?: Array<{ id: string; url: string }>;
      }) =>
        apiFetch<{ codeSuivi: string; numero: string; suiviUrl: string }>(
          `/api/public/tenants/${encodeURIComponent(slug)}/signalements`,
          { method: 'POST', body: JSON.stringify(body) },
          false
        ),
      /** GET /api/public/signalements/:code — suivi anonyme par code */
      getByCode: (code: string) =>
        apiFetch<{
          codeSuivi: string;
          numero: string;
          sujet: string;
          description: string;
          categorie: string;
          urgence: string;
          statut: string;
          statutLabel: string;
          localisationAdresse: string | null;
          localisationQuartier: string | null;
          photos: Array<{ id: string; url: string }>;
          createdAt: string;
          resoluAt: string | null;
          resoluCommentaire: string | null;
          tenantNom: string;
        }>(`/api/public/signalements/${encodeURIComponent(code)}`, {}, false),
    },
    /** GET /api/tenants/public — liste des tenants visibles publiquement */
    tenants: () => apiFetch<TenantPublicListResponse>('/api/tenants/public', {}, false),
    /** GET /api/public/tenants/:slug/poi — points d'intérêt publics (vitrine touriste) */
    poi: (slug: string, famille?: string) =>
      apiFetch<{ commune: { nom: string; slug: string }; points: PoiPoint[] }>(
        `/api/public/tenants/${encodeURIComponent(slug)}/poi${famille ? `?famille=${famille}` : ''}`,
        {}, false
      ),
    /** GET /api/verifier-carte/:token — vérification publique d'une carte déchetterie (scan QR) */
    verifierCarte: (token: string) =>
      apiFetch<{
        valide: boolean;
        statut: 'valide' | 'expiree' | 'revoquee' | 'introuvable';
        numeroCarte: string | null;
        nomAdherent: string | null;
        commune: string | null;
        emiseLe: string | null;
        expireLe: string | null;
      }>(`/api/verifier-carte/${encodeURIComponent(token)}`, {}, false),
  },
  // ---------- Livraison 3 — Plateforme OneForAll Distribution cross-tenant (Bloc A.2) ----------
  platform: {
    tenants: {
      list: (filters?: PlatformTenantListFilters) => {
        const qs = filters
          ? '?' +
            new URLSearchParams(
              Object.entries(filters)
                .filter(([, v]) => v !== undefined && v !== null && v !== '')
                .map(([k, v]) => [k, String(v)])
            ).toString()
          : '';
        return apiFetch<PlatformTenantListResponse>(`/api/platform/tenants${qs}`);
      },
      get: (id: string) =>
        apiFetch<PlatformTenantListItem>(`/api/platform/tenants/${id}`),
      /** Solde SMS courant d'une commune (super_admin). */
      smsSolde: (id: string) =>
        apiFetch<{ soldeSms: number; modeCompte: 'commune' | 'passerelle'; totalConsomme: number }>(`/api/platform/tenants/${id}/sms`),
      /** Créditer le compteur SMS d'une commune après paiement (super_admin). */
      smsCrediter: (id: string, nbSms: number, demandeId?: string) =>
        apiFetch<{ soldeSms: number }>(`/api/platform/tenants/${id}/sms-crediter`, { method: 'POST', body: JSON.stringify({ nbSms, demandeId }) }),
      update: (id: string, body: PlatformTenantUpdateRequest) =>
        apiFetch<PlatformTenantListItem>(`/api/platform/tenants/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      create: (body: {
        nom: string;
        type?: 'commune' | 'epci' | 'syndicat';
        codePostal: string;
        ville?: string;
      }) =>
        apiFetch<PlatformTenantListItem>('/api/platform/tenants', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      impersonate: (id: string) =>
        apiFetch<PlatformImpersonateResponse>(
          `/api/platform/tenants/${id}/impersonate`,
          { method: 'POST' }
        ),
      /** POST /api/platform/tenants/:id/invitations — invite un admin (maire) dans la mairie */
      inviteAdmin: (
        id: string,
        body: { email: string; prenom?: string; nom?: string }
      ) =>
        apiFetch<{ email: string; tenantNom: string }>(
          `/api/platform/tenants/${id}/invitations`,
          { method: 'POST', body: JSON.stringify(body) }
        ),
      /** Demandes de recharge SMS (toutes communes) — super_admin. */
      smsRechargeDemandes: (statut?: string) =>
        apiFetch<{ demandes: Array<{ id: string; tenantId: string; communeNom?: string; nbSmsDemande: number | null; message: string | null; preuveNomFichier: string | null; statut: string; nbSmsCredite: number | null; createdAt: string }> }>(`/api/platform/sms-recharge-demandes${statut ? `?statut=${encodeURIComponent(statut)}` : ''}`),
      /** URL signée de la preuve de paiement d'une demande — super_admin. */
      smsRechargePreuve: (demandeId: string) =>
        apiFetch<{ url: string }>(`/api/platform/sms-recharge-demandes/${demandeId}/preuve`),
    },
    auditLog: (filters?: PlatformAuditLogFilters) => {
      const qs = filters
        ? '?' +
          new URLSearchParams(
            Object.entries(filters)
              .filter(([, v]) => v !== undefined && v !== null && v !== '')
              .map(([k, v]) => [k, String(v)])
          ).toString()
        : '';
      return apiFetch<PlatformAuditLogResponse>(`/api/platform/audit-log${qs}`);
    },
  },
  serviceTechnique: {
    signalements: {
      /** M8 — la photo est-elle exigée pour l'utilisateur connecté ? */
      reglesDeclaration: () =>
        apiFetch<{ photoObligatoire: boolean }>('/api/service-technique/signalements/regles-declaration'),
      create: (body: CreateSignalementInput) =>
        apiFetch<Signalement>('/api/service-technique/signalements', {
          method: 'POST',
          body: JSON.stringify(body),
        }),      list: (filters?: SignalementListFilters) => {
        const qs = filters ? '?' + new URLSearchParams(
          Object.entries(filters)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)])
        ).toString() : '';
        return apiFetch<SignalementListResponse>(`/api/service-technique/signalements${qs}`);
      },
      listMine: (filters?: SignalementListFilters) => {
        const qs = filters ? '?' + new URLSearchParams(
          Object.entries(filters)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => [k, Array.isArray(v) ? v.join(',') : String(v)])
        ).toString() : '';
        return apiFetch<SignalementListResponse>(`/api/service-technique/signalements/mine${qs}`);
      },
      get: (id: string) =>
        apiFetch<Signalement>(`/api/service-technique/signalements/${id}`),
      updateStatut: (id: string, body: UpdateSignalementStatutInput) =>
        apiFetch<Signalement>(`/api/service-technique/signalements/${id}/statut`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      evenements: (id: string) =>
        apiFetch<SignalementEvenement[]>(`/api/service-technique/signalements/${id}/evenements`),
      // Phase 2A core L10 — support backoffice
      agents: () =>
        apiFetch<{ items: Array<{ id: string; fullName: string; email: string; role: string }> }>(
          '/api/service-technique/signalements/support/agents'
        ),
      stats: () =>
        apiFetch<{
          byStatut: Record<string, number>;
          byCategorie: Record<string, number>;
          byUrgenceOpen: Record<string, number>;
          totalAnonymes: number;
        }>('/api/service-technique/signalements/support/stats'),
      // L13.8 — Photos avant/après (Cloudflare R2 + signed URLs)
      listPhotos: (id: string) =>
        apiFetch<{ items: SignalementPhotoShared[] }>(
          `/api/service-technique/signalements/${id}/photos`
        ),
      getPhotoSignedUrl: (id: string, photoId: string) =>
        apiFetch<SignalementPhotoSignedUrlResponseShared>(
          `/api/service-technique/signalements/${id}/photos/${photoId}/signed-url`
        ),
      uploadPhoto: async (
        id: string,
        kind: SignalementPhotoKindShared,
        file: File
      ): Promise<SignalementPhotoShared> => {
        // FormData multipart : on ne passe PAS Content-Type, le navigateur ajoute
        // automatiquement le boundary correct
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('kind', kind);
        return apiFetchRaw<SignalementPhotoShared>(
          `/api/service-technique/signalements/${id}/photos`,
          {
            method: 'POST',
            body: formData,
          }
        );
      },
      deletePhoto: (id: string, photoId: string) =>
        apiFetch<{ ok: true }>(
          `/api/service-technique/signalements/${id}/photos/${photoId}`,
          { method: 'DELETE' }
        ),
      // Demandes de devis (V1, mig 214) — rattachées à une fiche.
      listDevis: (id: string) =>
        apiFetch<DevisDemandeShared[]>(`/api/service-technique/signalements/${id}/devis`),
      demanderDevis: (id: string, body: { tiersId?: string | null; tiersNom: string; tiersEmail: string; objet: string; description: string }) =>
        apiFetch<DevisDemandeShared>(`/api/service-technique/signalements/${id}/devis`, { method: 'POST', body: JSON.stringify(body) }),
      // État des lieux (mig 215) — variété de fiche technique.
      creerEtatDesLieux: (body: { sujet: string; description?: string; localisationAdresse?: string; checklist?: ChecklistItemShared[]; lieuId?: string | null; templateId?: string | null }) =>
        apiFetch<Signalement>('/api/service-technique/signalements/etats-des-lieux', { method: 'POST', body: JSON.stringify(body) }),
      /** M12 — saisonnalité : « intervention possible à partir du » (null = retirer). */
      definirSaison: (id: string, body: { saisonOuverture: string | null; saisonMotif?: string | null }) =>
        apiFetch<{ saisonOuverture: string | null; saisonMotif: string | null }>(
          `/api/service-technique/signalements/${id}/saison`,
          { method: 'PATCH', body: JSON.stringify(body) }),
      updateChecklist: (id: string, checklist: ChecklistItemShared[]) =>
        apiFetch<Signalement>(`/api/service-technique/signalements/${id}/checklist`, { method: 'PATCH', body: JSON.stringify({ checklist }) }),
    },
    // Templates d'état des lieux (mig 216) — un par lieu.
    edlTemplates: {
      list: (inclureInactifs = false) =>
        apiFetch<EdlTemplateShared[]>(`/api/service-technique/edl-templates${inclureInactifs ? '?inclureInactifs=true' : ''}`),
      get: (id: string) => apiFetch<EdlTemplateShared>(`/api/service-technique/edl-templates/${id}`),
      pourLieu: (lieuId: string) => apiFetch<EdlTemplateShared | null>(`/api/service-technique/edl-templates/pour-lieu/${lieuId}`),
      create: (body: { lieuId: string | null; estType?: boolean; nom: string; points: { id: string; libelle: string; piece?: string | null }[]; actif?: boolean }) =>
        apiFetch<EdlTemplateShared>('/api/service-technique/edl-templates', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: { lieuId: string | null; estType?: boolean; nom: string; points: { id: string; libelle: string; piece?: string | null }[]; actif?: boolean }) =>
        apiFetch<EdlTemplateShared>(`/api/service-technique/edl-templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<{ ok: true }>(`/api/service-technique/edl-templates/${id}`, { method: 'DELETE' }),
      // Vague 2 — export du template vide (à remettre / remplir).
      exportExcel: (id: string, nom: string) =>
        downloadCompta(`/api/service-technique/edl-templates/${id}/export.xlsx`, `etat-des-lieux-${nom}.xlsx`),
      exportPdf: (id: string, nom: string) =>
        downloadCompta(`/api/service-technique/edl-templates/${id}/export.pdf`, `etat-des-lieux-${nom}.pdf`),
      /** Chantier 7 — export Word rangé par pièce, et duplication d'un modèle. */
      exportWord: (id: string, nom: string) =>
        downloadCompta(`/api/service-technique/edl-templates/${id}/export.docx`, `etat-des-lieux-${nom}.docx`),
      dupliquer: (id: string, body: { lieuId?: string | null; nom?: string }) =>
        apiFetch<EdlTemplateShared>(`/api/service-technique/edl-templates/${id}/dupliquer`, { method: 'POST', body: JSON.stringify(body) }),
      // Vague 2 — import de points depuis Excel.
      modeleImport: () =>
        downloadCompta('/api/service-technique/edl-templates/import/modele', 'modele-import-etat-des-lieux.xlsx'),
      importPoints: async (file: File): Promise<{ points: { id: string; libelle: string }[]; ignorees: number }> => {
        const fd = new FormData(); fd.set('fichier', file);
        const token = getAccessToken();
        const res = await fetch('/api/service-technique/edl-templates/import/points', {
          method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', body: fd,
        });
        if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Erreur', code: 'IMPORT_FAILED' })));
        return res.json() as Promise<{ points: { id: string; libelle: string }[]; ignorees: number }>;
      },
    },
    // Phase 2A étendue L12 — planning manager
    planning: {
      /** GET /planning?weekStart=YYYY-MM-DD */
      getWeek: (weekStart: string) =>
        apiFetch<{
          weekStart: string;
          weekEnd: string;
          agents: Array<{
            id: string;
            fullName: string;
            email: string;
            role: string;
            couleurMarker: string | null;
          }>;
          creneaux: Array<{
            id: string;
            tenantId: string;
            agentUserId: string;
            agentFullName: string;
            agentCouleurMarker: string | null;
            dateDebut: string;
            dateFin: string;
            titre: string;
            description: string | null;
            signalementId: string | null;
            signalementNumero: string | null;
            profilId?: string | null;
            type: string;
            couleurMarker: string | null;
          }>;
          signalementsAffectables: Array<{
            id: string;
            numero: string;
            sujet: string;
            categorie: string;
            urgence: string;
            statut: string;
            localisationAdresse: string | null;
            localisationQuartier: string | null;
            isAnonymous: boolean;
            createdAt: string;
            nbCreneaux?: number;
          }>;
          suggestionsRegroupement: Array<{
            id: string;
            libelle: string;
            signalementIds: string[];
            raison: string;
          }>;
        }>(`/api/service-technique/planning?weekStart=${encodeURIComponent(weekStart)}`),
      createCreneau: (body: {
        agentUserId: string;
        dateDebut: string;
        dateFin: string;
        titre: string;
        description?: string;
        signalementId?: string;
        type?: string;
        couleurMarker?: string;
      }) =>
        apiFetch<PlanningCreneau>('/api/service-technique/planning/creneaux', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      updateCreneau: (
        id: string,
        body: {
          agentUserId?: string;
          dateDebut?: string;
          dateFin?: string;
          titre?: string;
          description?: string | null;
          signalementId?: string | null;
          type?: string;
          couleurMarker?: string | null;
        }
      ) =>
        apiFetch<{ id: string }>(`/api/service-technique/planning/creneaux/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      deleteCreneau: (id: string) =>
        apiFetch<void>(`/api/service-technique/planning/creneaux/${id}`, {
          method: 'DELETE',
        }),
      setAgentCouleur: (agentUserId: string, couleurMarker: string | null) =>
        apiFetch<void>(`/api/service-technique/planning/agents/${agentUserId}/couleur`, {
          method: 'PATCH',
          body: JSON.stringify({ couleurMarker }),
        }),
      // L13.12 — workflow statut créneau
      updateStatutCreneau: (id: string, body: { statut: string; commentaire?: string }) =>
        apiFetch<unknown>(`/api/service-technique/planning/creneaux/${id}/statut`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      // L13.12 — vue semaine pour l'agent connecté
      maSemaine: (weekStart?: string) => {
        const qs = weekStart ? `?weekStart=${encodeURIComponent(weekStart)}` : '';
        return apiFetch<{ items: unknown[]; weekStart: string }>(
          `/api/service-technique/planning/ma-semaine${qs}`
        );
      },
      // Sous-lot planning unifié — profils + génération + paie + jours non travaillés
      listProfils: () => apiFetch<PlanningProfil[]>('/api/service-technique/planning/profils'),
      createProfil: (body: CreatePlanningProfilInput) =>
        apiFetch<PlanningProfil>('/api/service-technique/planning/profils', { method: 'POST', body: JSON.stringify(body) }),
      updateProfil: (id: string, body: CreatePlanningProfilInput) =>
        apiFetch<{ profil: PlanningProfil; supprimes: number; crees: number }>(`/api/service-technique/planning/profils/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteProfil: (id: string) =>
        apiFetch<void>(`/api/service-technique/planning/profils/${id}`, { method: 'DELETE' }),
      generer: (body: GenererPlanningStInput) =>
        apiFetch<GenererPlanningResult>('/api/service-technique/planning/generer', { method: 'POST', body: JSON.stringify(body) }),
      joursNonTravailles: (from: string, to: string) =>
        apiFetch<JourNonTravaille[]>(`/api/service-technique/planning/jours-non-travailles?from=${from}&to=${to}`),
      transmettrePaie: (from: string, to: string) =>
        apiFetch<{ transmis: number }>('/api/service-technique/planning/transmettre-paie', { method: 'POST', body: JSON.stringify({ from, to }) }),
      /** Export planning ST — Lot C 6.6. date = YYYY-MM-DD local ; portee = 'semaine' | 'mois'. */
      exportExcel: (date: string, portee: 'semaine' | 'mois', agentId?: string) =>
        downloadCompta(
          `/api/service-technique/planning/export/excel?date=${encodeURIComponent(date)}&portee=${portee}${agentId ? `&agentId=${agentId}` : ''}`,
          `planning-st-${portee}.xlsx`),
      exportPdf: (date: string, portee: 'semaine' | 'mois', agentId?: string) =>
        downloadCompta(
          `/api/service-technique/planning/export/pdf?date=${encodeURIComponent(date)}&portee=${portee}${agentId ? `&agentId=${agentId}` : ''}`,
          `planning-st-${portee}.pdf`),
      // Bloc 1A — présence d'un jour + vue année par personne
      presenceJour: (jour: string) =>
        apiFetch<PlanningPresenceJour>(`/api/service-technique/planning/presence?jour=${encodeURIComponent(jour)}`),
      annee: (annee: number, agentId?: string) =>
        apiFetch<PlanningAnneeResponse>(
          `/api/service-technique/planning/annee?annee=${annee}${agentId ? `&agentId=${encodeURIComponent(agentId)}` : ''}`
        ),
      // Bloc 2 — assignation multi-personnes + équipes de travail
      assignMulti: (body: AssignMultiInput) =>
        apiFetch<{ crees: number }>('/api/service-technique/planning/assign-multi', { method: 'POST', body: JSON.stringify(body) }),
      listEquipes: () =>
        apiFetch<PlanningEquipe[]>('/api/service-technique/planning/equipes'),
      createEquipe: (body: CreatePlanningEquipeInput) =>
        apiFetch<PlanningEquipe>('/api/service-technique/planning/equipes', { method: 'POST', body: JSON.stringify(body) }),
      updateEquipe: (id: string, body: CreatePlanningEquipeInput) =>
        apiFetch<PlanningEquipe>(`/api/service-technique/planning/equipes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteEquipe: (id: string) =>
        apiFetch<void>(`/api/service-technique/planning/equipes/${id}`, { method: 'DELETE' }),
      listTemplates: () =>
        apiFetch<PlanningTemplate[]>('/api/service-technique/planning/templates'),
      createTemplate: (body: CreatePlanningTemplateInput) =>
        apiFetch<PlanningTemplate>('/api/service-technique/planning/templates', { method: 'POST', body: JSON.stringify(body) }),
      updateTemplate: (id: string, body: CreatePlanningTemplateInput) =>
        apiFetch<PlanningTemplate>(`/api/service-technique/planning/templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteTemplate: (id: string) =>
        apiFetch<void>(`/api/service-technique/planning/templates/${id}`, { method: 'DELETE' }),
    },
    // Phase 2A étendue L14 — arrêtés municipaux
    arretes: {
      /** 3.7 — relecteurs pré-enregistrés + envoi d'un arrêté en relecture. */
      relecteurs: {
        list: (inclureInactifs = false) =>
          apiFetch<{ items: StRelecteurShared[] }>(
            `/api/service-technique/arretes/relecteurs${inclureInactifs ? '?inclureInactifs=true' : ''}`
          ),
        create: (body: { nom: string; email: string; fonction?: string | null; actif?: boolean }) =>
          apiFetch<StRelecteurShared>('/api/service-technique/arretes/relecteurs', {
            method: 'POST', body: JSON.stringify(body),
          }),
        update: (id: string, body: { nom: string; email: string; fonction?: string | null; actif?: boolean }) =>
          apiFetch<StRelecteurShared>(`/api/service-technique/arretes/relecteurs/${id}`, {
            method: 'PATCH', body: JSON.stringify(body),
          }),
        remove: (id: string) =>
          apiFetch<void>(`/api/service-technique/arretes/relecteurs/${id}`, { method: 'DELETE' }),
      },
      parametresSt: {
        get: () => apiFetch<{ relectureMessageDefaut: string | null; photoObligatoireAdministre?: boolean }>(
          '/api/service-technique/arretes/parametres-st'
        ),
        /** Mise à jour PARTIELLE : seuls les champs envoyés changent. */
        update: (body: { relectureMessageDefaut?: string | null; photoObligatoireAdministre?: boolean }) =>
          apiFetch<{ relectureMessageDefaut: string | null; photoObligatoireAdministre: boolean }>(
            '/api/service-technique/arretes/parametres-st',
            { method: 'PUT', body: JSON.stringify(body) }
          ),
      },
      /** M17 — envoi de l'arrêté SIGNÉ (demandeur + annuaire). */
      envoi: (arreteId: string) =>
        apiFetch<{
          envoyable: boolean; statut: string;
          demandeurs: Array<{ email: string; nom: string | null }>;
          historique: Array<{ email: string; nom: string | null; qualite: string; envoye: boolean; le: string }>;
        }>(`/api/service-technique/arretes/${arreteId}/envoi`),
      envoyer: (arreteId: string, body: {
        inclureDemandeurs: boolean; emailDemandeur?: string | null;
        destinataireIds: string[]; message?: string | null;
      }) =>
        apiFetch<{ envoyes: number; echecs: number; destinataires: number }>(
          `/api/service-technique/arretes/${arreteId}/envoyer`,
          { method: 'POST', body: JSON.stringify(body) }
        ),
      /** M19 — pièces jointes d'un arrêté (PDF, Word, image). */
      pieces: {
        list: (arreteId: string) =>
          apiFetch<{ pieces: { id: string; nom: string; mime: string; taille: number; ajouteParNom: string | null; createdAt: string }[] }>(
            `/api/service-technique/arretes/${arreteId}/pieces`
          ),
        add: (arreteId: string, fichier: File) => {
          const fd = new FormData();
          fd.append('fichier', fichier);
          return apiFetchRaw<{ id: string; nom: string }>(
            `/api/service-technique/arretes/${arreteId}/pieces`,
            { method: 'POST', body: fd }
          );
        },
        lien: (arreteId: string, pieceId: string) =>
          apiFetch<{ url: string }>(`/api/service-technique/arretes/${arreteId}/pieces/${pieceId}/telechargement`),
        remove: (arreteId: string, pieceId: string) =>
          apiFetch<void>(`/api/service-technique/arretes/${arreteId}/pieces/${pieceId}`, { method: 'DELETE' }),
      },
      envoyerRelecture: (id: string, body: { relecteurId: string; message?: string }) =>
        apiFetch<{ envoye: boolean; destinataire: string; destinataireNom: string }>(
          `/api/service-technique/arretes/${id}/relecture`,
          { method: 'POST', body: JSON.stringify(body) }
        ),
      list: (params: { statut?: string; type?: string; limit?: number; offset?: number } = {}) => {
        const q = new URLSearchParams();
        if (params.statut) q.set('statut', params.statut);
        if (params.type) q.set('type', params.type);
        if (params.limit) q.set('limit', String(params.limit));
        if (params.offset) q.set('offset', String(params.offset));
        const qs = q.toString();
        return apiFetch<{
          items: Array<{
            id: string;
            numero: string;
            annee: number;
            type: string;
            titre: string;
            objet: string | null;
            statut: string;
            generePar: string;
            genereParFullName?: string;
            genereAt: string;
            signePar: string | null;
            signeAt: string | null;
            publieAt: string | null;
            hasPdf: boolean;
            createdAt: string;
            updatedAt: string;
          }>;
          total: number;
        }>(`/api/service-technique/arretes${qs ? '?' + qs : ''}`);
      },
      get: (id: string) =>
        apiFetch<{
          id: string;
          numero: string;
          annee: number;
          type: string;
          titre: string;
          objet: string | null;
          /** M14+M15 — voies et motifs multiples. */
          localisations?: Array<{ voie: string; precision?: string | null }>;
          motifs?: string[];
          considerants: string[];
          articles: Array<{ numero: number; texte: string }>;
          effetDu: string | null;
          effetAu: string | null;
          signalementIds: string[];
          signalementsDetail?: Array<{ id: string; numero: string; sujet: string }>;
          maireNom: string | null;
          maireTitre: string;
          statut: string;
          generePar: string;
          genereParFullName?: string;
          genereAt: string;
          reluPar: string | null;
          reluParFullName?: string;
          reluAt: string | null;
          reluCommentaire: string | null;
          signePar: string | null;
          signeParFullName?: string;
          signeAt: string | null;
          publieAt: string | null;
          pastellReference: string | null;
          hasPdf: boolean;
          pdfGeneratedAt: string | null;
          relectureEnvoyeeA?: string | null;
          relectureEnvoyeeNom?: string | null;
          relectureEnvoyeeAt?: string | null;
          createdAt: string;
          updatedAt: string;
        }>(`/api/service-technique/arretes/${id}`),
      pdfUrl: (id: string, download = false) =>
        `/api/service-technique/arretes/${id}/pdf${download ? '?download=1' : ''}`,
      /** R13.3 — Fetch authentifié qui retourne le PDF sous forme de Blob.
       *  Utilisé pour contourner le fait que <iframe src> ne transmet pas le JWT.
       *  L'appelant doit faire URL.createObjectURL(blob) puis URL.revokeObjectURL() au cleanup. */
      pdfBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/service-technique/arretes/${id}/pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        }
        return res.blob();
      },
      create: (body: {
        type: string;
        titre: string;
        objet?: string;
        /** M14+M15 — voies et motifs multiples. */
        localisations?: Array<{ voie: string; precision?: string | null }>;
        motifs?: string[];
        considerants?: string[];
        articles?: Array<{ numero: number; texte: string }>;
        /** Canevas du modèle : document complet, rendu tel quel. */
        corps?: string;
        effetDu?: string;
        effetAu?: string;
        signalementIds?: string[];
        maireNom?: string; modeleId?: string;
      }) =>
        apiFetch<{ id: string; numero: string }>('/api/service-technique/arretes', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<{ id: string }>(`/api/service-technique/arretes/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      transition: (id: string, body: { statut: string; commentaire?: string }) =>
        apiFetch<{ id: string; statut: string }>(`/api/service-technique/arretes/${id}/transition`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      delete: (id: string) =>
        apiFetch<void>(`/api/service-technique/arretes/${id}`, { method: 'DELETE' }),
      draftFromSignalements: (signalementIds: string[]) =>
        apiFetch<{
          titre: string;
          objet: string;
          /** M14+M15 — voies et motifs préremplis depuis les signalements. */
          localisations?: Array<{ voie: string; precision?: string | null }>;
          motifs?: string[];
          considerants: string[];
          articles: Array<{ numero: number; texte: string }>;
        }>('/api/service-technique/arretes/draft-from-signalements', {
          method: 'POST',
          body: JSON.stringify({ signalementIds }),
        }),
    },
  },
  // Phase 2A étendue L15a — GRC dashboard + inbox unifiée
  grc: {
    // Points d'intérêt / Tourisme & Entraide (migration 175)
    poi: {
      meta: () =>
        apiFetch<{ familles: string[]; typesParFamille: Record<string, string[]> }>('/api/grc/poi/meta'),
      list: (famille?: string) =>
        apiFetch<{ points: PoiPoint[] }>(`/api/grc/poi${famille ? `?famille=${famille}` : ''}`),
      publics: (famille?: string) =>
        apiFetch<{ points: PoiPoint[] }>(`/api/grc/poi/publics${famille ? `?famille=${famille}` : ''}`),
      create: (body: PoiInput) =>
        apiFetch<PoiPoint>('/api/grc/poi', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Partial<PoiInput>) =>
        apiFetch<PoiPoint>(`/api/grc/poi/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      photo: (id: string, fichier: File) => {
        const fd = new FormData();
        fd.append('fichier', fichier);
        return apiFetchRaw<{ photoUrl: string | null }>(`/api/grc/poi/${id}/photo`, { method: 'POST', body: fd });
      },
      remove: (id: string) =>
        apiFetch<{ ok: boolean }>(`/api/grc/poi/${id}`, { method: 'DELETE' }),
      importCatalogue: () =>
        apiFetch<{ catalogue: Array<{ famille: string; sousCategorie: string; label: string; type: string }>; rayonKm: number }>('/api/grc/poi/import/geotag/catalogue'),
      importGeotag: (selection?: string[], rayonKm?: number) =>
        apiFetch<{ crees: number; maj: number; ignores: number; total: number }>('/api/grc/poi/import/geotag', {
          method: 'POST',
          body: JSON.stringify({ selection: selection ?? null, rayonKm: rayonKm ?? null }),
        }),
      purgeGeotag: () =>
        apiFetch<{ supprimes: number }>('/api/grc/poi/import/geotag', { method: 'DELETE' }),
      affichetteUrl: () =>
        apiFetch<{ pageUrl: string; commune: string }>('/api/grc/poi/affichette/url'),
      affichettePdfBlob: async (): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch('/api/grc/poi/affichette/pdf', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include',
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'DOWNLOAD_FAILED' });
        return res.blob();
      },
    },
    cahier: {
      list: (statut?: 'a_faire' | 'fait') =>
        apiFetch<{ notes: CahierNote[] }>(`/api/grc/cahier${statut ? `?statut=${statut}` : ''}`),
      create: (body: {
        contenu: string;
        categorie?: 'rappel' | 'info' | 'a_transmettre' | 'urgent';
        assigneService?: string | null;
        assigneUserId?: string | null;
      }) => apiFetch<CahierNote>('/api/grc/cahier', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<CahierNote>(`/api/grc/cahier/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<void>(`/api/grc/cahier/${id}`, { method: 'DELETE' }),
    },
    // Pôle Info (migration 116) — back-office GRC : horaires services + démarches.
    poleInfo: {
      listServices: () => apiFetch<{ services: ServiceHoraire[] }>('/api/grc/infos/services'),
      createService: (body: Partial<ServiceHoraire>) =>
        apiFetch<ServiceHoraire>('/api/grc/infos/services', { method: 'POST', body: JSON.stringify(body) }),
      updateService: (id: string, body: Partial<ServiceHoraire>) =>
        apiFetch<ServiceHoraire>(`/api/grc/infos/services/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      deleteService: (id: string) =>
        apiFetch<void>(`/api/grc/infos/services/${id}`, { method: 'DELETE' }),
      reordreServices: (ids: string[]) =>
        apiFetch<{ ok: true }>('/api/grc/infos/services/ordre', { method: 'PUT', body: JSON.stringify({ ids }) }),
      listDemarches: () => apiFetch<{ demarches: Demarche[] }>('/api/grc/infos/demarches'),
      createDemarche: (body: { titre: string; texte?: string | null; pieces?: string[]; liens?: LienDemarcheManuel[]; affiche?: boolean }) =>
        apiFetch<Demarche>('/api/grc/infos/demarches', { method: 'POST', body: JSON.stringify(body) }),
      updateDemarche: (id: string, body: { titre: string; texte?: string | null; pieces?: string[]; liens?: LienDemarcheManuel[]; affiche?: boolean }) =>
        apiFetch<Demarche>(`/api/grc/infos/demarches/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      deleteDemarche: (id: string) =>
        apiFetch<void>(`/api/grc/infos/demarches/${id}`, { method: 'DELETE' }),
      reordreDemarches: (ids: string[]) =>
        apiFetch<{ ok: true }>('/api/grc/infos/demarches/ordre', { method: 'PUT', body: JSON.stringify({ ids }) }),
      uploadDemarchePdf: (id: string, file: File) => {
        const fd = new FormData();
        fd.append('fichier', file);
        return apiFetchRaw<Demarche>(`/api/grc/infos/demarches/${id}/pdf`, { method: 'POST', body: fd });
      },
      removeDemarchePdf: (id: string) =>
        apiFetch<Demarche>(`/api/grc/infos/demarches/${id}/pdf`, { method: 'DELETE' }),
    },
    // Référendums (migration 117) — back-office GRC.
    referendums: {
      list: () => apiFetch<{ referendums: Referendum[] }>('/api/grc/referendums'),
      create: (body: { question: string; description?: string | null; ouvertureAt: string; clotureAt: string; options: string[] }) =>
        apiFetch<Referendum>('/api/grc/referendums', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: { question: string; description?: string | null; ouvertureAt: string; clotureAt: string; options: string[] }) =>
        apiFetch<Referendum>(`/api/grc/referendums/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      publier: (id: string) =>
        apiFetch<Referendum>(`/api/grc/referendums/${id}/publier`, { method: 'POST' }),
      cloturer: (id: string) =>
        apiFetch<Referendum>(`/api/grc/referendums/${id}/cloturer`, { method: 'POST' }),
      remove: (id: string) =>
        apiFetch<void>(`/api/grc/referendums/${id}`, { method: 'DELETE' }),
    },
    // Déchets (migration 125) — back-office GRC : secteurs + jours de collecte.
    dechets: {
      etat: () => apiFetch<DechetsEtatMairie>('/api/grc/dechets/etat'),
      listSecteurs: () => apiFetch<{ secteurs: DechetsSecteur[] }>('/api/grc/dechets/secteurs'),
      createSecteur: (body: { nom: string; description?: string | null; affiche?: boolean }) =>
        apiFetch<DechetsSecteur>('/api/grc/dechets/secteurs', { method: 'POST', body: JSON.stringify(body) }),
      updateSecteur: (id: string, body: { nom: string; description?: string | null; affiche?: boolean }) =>
        apiFetch<DechetsSecteur>(`/api/grc/dechets/secteurs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removeSecteur: (id: string) =>
        apiFetch<void>(`/api/grc/dechets/secteurs/${id}`, { method: 'DELETE' }),
      listCollectes: () => apiFetch<{ collectes: DechetsCollecte[] }>('/api/grc/dechets/collectes'),
      createCollecte: (body: { secteurId: string; flux: DechetFlux; jour: number; frequence?: DechetFrequence; consigne?: string | null }) =>
        apiFetch<DechetsCollecte>('/api/grc/dechets/collectes', { method: 'POST', body: JSON.stringify(body) }),
      updateCollecte: (id: string, body: { secteurId: string; flux: DechetFlux; jour: number; frequence?: DechetFrequence; consigne?: string | null }) =>
        apiFetch<DechetsCollecte>(`/api/grc/dechets/collectes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removeCollecte: (id: string) =>
        apiFetch<void>(`/api/grc/dechets/collectes/${id}`, { method: 'DELETE' }),
    },
    // Objets trouvés (migration 126) — back-office GRC.
    objets: {
      etat: () => apiFetch<ObjetsEtatMairie>('/api/grc/objets/etat'),
      list: (statut?: ObjetStatut) =>
        apiFetch<{ objets: ObjetTrouve[] }>(`/api/grc/objets${statut ? `?statut=${statut}` : ''}`),
      create: (body: { libelle: string; description?: string | null; lieu?: string | null; dateTrouve: string }) =>
        apiFetch<ObjetTrouve>('/api/grc/objets', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: { libelle: string; description?: string | null; lieu?: string | null; dateTrouve: string }) =>
        apiFetch<ObjetTrouve>(`/api/grc/objets/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      changerStatut: (id: string, body: { statut: ObjetStatut; renduA?: string | null }) =>
        apiFetch<ObjetTrouve>(`/api/grc/objets/${id}/statut`, { method: 'PUT', body: JSON.stringify(body) }),
      uploadPhoto: (id: string, file: File) => {
        const fd = new FormData();
        fd.append('fichier', file);
        return apiFetchRaw<ObjetTrouve>(`/api/grc/objets/${id}/photo`, { method: 'POST', body: fd });
      },
      remove: (id: string) =>
        apiFetch<void>(`/api/grc/objets/${id}`, { method: 'DELETE' }),
    },
    // RDV (migration 127) — back-office GRC : motifs + disponibilités + réservations.
    rdv: {
      etat: () => apiFetch<RdvEtatMairie>('/api/grc/rdv/etat'),
      listMotifs: () => apiFetch<{ motifs: RdvMotif[] }>('/api/grc/rdv/motifs'),
      createMotif: (body: { nom: string; description?: string | null; lieu?: string | null; dureeMin: number; actif?: boolean }) =>
        apiFetch<RdvMotif>('/api/grc/rdv/motifs', { method: 'POST', body: JSON.stringify(body) }),
      updateMotif: (id: string, body: { nom: string; description?: string | null; lieu?: string | null; dureeMin: number; actif?: boolean }) =>
        apiFetch<RdvMotif>(`/api/grc/rdv/motifs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removeMotif: (id: string) =>
        apiFetch<void>(`/api/grc/rdv/motifs/${id}`, { method: 'DELETE' }),
      listDisponibilites: (motifId?: string) =>
        apiFetch<{ disponibilites: RdvDisponibilite[] }>(`/api/grc/rdv/disponibilites${motifId ? `?motifId=${motifId}` : ''}`),
      createDisponibilite: (body: { motifId: string; jour: number; heureDebut: string; heureFin: string }) =>
        apiFetch<RdvDisponibilite>('/api/grc/rdv/disponibilites', { method: 'POST', body: JSON.stringify(body) }),
      removeDisponibilite: (id: string) =>
        apiFetch<void>(`/api/grc/rdv/disponibilites/${id}`, { method: 'DELETE' }),
      listReservations: (params?: { statut?: RdvStatut; aVenir?: boolean }) => {
        const qs = new URLSearchParams();
        if (params?.statut) qs.set('statut', params.statut);
        if (params?.aVenir) qs.set('aVenir', 'true');
        const q = qs.toString();
        return apiFetch<{ reservations: RdvReservation[] }>(`/api/grc/rdv/reservations${q ? `?${q}` : ''}`);
      },
      changerStatut: (id: string, statut: RdvStatut) =>
        apiFetch<RdvReservation>(`/api/grc/rdv/reservations/${id}/statut`, { method: 'PUT', body: JSON.stringify({ statut }) }),
    },
    // Points de collecte & déchetterie (migration 136) — back-office GRC.
    collecte: {
      listPoints: () => apiFetch<{ points: CollectePoint[] }>('/api/grc/collecte/points'),
      createPoint: (body: Partial<CollectePoint>) =>
        apiFetch<CollectePoint>('/api/grc/collecte/points', { method: 'POST', body: JSON.stringify(body) }),
      updatePoint: (id: string, body: Partial<CollectePoint>) =>
        apiFetch<CollectePoint>(`/api/grc/collecte/points/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removePoint: (id: string) =>
        apiFetch<void>(`/api/grc/collecte/points/${id}`, { method: 'DELETE' }),
      reorderPoints: (ids: string[]) =>
        apiFetch<void>('/api/grc/collecte/points/ordre', { method: 'PUT', body: JSON.stringify({ ids }) }),
      getDechetterie: () => apiFetch<CollecteDechetterie | null>('/api/grc/collecte/dechetterie'),
      saveDechetterie: (body: Partial<CollecteDechetterie>) =>
        apiFetch<CollecteDechetterie>('/api/grc/collecte/dechetterie', { method: 'PUT', body: JSON.stringify(body) }),
      removeDechetterie: () =>
        apiFetch<void>('/api/grc/collecte/dechetterie', { method: 'DELETE' }),
      addPieceAttendue: (body: { libelle: string; obligatoire?: boolean }) =>
        apiFetch<CollectePieceAttendue>('/api/grc/collecte/pieces-attendues', { method: 'POST', body: JSON.stringify(body) }),
      removePieceAttendue: (id: string) =>
        apiFetch<void>(`/api/grc/collecte/pieces-attendues/${id}`, { method: 'DELETE' }),
      uploadFormulaire: (file: File | Blob, filename?: string) => {
        const fd = new FormData();
        fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'formulaire.pdf'));
        return apiFetchRaw<void>('/api/grc/collecte/formulaire', { method: 'POST', body: fd });
      },
      getFormulaireUrl: () => apiFetch<{ url: string }>('/api/grc/collecte/formulaire'),
      listDossiers: (statut?: CollecteDossierStatut) =>
        apiFetch<{ dossiers: CollecteDossier[] }>(`/api/grc/collecte/dossiers${statut ? `?statut=${statut}` : ''}`),
      changerStatutDossier: (id: string, body: { statut: CollecteDossierStatut; numeroCarte?: string | null; reponseStaff?: string | null; modeDelivrance?: 'dematerialisee' | 'physique' | 'les_deux' | null }) =>
        apiFetch<CollecteDossier>(`/api/grc/collecte/dossiers/${id}/statut`, { method: 'PUT', body: JSON.stringify(body) }),
      removeDossier: (id: string) =>
        apiFetch<void>(`/api/grc/collecte/dossiers/${id}`, { method: 'DELETE' }),
    },
    // Cimetière (migration 128) — back-office GRC.
    cimetiere: {
      etat: () => apiFetch<CimetiereEtatMairie>('/api/grc/cimetiere/etat'),
      listCimetieres: () => apiFetch<{ cimetieres: Cimetiere[] }>('/api/grc/cimetiere/cimetieres'),
      createCimetiere: (body: { nom: string; adresse?: string | null; actif?: boolean }) =>
        apiFetch<Cimetiere>('/api/grc/cimetiere/cimetieres', { method: 'POST', body: JSON.stringify(body) }),
      updateCimetiere: (id: string, body: { nom: string; adresse?: string | null; actif?: boolean }) =>
        apiFetch<Cimetiere>(`/api/grc/cimetiere/cimetieres/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removeCimetiere: (id: string) =>
        apiFetch<void>(`/api/grc/cimetiere/cimetieres/${id}`, { method: 'DELETE' }),
      listConcessions: (params?: { cimetiereId?: string; q?: string }) => {
        const qs = new URLSearchParams();
        if (params?.cimetiereId) qs.set('cimetiereId', params.cimetiereId);
        if (params?.q) qs.set('q', params.q);
        const q = qs.toString();
        return apiFetch<{ concessions: Concession[] }>(`/api/grc/cimetiere/concessions${q ? `?${q}` : ''}`);
      },
      getConcession: (id: string) => apiFetch<Concession>(`/api/grc/cimetiere/concessions/${id}`),
      echeances: (mois = 12) =>
        apiFetch<{ concessions: Concession[] }>(`/api/grc/cimetiere/concessions/echeance?mois=${mois}`),
      createConcession: (body: Record<string, unknown>) =>
        apiFetch<Concession>('/api/grc/cimetiere/concessions', { method: 'POST', body: JSON.stringify(body) }),
      updateConcession: (id: string, body: Record<string, unknown>) =>
        apiFetch<Concession>(`/api/grc/cimetiere/concessions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removeConcession: (id: string) =>
        apiFetch<void>(`/api/grc/cimetiere/concessions/${id}`, { method: 'DELETE' }),
      addDefunt: (concessionId: string, body: Record<string, unknown>) =>
        apiFetch<ConcessionDefunt>(`/api/grc/cimetiere/concessions/${concessionId}/defunts`, { method: 'POST', body: JSON.stringify(body) }),
      removeDefunt: (id: string) =>
        apiFetch<void>(`/api/grc/cimetiere/defunts/${id}`, { method: 'DELETE' }),
      titrePdfBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/grc/cimetiere/concessions/${id}/titre.pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        return res.blob();
      },
      exportXlsxBlob: async (): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch('/api/grc/cimetiere/concessions/export.xlsx', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'XLSX_FETCH_FAILED' });
        return res.blob();
      },
      listDemandes: (statut?: string) =>
        apiFetch<{ demandes: ConcessionDemande[] }>(`/api/grc/cimetiere/demandes${statut ? `?statut=${statut}` : ''}`),
      traiterDemande: (id: string, statut: 'en_cours' | 'traitee' | 'refusee', reponse?: string) =>
        apiFetch<ConcessionDemande>(`/api/grc/cimetiere/demandes/${id}/traiter`, { method: 'PUT', body: JSON.stringify({ statut, reponse }) }),
    },
    // Agenda municipal (migration 129) — back-office GRC.
    agenda: {
      list: (aVenir = false) =>
        apiFetch<{ evenements: AgendaEvenement[] }>(`/api/grc/agenda${aVenir ? '?aVenir=true' : ''}`),
      create: (body: Record<string, unknown>) =>
        apiFetch<AgendaEvenement>('/api/grc/agenda', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<AgendaEvenement>(`/api/grc/agenda/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<void>(`/api/grc/agenda/${id}`, { method: 'DELETE' }),
      /** Diffuser (mig 293) : cloche/e-mail vers administrés, salariés, annuaire. */
      diffuser: (id: string, body: {
        cibles: { administres?: boolean; salaries?: boolean; annuaireCategories?: string[] };
        canaux: { cloche?: boolean; email?: boolean };
      }) =>
        apiFetch<{ cloches: number; emails: number; resume: string }>(
          `/api/grc/agenda/${id}/diffuser`, { method: 'POST', body: JSON.stringify(body) }),
      /** Export calendrier — Lot C 9.4. aVenir = n'exporter que les événements futurs. */
      exportExcel: (aVenir = false) =>
        downloadCompta(`/api/grc/agenda/export/excel${aVenir ? '?aVenir=true' : ''}`, 'calendrier-evenements.xlsx'),
      exportPdf: (aVenir = false) =>
        downloadCompta(`/api/grc/agenda/export/pdf${aVenir ? '?aVenir=true' : ''}`, 'calendrier-evenements.pdf'),
    },
    // Boîte à idées (migration 130) — back-office GRC.
    idees: {
      etat: () => apiFetch<IdeesEtatMairie>('/api/grc/idees/etat'),
      list: (opts?: { statut?: string; masquees?: boolean }) => {
        const qs = new URLSearchParams();
        if (opts?.statut) qs.set('statut', opts.statut);
        if (opts?.masquees) qs.set('masquees', 'true');
        const q = qs.toString();
        return apiFetch<{ idees: Idee[] }>(`/api/grc/idees${q ? `?${q}` : ''}`);
      },
      traiter: (id: string, statut: IdeeStatut, reponse?: string) =>
        apiFetch<Idee>(`/api/grc/idees/${id}/traiter`, { method: 'PUT', body: JSON.stringify({ statut, reponse }) }),
      masquer: (id: string, masquee = true) =>
        apiFetch<void>(`/api/grc/idees/${id}/masquer`, { method: 'PUT', body: JSON.stringify({ masquee }) }),
    },
    // Pointage des factures (régie / comptabilité GRC).
    factures: {
      list: (params?: { service?: ServiceFacturable; etat?: 'attente' | 'toutes' | 'payee' | 'annulee' }) => {
        const qs = new URLSearchParams();
        if (params?.service) qs.set('service', params.service);
        if (params?.etat) qs.set('etat', params.etat);
        const q = qs.toString();
        return apiFetch<{ factures: Facture[]; resume: { nbEnAttente: number; montantEnAttente: number } }>(
          `/api/grc/factures${q ? `?${q}` : ''}`
        );
      },
      pointer: (id: string, body: { moyen: MoyenEncaissement; reference: string; montant?: number }) =>
        apiFetch<Facture>(`/api/grc/factures/${id}/pointer`, { method: 'POST', body: JSON.stringify(body) }),
      get: (id: string) => apiFetch<Facture>(`/api/grc/factures/${id}`),
      modifierEncaissement: (factureId: string, encId: string, body: { moyen: MoyenEncaissement; reference?: string | null }) =>
        apiFetch<Facture>(`/api/grc/factures/${factureId}/encaissements/${encId}`, { method: 'PATCH', body: JSON.stringify(body) }),
      exportExcelBlob: async (params?: { debut?: string; fin?: string }): Promise<Blob> => {
        const qs = new URLSearchParams();
        if (params?.debut) qs.set('debut', params.debut);
        if (params?.fin) qs.set('fin', params.fin);
        const q = qs.toString();
        const token = getAccessToken();
        const res = await fetch(`/api/grc/factures/export.xlsx${q ? `?${q}` : ''}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'EXPORT_FAILED' });
        }
        return res.blob();
      },
    },
    dashboard: () =>
      apiFetch<{
        kpis: {
          demandesEnCours: number;
          delaiMoyenReponseJours: number;
          slaReponseJours: number;
          abonnesNewsletter: number;
          abonnesNewsletter7jVariation: number;
          newsletterTauxOuverturePct: number | null;
          pushPwaOptIn: number;
          pushPwaOptInPct: number;
        };
        topSujets: Array<{ categorie: string; label: string; count: number }>;
        inboxPreview: InboxItem[];
      }>('/api/grc/dashboard'),
    inbox: (filters: {
      source?: 'all' | 'grc_message' | 'signalement';
      canal?: string;
      service?: string;
      statut?: string;
      assigneUserId?: string;
      search?: string;
      limit?: number;
      offset?: number;
    } = {}) => {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
      const qs = q.toString();
      return apiFetch<{ items: InboxItem[]; total: number }>(
        `/api/grc/inbox${qs ? '?' + qs : ''}`
      );
    },
    messages: {
      /** Export messages GRC — Lot C 8.6. Filtres statut/service/canal/from/to. */
      exportExcel: (filters: { statut?: string; service?: string; canal?: string; from?: string; to?: string } = {}) => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
        const qs = q.toString();
        return downloadCompta(`/api/grc/messages/export/excel${qs ? '?' + qs : ''}`, 'messages-administres.xlsx');
      },
      exportPdf: (filters: { statut?: string; service?: string; canal?: string; from?: string; to?: string } = {}) => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
        const qs = q.toString();
        return downloadCompta(`/api/grc/messages/export/pdf${qs ? '?' + qs : ''}`, 'messages-administres.pdf');
      },
      create: (body: {
        canal: string;
        sujet: string;
        contenu?: string;
        citoyenUserId?: string;
        citoyenNom?: string;
        citoyenEmail?: string;
        citoyenTelephone?: string;
        service?: string;
        categorieSujet?: string;
        alerteId?: string;
        receivedAt?: string;
      }) =>
        apiFetch<{ id: string }>('/api/grc/messages', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      get: (id: string) =>
        apiFetch<GrcMessageDetail>(`/api/grc/messages/${id}`),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<GrcMessageDetail>(`/api/grc/messages/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      delete: (id: string) =>
        apiFetch<void>(`/api/grc/messages/${id}`, { method: 'DELETE' }),
    },
    setNewsletterOptIn: (optIn: boolean) =>
      apiFetch<void>('/api/grc/me/newsletter', {
        method: 'PATCH',
        body: JSON.stringify({ optIn }),
      }),
    // Phase 2A étendue L15b — alertes matricées
    alertes: {
      list: (filters: { statut?: string; niveau?: string; limit?: number; offset?: number } = {}) => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
        const qs = q.toString();
        return apiFetch<{
          items: GrcAlerteShared[];
          total: number;
          activeCount: number;
        }>(`/api/grc/alertes${qs ? '?' + qs : ''}`);
      },
      active: () =>
        apiFetch<{ alerte: GrcAlerteShared | null }>('/api/grc/alertes/active'),
      /** Liste légère des alertes rattachables (alerte+crise) — mini-lot alerte T1. */
      rattachables: () =>
        apiFetch<{ alertes: { id: string; titre: string; niveau: string; date: string | null }[] }>(
          '/api/grc/alertes/rattachables'),
      get: (id: string) =>
        apiFetch<GrcAlerteDetailShared>(`/api/grc/alertes/${id}`),
      /** Export journal de crise (item 8.12) — PDF + Excel horodatés. */
      journalPdf: (id: string) =>
        downloadCompta(`/api/grc/alertes/${id}/journal/pdf`, 'journal-crise.pdf'),
      journalExcel: (id: string) =>
        downloadCompta(`/api/grc/alertes/${id}/journal/excel`, 'journal-crise.xlsx'),
      getPublic: (id: string) =>
        apiFetch<GrcAlerteShared>(`/api/grc/alertes/${id}/public`),
      create: (body: {
        niveau: string;
        categorie: string;
        titre: string;
        message: string;
        messageLong?: string;
        zoneGeographique?: string;
        secteurIds?: string[];
        dateDebut?: string;
        dateFin?: string;
        canaux?: { site: boolean; mail: boolean; pushPwa: boolean; sms: boolean; panneau: boolean };
        incidentId?: string;
      }) =>
        apiFetch<GrcAlerteShared>('/api/grc/alertes', {
          method: 'POST',
          body: JSON.stringify(body),
        }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<GrcAlerteShared>(`/api/grc/alertes/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      trigger: (id: string) =>
        apiFetch<GrcAlerteShared>(`/api/grc/alertes/${id}/trigger`, { method: 'POST' }),
      resolve: (id: string) =>
        apiFetch<GrcAlerteShared>(`/api/grc/alertes/${id}/resolve`, { method: 'POST' }),
      cancel: (id: string) =>
        apiFetch<GrcAlerteShared>(`/api/grc/alertes/${id}/cancel`, { method: 'POST' }),
      delete: (id: string) =>
        apiFetch<void>(`/api/grc/alertes/${id}`, { method: 'DELETE' }),
      vapidPublicKey: () =>
        apiFetch<{ publicKey: string }>('/api/grc/alertes/vapid-public-key'),
      subscribePush: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
        apiFetch<void>('/api/grc/alertes/me/push/subscribe', {
          method: 'POST',
          body: JSON.stringify(subscription),
        }),
      unsubscribePush: () =>
        apiFetch<void>('/api/grc/alertes/me/push/subscribe', { method: 'DELETE' }),

      /** Pièce jointe de l'alerte (item 8.9) — site + mail uniquement. */
      pieceJointe: {
        upload: (id: string, fichier: File) => {
          const fd = new FormData();
          fd.append('fichier', fichier);
          return apiFetchRaw<GrcAlerteShared>(`/api/grc/alertes/${id}/piece-jointe`, {
            method: 'POST', body: fd,
          });
        },
        supprimer: (id: string) =>
          apiFetch<GrcAlerteShared>(`/api/grc/alertes/${id}/piece-jointe`, { method: 'DELETE' }),
        url: (id: string) =>
          apiFetch<{ url: string }>(`/api/grc/alertes/${id}/piece-jointe`),
      },

      /** Incidents (mig 234) — regroupent les alertes d'un même événement. */
      incidents: {
        list: (statut?: 'ouvert' | 'cloture') =>
          apiFetch<{ incidents: GrcIncidentShared[] }>(
            `/api/grc/alertes/incidents${statut ? `?statut=${statut}` : ''}`),
        get: (id: string) =>
          apiFetch<GrcIncidentShared>(`/api/grc/alertes/incidents/${id}`),
        renommer: (id: string, libelle: string) =>
          apiFetch<GrcIncidentShared>(`/api/grc/alertes/incidents/${id}`, {
            method: 'PATCH', body: JSON.stringify({ libelle }),
          }),
        cloturer: (id: string) =>
          apiFetch<GrcIncidentShared>(`/api/grc/alertes/incidents/${id}/cloturer`, {
            method: 'POST',
          }),
      },

      /** Modèles d'alerte (item 8.8) — pré-remplissent le formulaire. */
      presets: {
        list: (tous = false) =>
          apiFetch<{ presets: GrcAlertePresetShared[] }>(
            `/api/grc/alertes/presets${tous ? '?tous=1' : ''}`),
        creer: (body: {
          libelle: string;
          niveau: string;
          categorie: string;
          titre?: string;
          message?: string;
          messageLong?: string;
          zoneGeographique?: string;
          secteurIds?: string[];
          canaux?: { site: boolean; mail: boolean; pushPwa: boolean; sms: boolean; panneau: boolean };
          ordre?: number;
        }) =>
          apiFetch<GrcAlertePresetShared>('/api/grc/alertes/presets', {
            method: 'POST', body: JSON.stringify(body),
          }),
        maj: (id: string, body: Record<string, unknown>) =>
          apiFetch<GrcAlertePresetShared>(`/api/grc/alertes/presets/${id}`, {
            method: 'PATCH', body: JSON.stringify(body),
          }),
      },
    },
    // Module SMS d'alerte (mig. 141) — canal payant
    sms: {
      getConfig: () =>
        apiFetch<{
          actif: boolean;
          modeCompte: 'commune' | 'passerelle';
          provider: string;
          expediteur: string;
          prixUnitaireEur: number;
          surcoutPasserelleEur: number;
          prixEffectifEur: number;
          soldeSms: number;
          totalConsomme: number;
          seuilAlerteSms: number;
          packRechargeSms: number;
          facturationSmsJour: number;
          facturationSmsAuto: boolean;
          creditDepartSms: number;
          cleConfiguree: boolean;
          passerelleVpcDisponible: boolean;
        }>('/api/grc/sms/config'),
      updateConfig: (body: {
        actif?: boolean;
        modeCompte?: 'commune' | 'passerelle';
        expediteur?: string;
        prixUnitaireEur?: number;
        surcoutPasserelleEur?: number;
        apiKey?: string | null;
      }) =>
        apiFetch<Record<string, unknown>>('/api/grc/sms/config', {
          method: 'PUT', body: JSON.stringify(body),
        }),
      crediter: (nbSms: number) =>
        apiFetch<Record<string, unknown>>('/api/grc/sms/crediter', {
          method: 'POST', body: JSON.stringify({ nbSms }),
        }),
      estimation: (message: string) =>
        apiFetch<{
          nbAbonnes: number;
          nbSegments: number;
          nbSmsTotal: number;
          prixUnitaireEur: number;
          surcoutPasserelleEur: number;
          prixEffectifEur: number;
          coutEstimeEur: number;
          modeCompte: 'commune' | 'passerelle';
          soldeSms: number;
          soldeSuffisant: boolean;
          canalPret: boolean;
        }>('/api/grc/sms/estimation', {
          method: 'POST', body: JSON.stringify({ message }),
        }),
      abonnesCount: () =>
        apiFetch<{ nbAbonnes: number }>('/api/grc/sms/abonnes/count'),
      listAbonnes: () =>
        apiFetch<{ abonnes: Array<{ id: string; telephone: string; nomAffiche: string | null; statut: string; consentAt: string; desabonneAt: string | null }> }>('/api/grc/sms/abonnes'),
      desabonnerParId: (id: string) =>
        apiFetch<{ desabonne: boolean }>(`/api/grc/sms/abonnes/${id}`, { method: 'DELETE' }),
      monAbonnement: () =>
        apiFetch<{ abonne: boolean; telephone: string | null; secteurIds: string[] }>('/api/grc/sms/mon-abonnement'),
      abonner: (body: { telephone: string; nomAffiche?: string; secteurIds?: string[] }) =>
        apiFetch<{ id: string; telephone: string; statut: string }>('/api/grc/sms/abonnement', {
          method: 'POST', body: JSON.stringify(body),
        }),
      desabonner: (telephone: string) =>
        apiFetch<{ desabonne: boolean }>('/api/grc/sms/abonnement', {
          method: 'DELETE', body: JSON.stringify({ telephone }),
        }),
      // Secteurs d'alerte (segmentation PCS)
      secteurs: {
        list: (tous = false) =>
          apiFetch<{ secteurs: Array<{ id: string; libelle: string; ordre: number; actif: boolean; nbAbonnes: number }> }>(`/api/grc/sms/secteurs${tous ? '?tous=1' : ''}`),
        creer: (body: { libelle: string; ordre?: number }) =>
          apiFetch<{ id: string; libelle: string; ordre: number; actif: boolean; nbAbonnes: number }>('/api/grc/sms/secteurs', { method: 'POST', body: JSON.stringify(body) }),
        maj: (id: string, body: { libelle?: string; ordre?: number; actif?: boolean }) =>
          apiFetch<{ ok: boolean }>(`/api/grc/sms/secteurs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
        cartes: {
          list: () =>
            apiFetch<{ cartes: Array<{ id: string; libelle: string; typeFichier: 'pdf' | 'image'; mime: string; tailleBytes: number; url: string; createdAt: string }> }>('/api/grc/sms/secteurs/cartes'),
          importer: (fichier: File, libelle: string) => {
            const fd = new FormData();
            fd.append('fichier', fichier);
            fd.append('libelle', libelle);
            return apiFetchRaw<{ id: string }>('/api/grc/sms/secteurs/cartes', { method: 'POST', body: fd });
          },
          supprimer: (id: string) =>
            apiFetch<{ ok: boolean }>(`/api/grc/sms/secteurs/cartes/${id}`, { method: 'DELETE' }),
        },
      },
      // Facturation SMS (mode passerelle)
      facturation: {
        journal: (from: string, to: string) =>
          apiFetch<{ journal: Array<{ id: string; telephone: string; nbSegments: number; coutEur: number; statut: string; statutRemise: string; createdAt: string }> }>(`/api/grc/sms/facturation/journal?from=${from}&to=${to}`),
        statsRemise: (from: string, to: string) =>
          apiFetch<{ total: number; remis: number; echec: number; enCours: number; inconnu: number; tauxRemisePct: number | null }>(`/api/grc/sms/facturation/stats-remise?from=${from}&to=${to}`),
        factures: () =>
          apiFetch<{ factures: Array<{ id: string; type: string; periode: string | null; nbSms: number; montantTtc: number; factureId: string | null; createdAt: string }> }>('/api/grc/sms/facturation/factures'),
        conso: (mois: string) =>
          apiFetch<{ nbSms: number; coutEur: number }>(`/api/grc/sms/facturation/conso?mois=${mois}`),
        recharge: () =>
          apiFetch<Record<string, unknown>>('/api/grc/sms/facturation/recharge', { method: 'POST' }),
        emettreMensuelle: (mois?: string) =>
          apiFetch<Record<string, unknown>>('/api/grc/sms/facturation/mensuelle', { method: 'POST', body: JSON.stringify(mois ? { mois } : {}) }),
        setConfig: (body: { seuilAlerteSms?: number; facturationSmsJour?: number; facturationSmsAuto?: boolean; packRechargeSms?: number }) =>
          apiFetch<{ ok: boolean }>('/api/grc/sms/facturation/config', { method: 'PUT', body: JSON.stringify(body) }),
        crediter: (nbSms: number) =>
          apiFetch<{ soldeSms: number }>('/api/grc/sms/crediter', { method: 'POST', body: JSON.stringify({ nbSms }) }),
        // Supervision plateforme (super_admin) : solde smsmode réel
        platformConfig: () =>
          apiFetch<{ config: { modeSeuil: 'auto' | 'fixe'; reserveSmsParCommune: number; coutUnitaireEur: number; seuilFixeEur: number; emailAlerte: string; dernierSoldeEur: number | null; dernierSoldeAt: string | null }; seuilEur: number; seuilDetail: string }>('/api/grc/sms/platform/config'),
        setPlatformConfig: (body: Record<string, unknown>) =>
          apiFetch<{ ok: boolean }>('/api/grc/sms/platform/config', { method: 'PUT', body: JSON.stringify(body) }),
        testerSolde: () =>
          apiFetch<{ ok: boolean; amount: number | null; currency: string | null; paymentType: string | null; error?: string }>('/api/grc/sms/platform/solde'),
        verifierSolde: () =>
          apiFetch<{ ok: boolean; soldeEur: number | null; seuilEur: number; alerteEmise: boolean; error?: string }>('/api/grc/sms/platform/verifier', { method: 'POST' }),
        // Demande de recharge (commune) — preuve obligatoire (FormData)
        demanderRecharge: (form: FormData) =>
          apiFetchRaw<{ id: string; statut: string }>('/api/grc/sms/recharge/demande', { method: 'POST', body: form }),
        mesDemandes: () =>
          apiFetch<{ demandes: Array<{ id: string; nbSmsDemande: number | null; message: string | null; preuveNomFichier: string | null; statut: string; nbSmsCredite: number | null; createdAt: string }> }>('/api/grc/sms/recharge/demandes'),
      },
    },
    // Phase 2A étendue L15c — newsletters
    newsletters: {
      list: (filters: { statut?: string; limit?: number; offset?: number } = {}) => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
        const qs = q.toString();
        return apiFetch<{
          items: GrcNewsletterShared[];
          total: number;
        }>(`/api/grc/newsletters${qs ? '?' + qs : ''}`);
      },
      get: (id: string) =>
        apiFetch<GrcNewsletterDetailShared>(`/api/grc/newsletters/${id}`),
      /** Export PDF d'archive d'une newsletter — Lot C 8.7. */
      exportPdf: (id: string) =>
        downloadCompta(`/api/grc/newsletters/${id}/export/pdf`, 'newsletter.pdf'),
      create: (body: { titre: string; sujetEmail: string; intro?: string; conclusion?: string }) =>
        apiFetch<GrcNewsletterShared>('/api/grc/newsletters', {
          method: 'POST', body: JSON.stringify(body),
        }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<GrcNewsletterShared>(`/api/grc/newsletters/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),
      send: (id: string) =>
        apiFetch<GrcNewsletterShared>(`/api/grc/newsletters/${id}/send`, { method: 'POST' }),
      cancel: (id: string) =>
        apiFetch<GrcNewsletterShared>(`/api/grc/newsletters/${id}/cancel`, { method: 'POST' }),
      delete: (id: string) =>
        apiFetch<void>(`/api/grc/newsletters/${id}`, { method: 'DELETE' }),
      articles: {
        create: (newsletterId: string, body: {
          categorie?: string; titre: string; contenu: string;
          imageUrl?: string; imageAlt?: string; lienUrl?: string;
        }) =>
          apiFetch<GrcNewsletterArticleShared>(`/api/grc/newsletters/${newsletterId}/articles`, {
            method: 'POST', body: JSON.stringify(body),
          }),
        update: (articleId: string, body: Record<string, unknown>) =>
          apiFetch<GrcNewsletterArticleShared>(`/api/grc/newsletters/articles/${articleId}`, {
            method: 'PATCH', body: JSON.stringify(body),
          }),
        delete: (articleId: string) =>
          apiFetch<void>(`/api/grc/newsletters/articles/${articleId}`, { method: 'DELETE' }),
        reorder: (newsletterId: string, articleIds: string[]) =>
          apiFetch<{ articles: GrcNewsletterArticleShared[] }>(
            `/api/grc/newsletters/${newsletterId}/articles/reorder`,
            { method: 'POST', body: JSON.stringify({ articleIds }) }
          ),
      },
    },
    // Planning communication/GRC (même format que ST & scolaire)
    planning: {
      listAgents: () =>
        apiFetch<{ items: { id: string; fullName: string; role: string; couleurMarker: string | null }[] }>('/api/grc/planning/agents'),
      setAgentCouleur: (agentUserId: string, couleurMarker: string | null) =>
        apiFetch<void>(`/api/grc/planning/agents/${agentUserId}/couleur`, { method: 'PATCH', body: JSON.stringify({ couleurMarker }) }),
      getWeek: (from: string, to: string) =>
        apiFetch<GrcPlanningCreneau[]>(`/api/grc/planning?from=${from}&to=${to}`),
      maSemaine: (from: string, to: string) =>
        apiFetch<GrcPlanningCreneau[]>(`/api/grc/planning/ma-semaine?from=${from}&to=${to}`),
      createCreneau: (body: { agentUserId: string; dateDebut: string; dateFin: string; titre: string; description?: string; type: string; plageType?: string; couleurMarker?: string }) =>
        apiFetch<GrcPlanningCreneau>('/api/grc/planning/creneaux', { method: 'POST', body: JSON.stringify(body) }),
      updateCreneau: (id: string, body: Record<string, unknown>) =>
        apiFetch<GrcPlanningCreneau>(`/api/grc/planning/creneaux/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      updateStatut: (id: string, statut: string) =>
        apiFetch<GrcPlanningCreneau>(`/api/grc/planning/creneaux/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
      deleteCreneau: (id: string) =>
        apiFetch<void>(`/api/grc/planning/creneaux/${id}`, { method: 'DELETE' }),
      listProfils: () => apiFetch<GrcPlanningProfil[]>('/api/grc/planning/profils'),
      // Présence unifiée (équipe/personne/année) — même format que le ST
      presenceSemaine: (weekStart: string) =>
        apiFetch<PlanningWeekResponse>(`/api/grc/planning/presence-semaine?weekStart=${weekStart}`),
      presenceAnnee: (annee: number, agentId?: string) =>
        apiFetch<PlanningAnneeResponse>(`/api/grc/planning/presence-annee?annee=${annee}${agentId ? `&agentId=${agentId}` : ''}`),
      // Postes (templates de poste : Accueil, RDV CNI…) — glissables sur le planning
      listPostes: () => apiFetch<Array<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>>('/api/grc/planning/postes'),
      createPoste: (body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>('/api/grc/planning/postes', { method: 'POST', body: JSON.stringify(body) }),
      updatePoste: (id: string, body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>(`/api/grc/planning/postes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deletePoste: (id: string) => apiFetch<void>(`/api/grc/planning/postes/${id}`, { method: 'DELETE' }),
      createProfil: (body: CreateGrcPlanningProfilInput) =>
        apiFetch<GrcPlanningProfil>('/api/grc/planning/profils', { method: 'POST', body: JSON.stringify(body) }),
      updateProfil: (id: string, body: CreateGrcPlanningProfilInput) =>
        apiFetch<{ profil: GrcPlanningProfil; supprimes: number; crees: number }>(`/api/grc/planning/profils/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteProfil: (id: string) =>
        apiFetch<void>(`/api/grc/planning/profils/${id}`, { method: 'DELETE' }),
      generer: (body: GenererGrcPlanningInput) =>
        apiFetch<GenererPlanningResult>('/api/grc/planning/generer', { method: 'POST', body: JSON.stringify(body) }),
      joursNonTravailles: (from: string, to: string) =>
        apiFetch<JourNonTravaille[]>(`/api/grc/planning/jours-non-travailles?from=${from}&to=${to}`),
      transmettrePaie: (from: string, to: string) =>
        apiFetch<{ transmis: number }>('/api/grc/planning/transmettre-paie', { method: 'POST', body: JSON.stringify({ from, to }) }),
    },
  },
  // Pôle Info (migration 116) — espace administré, accès citoyen, lecture seule.
  // Réglages d'organisation transverses — écran dans la brique RH (Profils
  // horaires). Routes SANS service GRC : un manager RH y accède (recette 02/08).
  organisation: {
    periodes: {
      list: () => apiFetch<{ periodes: PeriodeSaisonniere[] }>('/api/organisation/periodes'),
      create: (body: { nom: string; dateDebut: string; dateFin: string; preavisJours?: number }) =>
        apiFetch<PeriodeSaisonniere>('/api/organisation/periodes', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: { nom: string; dateDebut: string; dateFin: string; preavisJours?: number }) =>
        apiFetch<PeriodeSaisonniere>(`/api/organisation/periodes/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<void>(`/api/organisation/periodes/${id}`, { method: 'DELETE' }),
    },
  },

  // Dossiers d'état civil (item 13.1) — suivi de guichet, staff GRC.
  etatCivil: {
    list: (opts?: { type?: EtatCivilType; actifs?: boolean }) => {
      const q = new URLSearchParams();
      if (opts?.type) q.set('type', opts.type);
      if (opts?.actifs) q.set('actifs', 'true');
      const qs = q.toString();
      return apiFetch<{ dossiers: EtatCivilDossier[] }>(`/api/grc/etat-civil${qs ? `?${qs}` : ''}`);
    },
    create: (body: Partial<EtatCivilDossier>) =>
      apiFetch<EtatCivilDossier>('/api/grc/etat-civil', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<EtatCivilDossier>) =>
      apiFetch<EtatCivilDossier>(`/api/grc/etat-civil/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id: string) =>
      apiFetch<void>(`/api/grc/etat-civil/${id}`, { method: 'DELETE' }),
  },

  infosCommune: {
    get: () => apiFetch<PoleInfoPayload>('/api/infos-commune'),
    demarchePdfUrl: (id: string) =>
      apiFetch<{ url: string }>(`/api/infos-commune/demarches/${id}/pdf`),
  },

  // Déchets — espace administré (accès citoyen : sa collecte + préférence rappel).
  dechets: {
    get: () => apiFetch<DechetsCitoyenPayload | null>('/api/dechets'),
    setPreference: (body: { secteurId?: string | null; rappelActif: boolean }) =>
      apiFetch<DechetsCitoyenPayload | null>('/api/dechets/preference', {
        method: 'PUT', body: JSON.stringify(body),
      }),
  },

  // Objets trouvés — espace administré (accès citoyen : liste à réclamer, lecture seule).
  objetsTrouves: {
    get: () => apiFetch<ObjetTrouve[] | null>('/api/objets-trouves'),
  },

  // RDV — espace administré (accès citoyen : motifs, créneaux, réservation).
  rdv: {
    get: () => apiFetch<RdvCitoyenPayload | null>('/api/rdv'),
    creneaux: (motifId: string) =>
      apiFetch<{ creneaux: RdvCreneau[] }>(`/api/rdv/motifs/${motifId}/creneaux`),
    reserver: (body: { motifId: string; date: string; heureDebut: string; note?: string | null;
                       /** Item 13.2 — n° de pré-demande ANTS, pour un RDV titre d'identité. */
                       numeroPredemande?: string | null }) =>
      apiFetch<RdvReservation>('/api/rdv/reserver', { method: 'POST', body: JSON.stringify(body) }),
    annuler: (id: string) =>
      apiFetch<void>(`/api/rdv/reservations/${id}`, { method: 'DELETE' }),
  },

  // Points de collecte & déchetterie — espace administré (carte + dossier).
  collecte: {
    get: () => apiFetch<CollecteCitoyenPayload | null>('/api/collecte'),
    monDossier: () => apiFetch<CollecteDossier | null>('/api/collecte/mon-dossier'),
    deposerDossier: (body: { demandeurNom?: string | null; demandeurEmail?: string | null; demandeurTel?: string | null; demandeurAdresse?: string | null; message?: string | null }) =>
      apiFetch<CollecteDossier>('/api/collecte/dossier', { method: 'POST', body: JSON.stringify(body) }),
    ajouterPiece: (dossierId: string, file: File | Blob, libelle?: string, filename?: string) => {
      const fd = new FormData();
      fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'piece'));
      if (libelle) fd.append('libelle', libelle);
      return apiFetchRaw<CollecteDossierPiece>(`/api/collecte/dossier/${dossierId}/piece`, { method: 'POST', body: fd });
    },
    attestationBlob: async (dossierId: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/collecte/dossier/${dossierId}/attestation`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
    supprimerPiece: (dossierId: string, pieceId: string) =>
      apiFetch<void>(`/api/collecte/dossier/${dossierId}/piece/${pieceId}`, { method: 'DELETE' }),
  },

  // Cimetière — espace administré (accès citoyen : consulter + déposer une demande).
  cimetiere: {
    get: () => apiFetch<CimetiereCitoyenPayload | null>('/api/cimetiere'),
    creerDemande: (body: { typeDemande: 'nouvelle' | 'renouvellement'; concessionId?: string | null; message?: string | null }) =>
      apiFetch<ConcessionDemande>('/api/cimetiere/demandes', { method: 'POST', body: JSON.stringify(body) }),
  },

  // Boîte à idées — espace administré (proposer, soutenir, retirer).
  idees: {
    list: (opts?: { categorie?: string; statut?: string }) => {
      const qs = new URLSearchParams();
      if (opts?.categorie) qs.set('categorie', opts.categorie);
      if (opts?.statut) qs.set('statut', opts.statut);
      const q = qs.toString();
      return apiFetch<{ idees: Idee[] }>(`/api/idees${q ? `?${q}` : ''}`);
    },
    proposer: (body: { titre: string; description: string; categorie: IdeeCategorie }) =>
      apiFetch<Idee>('/api/idees', { method: 'POST', body: JSON.stringify(body) }),
    soutenir: (id: string) =>
      apiFetch<{ soutiens: number; jeSoutiens: boolean }>(`/api/idees/${id}/soutien`, { method: 'POST' }),
    retirer: (id: string) =>
      apiFetch<void>(`/api/idees/${id}`, { method: 'DELETE' }),
  },

  // Acteurs locaux — espace administré (annuaire public + produits).
  acteursLocaux: {
    list: (params?: { categorie?: string; q?: string }) => {
      const qs = new URLSearchParams();
      if (params?.categorie) qs.set('categorie', params.categorie);
      if (params?.q) qs.set('q', params.q);
      const q = qs.toString();
      return apiFetch<ActeursCitoyenPayload | null>(`/api/acteurs-locaux${q ? `?${q}` : ''}`);
    },
    declarer: (body: Record<string, unknown>) =>
      apiFetch<ActeurLocal>('/api/acteurs-locaux/declarer', { method: 'POST', body: JSON.stringify(body) }),
    mesDeclarations: () =>
      apiFetch<{ declarations: ActeurLocal[] }>('/api/acteurs-locaux/mes-declarations'),
  },
  // Référendums (migration 117) — espace administré : consulter & voter.
  referendumsCitoyen: {
    list: () => apiFetch<{ referendums: Referendum[] }>('/api/referendums'),
    get: (id: string) => apiFetch<Referendum>(`/api/referendums/${id}`),
    vote: (id: string, optionId: string) =>
      apiFetch<Referendum>(`/api/referendums/${id}/vote`, { method: 'POST', body: JSON.stringify({ optionId }) }),
    franceConnectLogin: (id: string) =>
      apiFetch<{ url: string; mock: boolean }>(`/api/referendums/${id}/franceconnect/login`),
  },
  // Urbanisme (migration 119) — back-office service urbanisme.
  urbanisme: {
    planning: {
      presenceSemaine: (weekStart: string) =>
        apiFetch<PlanningWeekResponse>(`/api/urbanisme/planning/presence-semaine?weekStart=${weekStart}`),
      presenceAnnee: (annee: number, agentId?: string) =>
        apiFetch<PlanningAnneeResponse>(`/api/urbanisme/planning/presence-annee?annee=${annee}${agentId ? `&agentId=${agentId}` : ''}`),
      listAgents: () => apiFetch<{ items: { id: string; fullName: string; role: string; couleurMarker: string | null }[] }>('/api/urbanisme/planning/agents'),
      setAgentCouleur: (agentUserId: string, couleurMarker: string | null) =>
        apiFetch<void>(`/api/urbanisme/planning/agents/${agentUserId}/couleur`, { method: 'PATCH', body: JSON.stringify({ couleurMarker }) }),
      joursNonTravailles: (from: string, to: string) =>
        apiFetch<{ date: string; libelle: string }[]>(`/api/urbanisme/planning/jours-non-travailles?from=${from}&to=${to}`),
      listProfils: () => apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }[]>('/api/urbanisme/planning/profils'),
      createProfil: (body: { nom: string; plages: ProfilPlage[] }) =>
        apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>('/api/urbanisme/planning/profils', { method: 'POST', body: JSON.stringify(body) }),
      updateProfil: (id: string, body: { nom: string; plages: ProfilPlage[] }) =>
        apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>(`/api/urbanisme/planning/profils/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteProfil: (id: string) => apiFetch<void>(`/api/urbanisme/planning/profils/${id}`, { method: 'DELETE' }),
      generer: (body: { profilId: string; agentIds: string[]; from: string; to: string; inclureVacances?: boolean }) =>
        apiFetch<{ creneauxCrees: number; joursIgnores: number }>('/api/urbanisme/planning/generer', { method: 'POST', body: JSON.stringify(body) }),
      transmettrePaie: (from: string, to: string) =>
        apiFetch<{ transmis: number }>('/api/urbanisme/planning/transmettre-paie', { method: 'POST', body: JSON.stringify({ from, to }) }),
      // Grille unique (livraison B) — créneaux + postes glissables.
      getWeek: (from: string, to: string) =>
        apiFetch<{ creneaux: SimpleGrilleCreneau[] }>(`/api/urbanisme/planning?from=${from}&to=${to}`).then((r) => r.creneaux),
      getMaSemaine: (from: string, to: string) =>
        apiFetch<{ creneaux: SimpleGrilleCreneau[] }>(`/api/urbanisme/planning/ma-semaine?from=${from}&to=${to}`).then((r) => r.creneaux),
      createCreneau: (body: SimpleCreneauInput) =>
        apiFetch<SimpleGrilleCreneau>('/api/urbanisme/planning/creneaux', { method: 'POST', body: JSON.stringify(body) }),
      updateCreneau: (id: string, body: Partial<SimpleCreneauInput>) =>
        apiFetch<SimpleGrilleCreneau>(`/api/urbanisme/planning/creneaux/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      updateStatut: (id: string, statut: 'planifie' | 'absent') =>
        apiFetch<SimpleGrilleCreneau>(`/api/urbanisme/planning/creneaux/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
      deleteCreneau: (id: string) => apiFetch<void>(`/api/urbanisme/planning/creneaux/${id}`, { method: 'DELETE' }),
      listPostes: () => apiFetch<SimplePoste[]>('/api/urbanisme/planning/postes'),
      createPoste: (body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<SimplePoste>('/api/urbanisme/planning/postes', { method: 'POST', body: JSON.stringify(body) }),
      updatePoste: (id: string, body: { titre?: string; dureeMin?: number; couleur?: string }) =>
        apiFetch<SimplePoste>(`/api/urbanisme/planning/postes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deletePoste: (id: string) => apiFetch<void>(`/api/urbanisme/planning/postes/${id}`, { method: 'DELETE' }),
    },
    listDemandes: (filtre?: { statut?: UrbaStatut; aInstruire?: boolean; decides?: boolean }) => {
      const q = new URLSearchParams();
      if (filtre?.statut) q.set('statut', filtre.statut);
      if (filtre?.aInstruire) q.set('aInstruire', 'true');
      if (filtre?.decides) q.set('decides', 'true');
      const qs = q.toString();
      return apiFetch<{ demandes: UrbaDemande[] }>(`/api/urbanisme/demandes${qs ? `?${qs}` : ''}`);
    },
    getDemande: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}`),
    changerStatut: (id: string, body: { statut: Exclude<UrbaStatut, 'deposee' | 'annulee'>; motif?: string | null; piecesDemandees?: string | null; piecesMessage?: string | null }) =>
      apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/statut`, { method: 'PUT', body: JSON.stringify(body) }),
    addAvis: (id: string, body: { service: string; sens?: UrbaAvisSens; commentaire?: string | null; saisiAt?: string | null; reponseAt?: string | null }) =>
      apiFetch<UrbaAvis>(`/api/urbanisme/demandes/${id}/avis`, { method: 'POST', body: JSON.stringify(body) }),
    updateAvis: (avisId: string, body: { service: string; sens?: UrbaAvisSens; commentaire?: string | null; saisiAt?: string | null; reponseAt?: string | null }) =>
      apiFetch<UrbaAvis>(`/api/urbanisme/avis/${avisId}`, { method: 'PUT', body: JSON.stringify(body) }),
    removeAvis: (avisId: string) => apiFetch<void>(`/api/urbanisme/avis/${avisId}`, { method: 'DELETE' }),
    config: () => apiFetch<{ signatureDisponible: boolean; actes: { tiersTeletransmission: string | null; lienPrefectureActes: string | null } }>('/api/urbanisme/config'),
    marquerActesTransmis: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/actes`, { method: 'POST' }),
    annulerActesTransmis: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/actes`, { method: 'DELETE' }),
    genererArrete: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/arrete`, { method: 'POST' }),
    arreteUrl: (id: string) => apiFetch<{ url: string }>(`/api/urbanisme/demandes/${id}/arrete/url`),
    envoyerSignature: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/signature`, { method: 'POST' }),
    statutSignature: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/signature/statut`, { method: 'POST' }),
    rafraichirCadastre: (id: string) => apiFetch<UrbaDemande>(`/api/urbanisme/demandes/${id}/cadastre`, { method: 'POST' }),
    geoportailUrl: (id: string) => apiFetch<{ url: string }>(`/api/urbanisme/demandes/${id}/geoportail`),
    pieceUrl: (id: string) => apiFetch<{ url: string }>(`/api/urbanisme/pieces/${id}/url`),
    removeDemande: (id: string) => apiFetch<void>(`/api/urbanisme/demandes/${id}`, { method: 'DELETE' }),
    // Documents
    listDocuments: () => apiFetch<{ documents: UrbaDocument[] }>('/api/urbanisme/documents'),
    createDocument: (body: { titre: string; typeDoc?: string; typeLibre?: string | null; lienGpu?: string | null }) =>
      apiFetch<UrbaDocument>('/api/urbanisme/documents', { method: 'POST', body: JSON.stringify(body) }),
    updateDocument: (id: string, body: { titre: string; typeDoc?: string; typeLibre?: string | null; lienGpu?: string | null; affiche?: boolean }) =>
      apiFetch<UrbaDocument>(`/api/urbanisme/documents/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    reorderDocuments: (ids: string[]) =>
      apiFetch<{ ok: boolean }>('/api/urbanisme/documents/ordre', { method: 'PUT', body: JSON.stringify({ ids }) }),
    uploadDocumentFichier: (id: string, file: File | Blob, filename?: string) => {
      const fd = new FormData();
      fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'document'));
      return apiFetchRaw<UrbaDocument>(`/api/urbanisme/documents/${id}/fichier`, { method: 'POST', body: fd });
    },
    removeDocument: (id: string) => apiFetch<void>(`/api/urbanisme/documents/${id}`, { method: 'DELETE' }),
    cerfaCatalogue: () => apiFetch<{ cerfa: { numero: string; titre: string }[] }>('/api/urbanisme/documents/cerfa-catalogue'),
    importCerfa: () => apiFetch<{ importes: number; total: number }>('/api/urbanisme/documents/import-cerfa', { method: 'POST' }),
  },
  // Urbanisme — espace administré (accès citoyen).
  mesDemandesUrbanisme: {
    list: () => apiFetch<{ demandes: UrbaDemande[] }>('/api/mes-demandes-urbanisme'),
    get: (id: string) => apiFetch<UrbaDemande>(`/api/mes-demandes-urbanisme/${id}`),
    create: (body: { typeDemande: UrbaType; cuNature?: 'informatif' | 'operationnel' | null; pcmi?: boolean; parcelle?: string | null; surfaceM2?: number | null; adresseTerrain?: string | null; description?: string | null }) =>
      apiFetch<UrbaDemande>('/api/mes-demandes-urbanisme', { method: 'POST', body: JSON.stringify(body) }),
    annuler: (id: string) => apiFetch<UrbaDemande>(`/api/mes-demandes-urbanisme/${id}/annuler`, { method: 'POST' }),
    addPiece: (id: string, file: File | Blob, filename?: string) => {
      const fd = new FormData();
      fd.append('fichier', file, filename ?? (file instanceof File ? file.name : 'piece'));
      return apiFetchRaw<UrbaPiece>(`/api/mes-demandes-urbanisme/${id}/pieces`, { method: 'POST', body: fd });
    },
    pieceUrl: (id: string) => apiFetch<{ url: string }>(`/api/mes-demandes-urbanisme/pieces/${id}/url`),
    listDocuments: () => apiFetch<{ documents: UrbaDocument[] }>('/api/mes-demandes-urbanisme/documents'),
    documentUrl: (id: string) => apiFetch<{ url: string }>(`/api/mes-demandes-urbanisme/documents/${id}/url`),
    arreteUrl: (id: string) => apiFetch<{ url: string }>(`/api/mes-demandes-urbanisme/${id}/arrete/url`),
    geoportailUrl: (id: string) => apiFetch<{ url: string }>(`/api/mes-demandes-urbanisme/${id}/geoportail`),
  },
  // Phase 2A étendue L13 — parc véhicules
  parc: {
    dashboard: () =>
      apiFetch<{
        kpis: {
          totalVehicules: number;
          depensesAnnee: number;
          ctAVenir30j: number;
          pneusARemplacer: number;
          kmMoyensAn: number | null;
        };
        vehicules: VehiculeWithKpisShared[];
      }>('/api/parc/dashboard'),
    list: (filters: { actif?: boolean; type?: string; search?: string } = {}) => {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
      const qs = q.toString();
      return apiFetch<VehiculeWithKpisShared[]>(`/api/parc/vehicules${qs ? '?' + qs : ''}`);
    },
    get: (id: string) => apiFetch<VehiculeDetailShared>(`/api/parc/vehicules/${id}`),
    create: (body: Record<string, unknown>) =>
      apiFetch<VehiculeWithKpisShared>('/api/parc/vehicules', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Record<string, unknown>) =>
      apiFetch<VehiculeWithKpisShared>(`/api/parc/vehicules/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch<void>(`/api/parc/vehicules/${id}`, { method: 'DELETE' }),
    /** Photos de la fiche véhicule (2 max, internes — item 3.10). */
    photos: {
      list: (vehiculeId: string) =>
        apiFetch<{ items: FichePhotoShared[] }>(`/api/parc/vehicules/${vehiculeId}/photos`),
      signedUrl: (vehiculeId: string, photoId: string) =>
        apiFetch<{ url: string }>(`/api/parc/vehicules/${vehiculeId}/photos/${photoId}/signed-url`),
      add: (vehiculeId: string, file: File) => {
        const fd = new FormData();
        fd.append('photo', file);
        return apiFetchRaw<FichePhotoShared>(`/api/parc/vehicules/${vehiculeId}/photos`, {
          method: 'POST', body: fd,
        });
      },
      remove: (vehiculeId: string, photoId: string) =>
        apiFetchRaw<void>(`/api/parc/vehicules/${vehiculeId}/photos/${photoId}`, { method: 'DELETE' }),
    },
    carnet: {
      create: (vehiculeId: string, body: Record<string, unknown>) =>
        apiFetch<VehiculeEntreeCarnetShared>(`/api/parc/vehicules/${vehiculeId}/carnet`, {
          method: 'POST', body: JSON.stringify(body),
        }),
      delete: (entreeId: string) =>
        apiFetch<void>(`/api/parc/carnet/${entreeId}`, { method: 'DELETE' }),
    },
    scanFacture: (vehiculeId: string, imageBase64: string, mediaType: 'image/jpeg' | 'image/png' | 'image/webp') =>
      apiFetch<OcrFactureResultShared>(`/api/parc/vehicules/${vehiculeId}/scan-facture`, {
        method: 'POST', body: JSON.stringify({ imageBase64, mediaType }),
      }),
    /** Export parc — Lot C 3.11. Excel (donnée retravaillable) ou PDF charté. */
    exportExcel: () => downloadCompta('/api/parc/export/excel', 'parc-vehicules.xlsx'),
    exportPdf: () => downloadCompta('/api/parc/export/pdf', 'parc-vehicules.pdf'),
  },

  // Location matériel (tranche 1) — catalogue
  materiel: {
    list: (filters: { actif?: boolean; categorie?: string; search?: string } = {}) => {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
      const qs = q.toString();
      return apiFetch<MaterielCatalogueShared[]>(`/api/materiel/catalogue${qs ? '?' + qs : ''}`);
    },
    get: (id: string) => apiFetch<MaterielCatalogueShared>(`/api/materiel/catalogue/${id}`),
    create: (body: Record<string, unknown>) =>
      apiFetch<MaterielCatalogueShared>('/api/materiel/catalogue', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Record<string, unknown>) =>
      apiFetch<MaterielCatalogueShared>(`/api/materiel/catalogue/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => apiFetch<void>(`/api/materiel/catalogue/${id}`, { method: 'DELETE' }),
    notifUsers: () =>
      apiFetch<Array<{ id: string; nom: string; role: string; service: string | null }>>(
        '/api/materiel/notif-users'
      ),
    /** État courant du lieu d'un matériel (staff) — pour « Mettre à jour l'état des lieux ». */
    etatCourant: (materielId: string) =>
      apiFetch<EtatCourantLieuShared>(`/api/materiel/catalogue/${materielId}/etat-courant`),
    /** Photos de la fiche matériel/salle (2 max, internes — item 3.10). */
    photos: {
      list: (materielId: string) =>
        apiFetch<{ items: FichePhotoShared[] }>(`/api/materiel/catalogue/${materielId}/photos`),
      signedUrl: (materielId: string, photoId: string) =>
        apiFetch<{ url: string }>(`/api/materiel/catalogue/${materielId}/photos/${photoId}/signed-url`),
      add: (materielId: string, file: File) => {
        const fd = new FormData();
        fd.append('photo', file);
        return apiFetchRaw<FichePhotoShared>(`/api/materiel/catalogue/${materielId}/photos`, {
          method: 'POST', body: fd,
        });
      },
      remove: (materielId: string, photoId: string) =>
        apiFetchRaw<void>(`/api/materiel/catalogue/${materielId}/photos/${photoId}`, { method: 'DELETE' }),
    },
    reservations: {
      list: (filters: { statut?: string; materielId?: string } = {}) => {
        const q = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') q.set(k, String(v)); });
        const qs = q.toString();
        return apiFetch<MaterielReservationShared[]>(`/api/materiel/reservations${qs ? '?' + qs : ''}`);
      },
      get: (id: string) => apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}`),
      disponibiliteSalle: (salleId: string, from: string, to: string) =>
        apiFetch<{ occupes: Array<{ date: string; creneau: 'matin' | 'apres_midi' | 'journee' }> }>(
          `/api/materiel/salles/${salleId}/disponibilite?from=${from}&to=${to}`),
      create: (body: Record<string, unknown>) =>
        apiFetch<MaterielReservationShared>('/api/materiel/reservations', { method: 'POST', body: JSON.stringify(body) }),
      mesAffiliations: () =>
        apiFetch<{ affiliations: Array<{ type: 'association' | 'acteur'; id: string; nom: string; detail: string | null }> }>('/api/materiel/mes-affiliations'),
      updateStatut: (id: string, body: Record<string, unknown>) =>
        apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      /**
       * 9.2 — les clés du bâtiment de cette salle, et l'état de la remise.
       * `remise.remises` sert à griser le bouton du service qui arrive second.
       */
      cles: (id: string) => apiFetch<ClesReservationVue>(`/api/materiel/reservations/${id}/cles`),
      /**
       * 9.1 — où en est l'association de son contingent annuel de buvettes
       * (CSP art. L3334-2). Avertit, n'interdit pas.
       */
      contingentBuvette: (id: string) =>
        apiFetch<ContingentBuvetteDTO>(`/api/materiel/reservations/${id}/contingent-buvette`),
      controleEtat: (id: string, body: { conforme: boolean; note?: string }) =>
        apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}/controle-etat`, { method: 'POST', body: JSON.stringify({ conforme: body.conforme, note: body.note ?? null }) }),
      encaisserLocation: (id: string, moyen: 'cheque' | 'espece' | 'cb' | 'virement' | 'prelevement') =>
        apiFetch<{ created: boolean; encaissementId?: string }>(`/api/materiel/reservations/${id}/encaisser-location`, { method: 'POST', body: JSON.stringify({ moyen }) }),
      annuler: (id: string) =>
        apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}/annuler`, { method: 'POST' }),
      prolonger: (id: string, body: Record<string, unknown>) =>
        apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}/prolonger`, {
          method: 'POST', body: JSON.stringify(body),
        }),
      /** Fetch authentifié du récap PDF — l'appelant fait URL.createObjectURL(blob). */
      pdfBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/materiel/reservations/${id}/pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        }
        return res.blob();
      },
      /** État des lieux d'ENTRÉE (version publique) — état courant du lieu du bien loué. */
      etatEntreePdfBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/materiel/reservations/${id}/etat-des-lieux-entree/pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        }
        return res.blob();
      },
      cautionRendue: (id: string) =>
        apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}/caution-rendue`, {
          method: 'POST',
        }),
      cautionStatut: (id: string, body: { statut: 'rendue' | 'non_rendue' | 'encaissee'; commentaire?: string }) =>
        apiFetch<MaterielReservationShared>(`/api/materiel/reservations/${id}/caution-statut`, {
          method: 'POST', body: JSON.stringify({ statut: body.statut, commentaire: body.commentaire ?? null }),
        }),
    },
  },
  // R13 — endpoints /me/* du user courant (suivi signalements administré)
  me: {
    modules: () => apiFetch<{ modules: { moduleKey: string; actif: boolean; socle: boolean }[]; masquesAdministre: string[] }>('/api/me/modules'),
    /**
     * Canaux choisis pour les notifications qui visent la personne en tant
     * qu'HABITANT (lot N4). Ne concerne jamais les notifications de travail :
     * celles-ci sont réglées par le maire / la RH via le catalogue.
     */
    getPreferencesNotifications: () =>
      apiFetch<{ cloche: true; email: boolean; push: boolean }>('/api/me/preferences-notifications'),
    setPreferencesNotifications: (prefs: { email?: boolean; push?: boolean }) =>
      apiFetch<{ cloche: true; email: boolean; push: boolean }>('/api/me/preferences-notifications', {
        method: 'PUT', body: JSON.stringify(prefs),
      }),
    signalements: {
      list: () =>
        apiFetch<{
          items: Array<{
            id: string;
            codeSuivi: string | null;
            numero: string;
            sujet: string;
            categorie: string;
            urgence: string;
            statut: string;
            statutLabel: string;
            localisationAdresse: string | null;
            createdAt: string;
            resoluAt: string | null;
          }>;
        }>('/api/me/signalements'),
    },
  },
  // L13.17 — notifications in-app (la cloche)
  notifications: {
    list: () =>
      apiFetch<{ items: NotificationItem[] }>('/api/notifications'),
    unreadCount: () =>
      apiFetch<{ count: number }>('/api/notifications/unread-count'),
    markAllRead: () =>
      apiFetch<{ updated: number }>('/api/notifications/mark-all-read', {
        method: 'POST',
      }),
    remove: (id: string) =>
      apiFetch<void>(`/api/notifications/${id}`, { method: 'DELETE' }),
    removeAll: () =>
      apiFetch<{ deleted: number }>('/api/notifications', { method: 'DELETE' }),
  },
  // ── Notes de frais (module transverse RH ↔ Comptabilité) ──
  notesFrais: {
    types: {
      list: (actifs = false) => apiFetch<NdfTypeFrais[]>(`/api/notes-frais/types${actifs ? '?actifs=1' : ''}`),
      create: (body: Partial<NdfTypeFrais>) => apiFetch<NdfTypeFrais>('/api/notes-frais/types', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Partial<NdfTypeFrais>) => apiFetch<NdfTypeFrais>(`/api/notes-frais/types/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },
    baremeKm: {
      list: () => apiFetch<NdfBaremeKm[]>('/api/notes-frais/bareme-km'),
      updateTaux: (id: string, taux: number) => apiFetch<{ ok: true }>(`/api/notes-frais/bareme-km/${id}`, { method: 'PATCH', body: JSON.stringify({ taux }) }),
    },
    /**
     * Aide à la saisie. `annee` sert à situer la TRANCHE du barème : elle est
     * progressive et se lit sur le cumul annuel du déclarant, pas sur la
     * distance du trajet. Sans elle, l'écran afficherait un taux que le serveur
     * ne retiendrait pas.
     */
    calculKm: (distance: number, puissance: number, annee?: number) =>
      apiFetch<{ taux: number; montant: number }>(
        `/api/notes-frais/calcul-km?distance=${distance}&puissance=${puissance}`
        + (annee ? `&annee=${annee}` : '')),
    /**
     * Ordres de mission ACTIFS (item 5.18, mig 291). Route dédiée derrière
     * `ndf.note.declare` : un déclarant n'a pas accès aux réglages RH.
     */
    ordresMission: () => apiFetch<RhOrdreMission[]>('/api/notes-frais/ordres-mission'),
    notes: {
      mine: () => apiFetch<NdfNote[]>('/api/notes-frais/mes-notes'),
      get: (id: string) => apiFetch<NdfNote>(`/api/notes-frais/notes/${id}`),
      create: (body: { titre: string; dateNote?: string; lignes: unknown[]; tiersId?: string | null; envoyer?: boolean }) =>
        apiFetch<{ id: string }>('/api/notes-frais/notes', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: { titre: string; dateNote?: string; lignes: unknown[] }) =>
        apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      soumettre: (id: string) => apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}/soumettre`, { method: 'POST' }),
      remove: (id: string) => apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}`, { method: 'DELETE' }),
      justificatif: (key: string) => apiFetch<{ url: string }>(`/api/notes-frais/justificatif?key=${encodeURIComponent(key)}`),
      aValiderN1: () => apiFetch<Array<NdfNote & { nbLignes: number; alerte: boolean }>>('/api/notes-frais/a-valider-n1'),
      validerN1: (id: string) => apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}/valider-n1`, { method: 'POST' }),
      refuser: (id: string, motif: string) => apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}/refuser`, { method: 'POST', body: JSON.stringify({ motif }) }),
      compta: (statut: 'attente' | 'arembourser' | 'remboursees' | 'all') => apiFetch<Array<NdfNote & { valideN1Nom: string | null; n1Auto: boolean }>>(`/api/notes-frais/compta?statut=${statut}`),
      comptaStats: () => apiFetch<{ aValider: number; aRembourser: number; ceMois: number; annee: number }>('/api/notes-frais/compta/stats'),
      validerCompta: (id: string) => apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}/valider-compta`, { method: 'POST' }),
      rembourser: (id: string, mode: string, reference?: string) => apiFetch<{ ok: true }>(`/api/notes-frais/notes/${id}/rembourser`, { method: 'POST', body: JSON.stringify({ mode, reference }) }),
    },
    tiers: {
      list: (q?: string) => apiFetch<NdfTiers[]>(`/api/notes-frais/tiers${q ? `?q=${encodeURIComponent(q)}` : ''}`),
      create: (body: Partial<NdfTiers>) => apiFetch<NdfTiers>('/api/notes-frais/tiers', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Partial<NdfTiers>) => apiFetch<NdfTiers>(`/api/notes-frais/tiers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },
    uploadJustificatif: async (file: File): Promise<{ r2Key: string; nom: string }> => {
      const fd = new FormData(); fd.set('piece', file);
      const token = getAccessToken();
      const res = await fetch('/api/notes-frais/upload-justificatif', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', body: fd });
      if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Erreur' })));
      return res.json() as Promise<{ r2Key: string; nom: string }>;
    },
  },
  // Annuaire des tiers partagé entre briques (mig 213) — SANS IBAN.
  annuaire: {
    categories: () => apiFetch<{ categories: TiersCategorieUsage[] }>('/api/annuaire/categories'),
    listTiers: (opts?: { recherche?: string; categorie?: TiersCategorieUsage }) => {
      const qs = new URLSearchParams();
      if (opts?.recherche) qs.set('recherche', opts.recherche);
      if (opts?.categorie) qs.set('categorie', opts.categorie);
      const s = qs.toString();
      return apiFetch<AnnuaireTiers[]>(`/api/annuaire/tiers${s ? `?${s}` : ''}`);
    },
    createTiers: (body: { nom: string; email?: string | null; telephone?: string | null; siret?: string | null; adresse?: string | null; codePostal?: string | null; ville?: string | null; categoriesUsage?: TiersCategorieUsage[] }) =>
      apiFetch<NdfTiers>('/api/annuaire/tiers', { method: 'POST', body: JSON.stringify(body) }),
    updateTiers: (id: string, body: { nom: string; email?: string | null; telephone?: string | null; siret?: string | null; adresse?: string | null; codePostal?: string | null; ville?: string | null; categoriesUsage?: TiersCategorieUsage[] }) =>
      apiFetch<NdfTiers>(`/api/annuaire/tiers/${id}/categories`, { method: 'PATCH', body: JSON.stringify(body) }),
  },
  // « Mon espace agent » (mig 253) — brique transverse, tous services.
  // `tous: true` n'est utilisé que par l'écran de configuration : il inclut les
  // entrées désactivées, que l'agent ne doit jamais voir.
  /** Annuaire INTERNE des agents (item 6.3, mig 286) — à ne pas confondre
   *  avec `annuaire` ci-dessus, qui est l'annuaire des TIERS. */
  /** M6 — échéances personnalisées (personnel de la commune). */
  echeancesPerso: {
    lister: (toutes = false) => apiFetch<EcheancePersoDTO[]>(`/api/echeances-perso${toutes ? '?toutes=1' : ''}`),
    creer: (body: EcheancePersoInput) =>
      apiFetch<{ id: string }>('/api/echeances-perso', { method: 'POST', body: JSON.stringify(body) }),
    modifier: (id: string, body: EcheancePersoInput) =>
      apiFetch<void>(`/api/echeances-perso/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    faite: (id: string) => apiFetch<void>(`/api/echeances-perso/${id}/faite`, { method: 'POST' }),
    annuler: (id: string) => apiFetch<void>(`/api/echeances-perso/${id}/annuler`, { method: 'POST' }),
  },
  annuaireInterne: {
    lister: () => apiFetch<AnnuaireInterneReponse>('/api/annuaire-interne'),
    majReglages: (body: AnnuaireInterneReglages) =>
      apiFetch<AnnuaireInterneReglages>('/api/annuaire-interne/reglages', {
        method: 'PUT', body: JSON.stringify(body),
      }),
  },

  monEspace: {
    tout: () => apiFetch<AgentsEspacePayload>('/api/mon-espace'),
    listCategories: () => apiFetch<AgentsLienCategorie[]>('/api/mon-espace/categories'),
    creerCategorie: (body: { libelle: string; ordre?: number }) =>
      apiFetch<AgentsLienCategorie>('/api/mon-espace/categories', { method: 'POST', body: JSON.stringify(body) }),
    modifierCategorie: (id: string, body: { libelle: string; ordre?: number }) =>
      apiFetch<AgentsLienCategorie>(`/api/mon-espace/categories/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    supprimerCategorie: (id: string) =>
      apiFetch<void>(`/api/mon-espace/categories/${id}`, { method: 'DELETE' }),

    listLiens: (tous = false) =>
      apiFetch<AgentsLienUtile[]>(`/api/mon-espace/liens${tous ? '?tous=1' : ''}`),
    creerLien: (body: LienUtilePayload) =>
      apiFetch<AgentsLienUtile>('/api/mon-espace/liens', { method: 'POST', body: JSON.stringify(body) }),
    modifierLien: (id: string, body: LienUtilePayload) =>
      apiFetch<AgentsLienUtile>(`/api/mon-espace/liens/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    supprimerLien: (id: string) =>
      apiFetch<void>(`/api/mon-espace/liens/${id}`, { method: 'DELETE' }),

    listUrgences: (tous = false) =>
      apiFetch<AgentsContactUrgence[]>(`/api/mon-espace/urgences${tous ? '?tous=1' : ''}`),
    creerUrgence: (body: ContactUrgencePayload) =>
      apiFetch<AgentsContactUrgence>('/api/mon-espace/urgences', { method: 'POST', body: JSON.stringify(body) }),
    modifierUrgence: (id: string, body: ContactUrgencePayload) =>
      apiFetch<AgentsContactUrgence>(`/api/mon-espace/urgences/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    supprimerUrgence: (id: string) =>
      apiFetch<void>(`/api/mon-espace/urgences/${id}`, { method: 'DELETE' }),
  },
  // Diffusions internes à retour attendu (mig 254, item 5.25).
  // Écrit dans les tables de la messagerie : l'inbox et la cloche marchent seules.
  diffusions: {
    mesAttentes: () => apiFetch<DiffusionAAcquitter[]>('/api/diffusions/mes-attentes'),
    acquitter: (id: string) =>
      apiFetch<{ acquitteAt: string }>(`/api/diffusions/${id}/acquitter`, { method: 'POST' }),
    maPortee: () =>
      apiFetch<{ peutDiffuserATous: boolean; monSecteur: string | null }>('/api/diffusions/ma-portee'),
    creer: (body: {
      objet: string; corps: string; retourAttendu: RetourAttendu;
      cibleType: DiffusionCibleType; cibleServices?: string[];
      destinataireIds?: string[]; documentIds?: string[];
      dateLimite?: string | null; inclutNouveauxArrivants?: boolean;
      /** M20 — admins/élus ajoutés nommément. */
      inclureAdminIds?: string[];
    }) =>
      apiFetch<{ id: string; destinataires: number }>('/api/diffusions', { method: 'POST', body: JSON.stringify(body) }),
    adminsIncluables: () => apiFetch<{ id: string; nom: string }[]>('/api/diffusions/admins-incluables'),
    suivi: (id: string) => apiFetch<SuiviDiffusion>(`/api/diffusions/${id}/suivi`),
    relancer: (id: string) =>
      apiFetch<{ relances: number }>(`/api/diffusions/${id}/relancer`, { method: 'POST' }),
    reglagesPortee: () => apiFetch<ReglagePorteeDiffusion[]>('/api/diffusions/reglages/portee'),
    setReglagePortee: (brique: string, portee: PorteeDiffusion) =>
      apiFetch<{ brique: string; portee: PorteeDiffusion }>('/api/diffusions/reglages/portee', { method: 'PUT', body: JSON.stringify({ brique, portee }) }),
  },
  // Délégations de brique (mig 255, item 15.6). Les données restent chez la
  // commune : ceci ne décrit QUE qui exerce la compétence, et sous quel régime.
  delegations: {
    list: (historique = false) =>
      apiFetch<BriqueDelegation[]>(`/api/delegations${historique ? '?historique=1' : ''}`),
    delegatairesPossibles: () =>
      apiFetch<{ id: string; nom: string; type: string }[]>('/api/delegations/delegataires-possibles'),
    creer: (body: {
      brique: string;
      delegataireTenantId?: string | null;
      delegataireIdentiteId?: string | null;
      regime: RegimeDelegation;
      responsableTraitement?: ResponsableTraitement;
      dateDebut: string;
      dateFin?: string | null;
      note?: string | null;
    }) => apiFetch<BriqueDelegation>('/api/delegations', { method: 'POST', body: JSON.stringify(body) }),
    terminer: (id: string, dateFin?: string | null) =>
      apiFetch<BriqueDelegation>(`/api/delegations/${id}/terminer`, { method: 'POST', body: JSON.stringify({ dateFin }) }),
    /** Le registre des accès délégués — lectures comprises (mig 256). */
    journal: (delegationId?: string) =>
      apiFetch<{
        id: string; brique: string; methode: string; chemin: string;
        agent: string; delegataire: string; createdAt: string;
      }[]>(`/api/delegations/journal${delegationId ? `?delegationId=${delegationId}` : ''}`),
  },
  // Catalogue COMMUNAL de modèles d'actes (mig 257). Une seule table pour
  // toutes les briques : le maire tient un seul registre des arrêtés.
  acteModeles: {
    list: (opts: { brique?: string; source?: string; tous?: boolean; pour?: string } = {}) => {
      const q = new URLSearchParams();
      if (opts.brique) q.set('brique', opts.brique);
      if (opts.source) q.set('source', opts.source);
      if (opts.tous) q.set('tous', '1');
      // `pour=service_technique` : filtré sur ce que la commune lui a ouvert.
      if (opts.pour) q.set('pour', opts.pour);
      const qs = q.toString();
      return apiFetch<ActeModele[]>(`/api/acte-modeles${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => apiFetch<ActeModele>(`/api/acte-modeles/${id}`),
    categories: () => apiFetch<string[]>('/api/acte-modeles/categories'),
    upsert: (body: Partial<ActeModele> & { code: string; libelle: string }) =>
      apiFetch<ActeModele>('/api/acte-modeles', { method: 'POST', body: JSON.stringify(body) }),
    supprimer: (id: string) =>
      apiFetch<void>(`/api/acte-modeles/${id}`, { method: 'DELETE' }),

    circuit: (modeleId: string | null) =>
      apiFetch<ActeCircuitIntervenant[]>(
        modeleId ? `/api/acte-modeles/${modeleId}/circuit` : '/api/acte-modeles/circuit-commun'),
    ajouterAuCircuit: (modeleId: string | null, body: {
      role: 'relecteur' | 'valideur'; relecteurId?: string | null; userId?: string | null; ordre?: number;
    }) => apiFetch<ActeCircuitIntervenant[]>(
      modeleId ? `/api/acte-modeles/${modeleId}/circuit` : '/api/acte-modeles/circuit-commun',
      { method: 'POST', body: JSON.stringify(body) }),
    retirerDuCircuit: (id: string) =>
      apiFetch<void>(`/api/acte-modeles/circuit/${id}`, { method: 'DELETE' }),

    /** `valider` à false = on VÉRIFIE le fichier sans rien enregistrer. */
    importer: async (fichier: File, valider: boolean) => {
      const fd = new FormData();
      fd.append('fichier', fichier);
      const token = getAccessToken();
      const res = await fetch(`/api/acte-modeles/import${valider ? '?valider=1' : ''}`, {
        method: 'POST', body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      if (!res.ok) throw new ApiException(res.status, data);
      return data as { lignes: number; crees: number; majs: number; erreurs: { ligne: number; message: string }[] };
    },
    exporterUrl: '/api/acte-modeles/export',
    /** Les domaines de modèles ouverts au service technique (réglage RH). */
    briquesServiceTechnique: () =>
      apiFetch<string[]>('/api/acte-modeles/reglages/briques-service-technique'),
    setBriquesServiceTechnique: (briques: string[]) =>
      apiFetch<string[]>('/api/acte-modeles/reglages/briques-service-technique',
        { method: 'PUT', body: JSON.stringify({ briques }) }),
    /**
     * Le canevas rempli de ce qu'on sait, et la liste des champs restants.
     * Ne crée rien : préparer n'est pas rédiger.
     */
    preparer: (id: string, source?: { sourceType: string; sourceId: string }) =>
      apiFetch<{
        modele: { id: string; code: string; libelle: string; metadonnees: Record<string, string> };
        corps: string;
        valeurs: Record<string, string>;
        manquantes: string[];
        /** Les mêmes clés, typées : un champ de saisie par trou. */
        champs: {
          cle: string; libelle: string;
          type: 'texte' | 'texte_long' | 'date' | 'nombre' | 'liste';
          source?: 'agents' | 'lieux' | 'tiers' | 'associations';
          aide?: string;
        }[];
        circuit: { id: string; role: string; nom: string; email: string | null; commun: boolean }[];
      }>(`/api/acte-modeles/${id}/preparer`, { method: 'POST', body: JSON.stringify(source ?? {}) }),
  },
  // Le registre UNIQUE des actes (mig 259). Toutes briques, numérotation continue.
  /** Relecture et validation des actes (mig 323-325, recette du 28/09). */
  relecture: {
    // Côté VALIDEUR (compte VPC désigné) — accès limité à SES validations.
    mesValidations: () => apiFetch<{ items: Array<{ intervenantId: string; acteId: string; role: 'relecteur' | 'valideur'; numero: string; titre: string;
      statut: string; solliciteAt: string | null; responduAt: string | null; avis: string | null; commentaire: string | null }> }>('/api/actes/mes-relectures'),
    acte: (intervenantId: string) => apiFetch<{ acteId: string; numero: string; statut: string; role: string; dejaRepondu: boolean; peutCorriger: boolean;
      titre: string; objet: string | null; considerants: string[]; articles: Array<{ numero: number; texte: string }>; corps: string | null;
      effetDu: string | null; effetAu: string | null }>(`/api/actes/intervenants/${intervenantId}/acte`),
    telechargerPdf: (intervenantId: string, numero: string) => downloadCompta(`/api/actes/intervenants/${intervenantId}/document`, `arrete-${numero}.pdf`),
    avis: (intervenantId: string, avis: 'favorable' | 'reserves' | 'defavorable', commentaire?: string | null) =>
      apiFetch<ActeIntervenantVue[]>(`/api/actes/intervenants/${intervenantId}/avis`, { method: 'POST', body: JSON.stringify({ avis, commentaire }) }),
    corrigerEtValider: (intervenantId: string, body: { titre?: string; objet?: string | null; considerants?: string[];
      articles?: Array<{ numero: number; texte: string }>; corps?: string | null; effetDu?: string | null; effetAu?: string | null; commentaire: string }) =>
      apiFetch<ActeIntervenantVue[]>(`/api/actes/intervenants/${intervenantId}/corriger-et-valider`, { method: 'POST', body: JSON.stringify(body) }),
    // Côté RÉDACTEUR (fiche de l'arrêté).
    intervenants: (acteId: string) => apiFetch<ActeIntervenantVue[]>(`/api/actes/${acteId}/intervenants`),
    designer: (acteId: string, body: { userIds?: string[]; relecteurIds?: string[] }) =>
      apiFetch<ActeIntervenantVue[]>(`/api/actes/${acteId}/relecteurs`, { method: 'POST', body: JSON.stringify(body) }),
    retirer: (intervenantId: string) => apiFetch<ActeIntervenantVue[]>(`/api/actes/intervenants/${intervenantId}`, { method: 'DELETE' }),
    envoyer: (acteId: string) => apiFetch<{ sollicites: number; externes?: number }>(`/api/actes/${acteId}/envoyer-relecture`, { method: 'POST' }),
    avisRecu: (intervenantId: string, avis: 'favorable' | 'reserves' | 'defavorable', commentaire?: string | null) =>
      apiFetch<ActeIntervenantVue[]>(`/api/actes/intervenants/${intervenantId}/avis-recu`, { method: 'POST', body: JSON.stringify({ avis, commentaire }) }),
    corrections: (acteId: string) => apiFetch<Array<{ id: string; auteur: string; commentaire: string; le: string;
      champs: Array<{ champ: string; libelle: string; avant: unknown; apres: unknown }> }>>(`/api/actes/${acteId}/corrections`),
  },
  actes: {
    list: (f: { statut?: string; brique?: string; annee?: number; q?: string } = {}) => {
      const p = new URLSearchParams();
      if (f.statut) p.set('statut', f.statut);
      if (f.brique) p.set('brique', f.brique);
      if (f.annee) p.set('annee', String(f.annee));
      if (f.q) p.set('q', f.q);
      const qs = p.toString();
      return apiFetch<ActeRegistre[]>(`/api/actes${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => apiFetch<ActeRegistre>(`/api/actes/${id}`),
    statistiques: (brique?: string) => apiFetch<StatistiquesRegistre>(
      `/api/actes/statistiques${brique ? `?brique=${encodeURIComponent(brique)}` : ''}`),
    creer: (body: {
      titre: string; objet?: string | null; brique?: string | null;
      modeleId?: string | null; corps?: string | null;
      considerants?: string[]; articles?: { numero: string; texte: string }[];
      effetDu?: string | null; effetAu?: string | null;
      sourceType?: string | null; sourceId?: string | null;
    }) => apiFetch<ActeRegistre>('/api/actes', { method: 'POST', body: JSON.stringify(body) }),
    /**
     * D4-02 — quelle délibération cet acte vise-t-il.
     *
     * `null` quand l'acte ne s'appuie sur aucune : ce n'est pas une erreur, la
     * plupart des arrêtés n'en visent pas.
     */
    deliberation: (id: string) =>
      apiFetch<{ deliberation: {
        id: string; numero: string; objet: string; dateSeance: string | null;
      } | null }>(`/api/actes/${id}/deliberation`),
    /** Refusé sur un acte publié : le registre garde ce qui est sorti. */
    supprimer: (id: string) => apiFetch<void>(`/api/actes/${id}`, { method: 'DELETE' }),
    transition: (id: string, vers: StatutActe, commentaire?: string | null) =>
      apiFetch<ActeRegistre>(`/api/actes/${id}/transition`,
        { method: 'POST', body: JSON.stringify({ vers, commentaire }) }),
    pdfBlob: async (id: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/actes/${id}/pdf`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
  },
  // Vue maire consolidée (pilotage agrégé tous services — admin/maire)
  comptabilite: {
    planning: {
      presenceSemaine: (weekStart: string) =>
        apiFetch<PlanningWeekResponse>(`/api/comptabilite/planning/presence-semaine?weekStart=${weekStart}`),
      presenceAnnee: (annee: number, agentId?: string) =>
        apiFetch<PlanningAnneeResponse>(`/api/comptabilite/planning/presence-annee?annee=${annee}${agentId ? `&agentId=${agentId}` : ''}`),
      listAgents: () => apiFetch<{ items: { id: string; fullName: string; role: string; couleurMarker: string | null }[] }>('/api/comptabilite/planning/agents'),
      setAgentCouleur: (agentUserId: string, couleurMarker: string | null) =>
        apiFetch<void>(`/api/comptabilite/planning/agents/${agentUserId}/couleur`, { method: 'PATCH', body: JSON.stringify({ couleurMarker }) }),
      joursNonTravailles: (from: string, to: string) =>
        apiFetch<{ date: string; libelle: string }[]>(`/api/comptabilite/planning/jours-non-travailles?from=${from}&to=${to}`),
      listProfils: () => apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }[]>('/api/comptabilite/planning/profils'),
      createProfil: (body: { nom: string; plages: ProfilPlage[] }) =>
        apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>('/api/comptabilite/planning/profils', { method: 'POST', body: JSON.stringify(body) }),
      updateProfil: (id: string, body: { nom: string; plages: ProfilPlage[] }) =>
        apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>(`/api/comptabilite/planning/profils/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteProfil: (id: string) => apiFetch<void>(`/api/comptabilite/planning/profils/${id}`, { method: 'DELETE' }),
      generer: (body: { profilId: string; agentIds: string[]; from: string; to: string; inclureVacances?: boolean }) =>
        apiFetch<{ creneauxCrees: number; joursIgnores: number }>('/api/comptabilite/planning/generer', { method: 'POST', body: JSON.stringify(body) }),
      transmettrePaie: (from: string, to: string) =>
        apiFetch<{ transmis: number }>('/api/comptabilite/planning/transmettre-paie', { method: 'POST', body: JSON.stringify({ from, to }) }),
      // Grille unique (livraison B) — créneaux + postes glissables.
      getWeek: (from: string, to: string) =>
        apiFetch<{ creneaux: SimpleGrilleCreneau[] }>(`/api/comptabilite/planning?from=${from}&to=${to}`).then((r) => r.creneaux),
      getMaSemaine: (from: string, to: string) =>
        apiFetch<{ creneaux: SimpleGrilleCreneau[] }>(`/api/comptabilite/planning/ma-semaine?from=${from}&to=${to}`).then((r) => r.creneaux),
      createCreneau: (body: SimpleCreneauInput) =>
        apiFetch<SimpleGrilleCreneau>('/api/comptabilite/planning/creneaux', { method: 'POST', body: JSON.stringify(body) }),
      updateCreneau: (id: string, body: Partial<SimpleCreneauInput>) =>
        apiFetch<SimpleGrilleCreneau>(`/api/comptabilite/planning/creneaux/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      updateStatut: (id: string, statut: 'planifie' | 'absent') =>
        apiFetch<SimpleGrilleCreneau>(`/api/comptabilite/planning/creneaux/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
      deleteCreneau: (id: string) => apiFetch<void>(`/api/comptabilite/planning/creneaux/${id}`, { method: 'DELETE' }),
      listPostes: () => apiFetch<SimplePoste[]>('/api/comptabilite/planning/postes'),
      createPoste: (body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<SimplePoste>('/api/comptabilite/planning/postes', { method: 'POST', body: JSON.stringify(body) }),
      updatePoste: (id: string, body: { titre?: string; dureeMin?: number; couleur?: string }) =>
        apiFetch<SimplePoste>(`/api/comptabilite/planning/postes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deletePoste: (id: string) => apiFetch<void>(`/api/comptabilite/planning/postes/${id}`, { method: 'DELETE' }),
    },
    dashboard: () => apiFetch<ComptaDashboardData>('/api/comptabilite/dashboard'),
    facturesManuelles: {
      templates: () => apiFetch<{ templates: FactureTemplate[] }>('/api/comptabilite/factures-manuelles/templates'),
      list: () => apiFetch<{ factures: FactureManuelle[] }>('/api/comptabilite/factures-manuelles'),
      tiersRecherche: (q: string) =>
        apiFetch<{ tiers: TiersRecherche[] }>(`/api/comptabilite/factures-manuelles/tiers-recherche?q=${encodeURIComponent(q)}`),
      aEncaisser: (q?: string) => apiFetch<{ factures: FactureAEncaisser[] }>(`/api/comptabilite/factures-a-encaisser${q ? `?q=${encodeURIComponent(q)}` : ''}`),
      creer: (body: CreerFactureManuelleBody) =>
        apiFetch<FactureManuelle>('/api/comptabilite/factures-manuelles', { method: 'POST', body: JSON.stringify(body) }),
      telechargerExcel: async (id: string, numero: string): Promise<void> => {
        await downloadCompta(`/api/comptabilite/factures-manuelles/${id}/excel`, `${numero}.xlsx`);
      },
      telechargerPdf: async (id: string, numero: string): Promise<void> => {
        await downloadCompta(`/api/comptabilite/factures-manuelles/${id}/pdf`, `${numero}.pdf`);
      },
      telechargerFacturX: async (id: string, numero: string): Promise<void> => {
        await downloadCompta(`/api/comptabilite/factures-manuelles/${id}/facturx`, `Factur-X_${numero}.pdf`);
      },
      telechargerPes: async (id: string, numero: string): Promise<void> => {
        await downloadCompta(`/api/comptabilite/factures-manuelles/${id}/pes`, `PES_Aller_${numero}.xml`);
      },
      envoyerEmail: (id: string) => apiFetch<{ email?: { sent: boolean; reason?: string }; efacture?: (EfactureResultat | null); efactureErreur?: string }>(`/api/comptabilite/factures-manuelles/${id}/envoyer`, { method: 'POST' }),
      efactureStatut: (id: string) => apiFetch<{ canal: string | null; statut: string; identifiant: string | null; message: string | null; transmiseAt: string | null }>(`/api/comptabilite/factures-manuelles/${id}/efacture`),
      payfip: (id: string) => apiFetch<{ url: string; idop: string; mock: boolean }>(`/api/comptabilite/factures-manuelles/${id}/payfip`, { method: 'POST' }),
      marquerPaye: (id: string, body: { moyen?: string; reference?: string | null; commentaire?: string | null }) =>
        apiFetch<{ ok: true; encaissementId: string }>(`/api/comptabilite/factures-manuelles/${id}/marquer-paye`, { method: 'POST', body: JSON.stringify(body) }),
    },
    // Factures fournisseurs REÇUES (Chorus — la commune destinataire)
    facturesRecues: {
      sync: () => apiFetch<{ nouvelles: number; total: number; source: string }>('/api/comptabilite/factures-recues/sync', { method: 'POST' }),
      list: (statut?: string) =>
        apiFetch<FactureRecue[]>(`/api/comptabilite/factures-recues${statut ? `?statut=${encodeURIComponent(statut)}` : ''}`),
      valider: (id: string) => apiFetch<{ ok: true }>(`/api/comptabilite/factures-recues/${id}/valider`, { method: 'POST' }),
      rejeter: (id: string, motif: string) =>
        apiFetch<{ ok: true }>(`/api/comptabilite/factures-recues/${id}/rejeter`, { method: 'POST', body: JSON.stringify({ motif }) }),
      mettreEnPaiement: (id: string) => apiFetch<{ ok: true }>(`/api/comptabilite/factures-recues/${id}/mettre-en-paiement`, { method: 'POST' }),
      telechargerPdf: async (id: string) => { await downloadCompta(`/api/comptabilite/factures-recues/${id}/pdf`, `facture-recue-${id}.pdf`); },
    },
    // LOT 3 — Gestion des modèles de facture (CRUD)
    templatesFactures: {
      list: () => apiFetch<{ templates: FactureTemplate[] }>('/api/comptabilite/factures-templates'),
      create: (body: TemplateFactureBody) =>
        apiFetch<FactureTemplate>('/api/comptabilite/factures-templates', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: TemplateFactureBody) =>
        apiFetch<FactureTemplate>(`/api/comptabilite/factures-templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      remove: (id: string) => apiFetch<{ ok: true }>(`/api/comptabilite/factures-templates/${id}`, { method: 'DELETE' }),
    },
    // LOT 4 — Décaissements (dépenses)
    decaissements: {
      list: (du?: string, au?: string) => {
        const q = new URLSearchParams();
        if (du) q.set('du', du);
        if (au) q.set('au', au);
        return apiFetch<{ decaissements: Decaissement[] }>(`/api/comptabilite/decaissements${q.toString() ? '?' + q.toString() : ''}`);
      },
      create: (body: DecaissementBody) =>
        apiFetch<Decaissement>('/api/comptabilite/decaissements', { method: 'POST', body: JSON.stringify(body) }),
      remove: (id: string) => apiFetch<{ ok: true }>(`/api/comptabilite/decaissements/${id}`, { method: 'DELETE' }),
      // Factures émises à payer (sens=dépense) — pour le sélecteur de dépense (mig. 177)
      facturesAPayer: (q?: string) =>
        apiFetch<{ factures: FactureAEncaisser[] }>(`/api/comptabilite/factures-a-payer${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    },
    // LOT 5 — Livre de comptabilité unifié
    livre: {
      get: (du?: string, au?: string) => {
        const q = new URLSearchParams();
        if (du) q.set('du', du);
        if (au) q.set('au', au);
        return apiFetch<LivreResult>(`/api/comptabilite/livre${q.toString() ? '?' + q.toString() : ''}`);
      },
      exportCsv: async (du?: string, au?: string): Promise<void> => {
        const q = new URLSearchParams();
        if (du) q.set('du', du);
        if (au) q.set('au', au);
        await downloadCompta(`/api/comptabilite/livre/export.csv${q.toString() ? '?' + q.toString() : ''}`, 'livre-comptabilite.csv');
      },
    },
    postes: {
      list: () => apiFetch<{ postes: ComptaPoste[] }>('/api/comptabilite/postes'),
      create: (body: { libelle: string; ordre?: number }) =>
        apiFetch<ComptaPoste>('/api/comptabilite/postes', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<ComptaPoste>(`/api/comptabilite/postes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },
    encaissements: {
      list: (filtre?: { aDeposer?: boolean; depose?: boolean; statut?: 'provisoire' | 'definitif' }) => {
        const q = new URLSearchParams();
        if (filtre?.aDeposer) q.set('aDeposer', 'true');
        if (filtre?.depose !== undefined) q.set('depose', String(filtre.depose));
        if (filtre?.statut) q.set('statut', filtre.statut);
        const qs = q.toString();
        return apiFetch<{ encaissements: ComptaEncaissement[] }>(`/api/comptabilite/encaissements${qs ? '?' + qs : ''}`);
      },
      valider: (id: string) => apiFetch<{ ok: true }>(`/api/comptabilite/encaissements/${id}/valider`, { method: 'POST' }),
      create: (body: {
        objet: string; montant: number; moyen: string; reference?: string | null;
        source?: string; commentaire?: string | null; pieceJointeUrl?: string | null;
        cautionReservationId?: string | null; factureId?: string | null;
        ventilation?: { posteId: string; montant: number }[];
      }) => apiFetch<ComptaEncaissement>('/api/comptabilite/encaissements', { method: 'POST', body: JSON.stringify(body) }),
      ventiler: (id: string, ventilation: { posteId: string; montant: number }[]) =>
        apiFetch<ComptaEncaissement>(`/api/comptabilite/encaissements/${id}/ventilation`, {
          method: 'PATCH', body: JSON.stringify({ ventilation }),
        }),
      rapprocher: (id: string, rapproche: boolean) =>
        apiFetch<void>(`/api/comptabilite/encaissements/${id}/rapproche`, {
          method: 'PATCH', body: JSON.stringify({ rapproche }),
        }),
    },
    depots: {
      list: () => apiFetch<{ depots: ComptaDepot[] }>('/api/comptabilite/depots'),
      create: (encaissementIds: string[]) =>
        apiFetch<ComptaDepot>('/api/comptabilite/depots', { method: 'POST', body: JSON.stringify({ encaissementIds }) }),
      bordereauBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/comptabilite/depots/${id}/bordereau.pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        return res.blob();
      },
    },
    journal: {
      list: (filtre?: { du?: string; au?: string; rapproche?: boolean }) => {
        const q = new URLSearchParams();
        if (filtre?.du) q.set('du', filtre.du);
        if (filtre?.au) q.set('au', filtre.au);
        if (filtre?.rapproche !== undefined) q.set('rapproche', String(filtre.rapproche));
        const qs = q.toString();
        return apiFetch<ComptaJournalResult>(`/api/comptabilite/journal${qs ? '?' + qs : ''}`);
      },
      csvBlob: async (filtre?: { du?: string; au?: string }): Promise<Blob> => {
        const token = getAccessToken();
        const q = new URLSearchParams();
        if (filtre?.du) q.set('du', filtre.du);
        if (filtre?.au) q.set('au', filtre.au);
        const res = await fetch(`/api/comptabilite/journal/export.csv${q.toString() ? '?' + q.toString() : ''}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'CSV_FETCH_FAILED' });
        return res.blob();
      },
      pdfBlob: async (filtre?: { du?: string; au?: string }): Promise<Blob> => {
        const token = getAccessToken();
        const q = new URLSearchParams();
        if (filtre?.du) q.set('du', filtre.du);
        if (filtre?.au) q.set('au', filtre.au);
        const res = await fetch(`/api/comptabilite/journal/export.pdf${q.toString() ? '?' + q.toString() : ''}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        return res.blob();
      },
      rapprocher: (id: string, rapproche: boolean) =>
        apiFetch<void>(`/api/comptabilite/encaissements/${id}/rapproche`, {
          method: 'PATCH', body: JSON.stringify({ rapproche }),
        }),
    },
  },
  petiteEnfance: {
    dashboard: () => apiFetch<PeDashboardData>('/api/petite-enfance/dashboard'),
    structures: {
      list: () => apiFetch<{ structures: PeStructure[] }>('/api/petite-enfance/structures'),
      create: (body: {
        nom: string; type?: PeStructureType; capacite?: number;
        amplitudeDebut?: string | null; amplitudeFin?: string | null;
      }) => apiFetch<PeStructure>('/api/petite-enfance/structures', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<PeStructure>(`/api/petite-enfance/structures/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },
    dossiers: {
      list: () => apiFetch<{ dossiers: PeDossier[] }>('/api/petite-enfance/dossiers'),
      upsert: (body: {
        enfantId: string; medecinNom?: string | null; medecinTel?: string | null;
        vaccinationsAJour?: boolean; paiDetail?: string | null; marche?: boolean;
        autorisePhoto?: boolean; autoriseSortie?: boolean;
        personnesAutorisees?: string | null; notes?: string | null;
      }) => apiFetch<PeDossier>('/api/petite-enfance/dossiers', { method: 'POST', body: JSON.stringify(body) }),
      /** Enfants connus de la mairie sans dossier PE (< 6 ans), pour la reprise. */
      enfantsDisponibles: () => apiFetch<{ enfants: PeEnfantDisponible[] }>('/api/petite-enfance/enfants-disponibles'),
      importCsv: (contenu: string) =>
        apiFetch<PeImportResult>('/api/petite-enfance/import-csv', { method: 'POST', body: JSON.stringify({ contenu }) }),
      exportBlob: async (format: 'csv' | 'pdf'): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/petite-enfance/export?format=${format}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'EXPORT_FAILED' });
        }
        return res.blob();
      },
    },
    contrats: {
      list: (filtre?: { structureId?: string; statut?: PeContratStatut }) => {
        const q = new URLSearchParams();
        if (filtre?.structureId) q.set('structureId', filtre.structureId);
        if (filtre?.statut) q.set('statut', filtre.statut);
        const qs = q.toString();
        return apiFetch<{ contrats: PeContrat[] }>(`/api/petite-enfance/contrats${qs ? '?' + qs : ''}`);
      },
      create: (body: {
        enfantId: string; structureId: string; type?: PeContratType;
        dateDebut: string; dateFin?: string | null; joursSemaine?: number[];
        heureDebut?: string | null; heureFin?: string | null;
        heuresMensualisees?: number; tarifHoraireEuros?: number; notes?: string | null;
      }) => apiFetch<PeContrat>('/api/petite-enfance/contrats', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<PeContrat>(`/api/petite-enfance/contrats/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    },
    presences: {
      list: (structureId: string, date: string) =>
        apiFetch<{ presences: PePresence[] }>(`/api/petite-enfance/presences?structureId=${structureId}&date=${date}`),
      feuillePdfBlob: async (structureId: string, date: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/petite-enfance/presences/pdf?structureId=${structureId}&date=${date}&download=1`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new ApiException(res.status, { error: 'PDF indisponible' });
        return res.blob();
      },
      upsert: (body: {
        enfantId: string; structureId: string; contratId?: string | null;
        dateJour: string; arrivee?: string | null; depart?: string | null; absent?: boolean;
      }) => apiFetch<PePresence>('/api/petite-enfance/presences', { method: 'POST', body: JSON.stringify(body) }),
    },
    encadrement: {
      get: (structureId: string, date: string) =>
        apiFetch<PeEncadrementJour>(`/api/petite-enfance/encadrement?structureId=${structureId}&date=${date}`),
    },
    planning: {
      list: (from: string, to: string, structureId?: string) => {
        const q = new URLSearchParams({ from, to });
        if (structureId) q.set('structureId', structureId);
        return apiFetch<{ creneaux: PePlanningCreneau[] }>(`/api/petite-enfance/planning?${q.toString()}`);
      },
      create: (body: {
        agentUserId: string; structureId: string; dateDebut: string; dateFin: string;
        titre?: string; type?: string; couleurMarker?: string | null;
      }) => apiFetch<PePlanningCreneau>('/api/petite-enfance/planning', { method: 'POST', body: JSON.stringify(body) }),
      setStatut: (id: string, statut: PePlanningStatut) =>
        apiFetch<PePlanningCreneau>(`/api/petite-enfance/planning/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
      update: (id: string, body: { structureId?: string; dateDebut?: string; dateFin?: string; titre?: string }) =>
        apiFetch<PePlanningCreneau>(`/api/petite-enfance/planning/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<void>(`/api/petite-enfance/planning/${id}`, { method: 'DELETE' }),
      // Présence unifiée (équipe/personne/année) — même format que les autres briques
      presenceSemaine: (weekStart: string) =>
        apiFetch<PlanningWeekResponse>(`/api/petite-enfance/planning/presence-semaine?weekStart=${weekStart}`),
      presenceAnnee: (annee: number, agentId?: string) =>
        apiFetch<PlanningAnneeResponse>(`/api/petite-enfance/planning/presence-annee?annee=${annee}${agentId ? `&agentId=${agentId}` : ''}`),
      listAgents: () => apiFetch<{ items: { id: string; fullName: string; role: string; couleurMarker: string | null }[] }>('/api/petite-enfance/planning/agents'),
      setAgentCouleur: (agentUserId: string, couleurMarker: string | null) =>
        apiFetch<void>(`/api/petite-enfance/planning/agents/${agentUserId}/couleur`, { method: 'PATCH', body: JSON.stringify({ couleurMarker }) }),
      joursNonTravailles: (from: string, to: string) =>
        apiFetch<{ date: string; libelle: string }[]>(`/api/petite-enfance/planning/jours-non-travailles?from=${from}&to=${to}`),
      // Profils + génération + transmission paie (comme scolaire/GRC)
      listProfils: () => apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }[]>('/api/petite-enfance/planning/profils'),
      createProfil: (body: { nom: string; type?: string; plages: ProfilPlage[] }) =>
        apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>('/api/petite-enfance/planning/profils', { method: 'POST', body: JSON.stringify(body) }),
      updateProfil: (id: string, body: { nom: string; type?: string; plages: ProfilPlage[] }) =>
        apiFetch<{ id: string; nom: string; type: string; plages: ProfilPlage[] }>(`/api/petite-enfance/planning/profils/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteProfil: (id: string) => apiFetch<void>(`/api/petite-enfance/planning/profils/${id}`, { method: 'DELETE' }),
      generer: (body: { profilId: string; structureId: string; agentIds: string[]; from: string; to: string; inclureVacances?: boolean }) =>
        apiFetch<{ creneauxCrees: number; joursIgnores: number }>('/api/petite-enfance/planning/generer', { method: 'POST', body: JSON.stringify(body) }),
      transmettrePaie: (from: string, to: string) =>
        apiFetch<{ transmis: number }>('/api/petite-enfance/planning/transmettre-paie', { method: 'POST', body: JSON.stringify({ from, to }) }),
      listPostes: () => apiFetch<Array<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>>('/api/petite-enfance/planning/postes'),
      createPoste: (body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>('/api/petite-enfance/planning/postes', { method: 'POST', body: JSON.stringify(body) }),
      updatePoste: (id: string, body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>(`/api/petite-enfance/planning/postes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deletePoste: (id: string) => apiFetch<void>(`/api/petite-enfance/planning/postes/${id}`, { method: 'DELETE' }),
    },
    facturation: {
      parametres: () => apiFetch<PeParametres>('/api/petite-enfance/parametres'),
      setParametres: (body: {
        psuEnabled?: boolean; tarifHoraireDefautEuros?: number;
        facturationUniteMinutes?: 15 | 30 | 60; facturationArrondi?: 'sup' | 'proche';
        amplitudeDebut?: string | null; amplitudeFin?: string | null;
        absenceDelaiPrevenanceJours?: number;
        absenceTraitement?: 'facture' | 'non_facture' | 'non_facture_si_justificatif';
        absenceDelaiJustificatifJours?: number;
        autoFacturationActive?: boolean; autoFacturationJour?: number;
        autoFacturationHeure?: number; autoFacturationPeriode?: 'mois_ecoule' | 'mois_en_cours';
        autoFacturationEnvoiEmail?: boolean;
      }) =>
        apiFetch<PeParametres>('/api/petite-enfance/parametres', { method: 'PUT', body: JSON.stringify(body) }),
      baremePsu: (annee?: number) =>
        apiFetch<{ bareme: PeBaremePsuLigne[] }>(`/api/petite-enfance/bareme-psu${annee ? '?annee=' + annee : ''}`),
      setBaremePsu: (body: { annee: number; nbEnfants: number; tauxEffortPct: number; plancherEuros?: number; plafondEuros?: number }) =>
        apiFetch<void>('/api/petite-enfance/bareme-psu', { method: 'PUT', body: JSON.stringify(body) }),
      apercu: (structureId: string, periode: string) =>
        apiFetch<PeFactureApercu>(`/api/petite-enfance/facturation/apercu?structureId=${structureId}&periode=${periode}`),
      emettre: (structureId: string, periode: string) =>
        apiFetch<{ creees: number; total: number; factures: { numero: string; familleId: string; montantEuros: number }[] }>(
          '/api/petite-enfance/facturation/emettre', { method: 'POST', body: JSON.stringify({ structureId, periode }) }),
      factures: () => apiFetch<{ factures: PeFacture[] }>('/api/petite-enfance/factures'),
    },
    calendrier: {
      list: (structureId: string, debut: string, fin: string) =>
        apiFetch<{ jours: PeJourOuverture[] }>(`/api/petite-enfance/calendrier?structureId=${structureId}&debut=${debut}&fin=${fin}`),
      set: (body: { structureId: string; date: string; ouvert: boolean; motif?: string | null }) =>
        apiFetch<PeJourOuverture>('/api/petite-enfance/calendrier', { method: 'PUT', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<void>(`/api/petite-enfance/calendrier/${id}`, { method: 'DELETE' }),
    },
    ram: {
      assmat: () => apiFetch<{ assmat: PeAssmat[] }>('/api/petite-enfance/assmat'),
      annuaire: () => apiFetch<{ assmat: PeAssmat[] }>('/api/petite-enfance/annuaire'),
      createAssmat: (body: Record<string, unknown>) =>
        apiFetch<PeAssmat>('/api/petite-enfance/assmat', { method: 'POST', body: JSON.stringify(body) }),
      updateAssmat: (id: string, body: Record<string, unknown>) =>
        apiFetch<PeAssmat>(`/api/petite-enfance/assmat/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      ateliers: () => apiFetch<{ ateliers: PeAtelier[] }>('/api/petite-enfance/ateliers'),
      createAtelier: (body: Record<string, unknown>) =>
        apiFetch<PeAtelier>('/api/petite-enfance/ateliers', { method: 'POST', body: JSON.stringify(body) }),
      setAtelierStatut: (id: string, statut: 'ouvert' | 'complet' | 'annule' | 'termine') =>
        apiFetch<PeAtelier>(`/api/petite-enfance/ateliers/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
      inscriptions: (atelierId: string) =>
        apiFetch<{ inscriptions: PeAtelierInscription[] }>(`/api/petite-enfance/ateliers/${atelierId}/inscriptions`),
      inscrire: (atelierId: string, body: { assmatId?: string | null; enfantNom?: string | null; nbEnfants?: number }) =>
        apiFetch<PeAtelierInscription>(`/api/petite-enfance/ateliers/${atelierId}/inscriptions`, { method: 'POST', body: JSON.stringify(body) }),
      desinscrire: (inscriptionId: string) =>
        apiFetch<void>(`/api/petite-enfance/inscriptions/${inscriptionId}`, { method: 'DELETE' }),
    },
    preinscriptions: {
      list: (filtre?: { statut?: PePreinscStatut; structureId?: string }) => {
        const q = new URLSearchParams();
        if (filtre?.statut) q.set('statut', filtre.statut);
        if (filtre?.structureId) q.set('structureId', filtre.structureId);
        const qs = q.toString();
        return apiFetch<{ preinscriptions: PePreinscription[] }>(`/api/petite-enfance/preinscriptions${qs ? '?' + qs : ''}`);
      },
      create: (body: Record<string, unknown>) =>
        apiFetch<PePreinscription>('/api/petite-enfance/preinscriptions', { method: 'POST', body: JSON.stringify(body) }),
      setStatut: (id: string, statut: PePreinscStatut, commentaire?: string | null) =>
        apiFetch<PePreinscription>(`/api/petite-enfance/preinscriptions/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut, commentaire }) }),
      attribuer: (id: string, body: { structureId?: string; dateDebut: string; tarifHoraireEuros?: number; heuresMensualisees?: number; motif?: string | null }) =>
        apiFetch<{ preinscription: PePreinscription; contratId: string }>(`/api/petite-enfance/preinscriptions/${id}/attribuer`, { method: 'POST', body: JSON.stringify(body) }),
    },
    espace: {
      get: () => apiFetch<PeEspaceParent>('/api/petite-enfance/espace'),
      deposerPreinscription: (body: { enfantPrenom?: string | null; enfantNom?: string | null; dateNaissance?: string | null; ne?: boolean; structureId?: string | null; dateAccueilSouhaitee?: string | null; typeSouhaite?: PeContratType; commentaire?: string | null; heuresSouhaitees?: number | null }) =>
        apiFetch<PePreinscription>('/api/petite-enfance/espace/preinscriptions', { method: 'POST', body: JSON.stringify(body) }),
    },
    factureBlob: async (id: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/petite-enfance/factures/${id}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
    factureBlobParent: async (id: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/petite-enfance/espace/factures/${id}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
    factureEmail: (id: string) =>
      apiFetch<{ sent: boolean; reason?: string }>(`/api/petite-enfance/factures/${id}/email`, { method: 'POST' }),
  },
  dashboard: {
    consolide: () => apiFetch<DashboardConsolide>('/api/dashboard/consolide'),
    detail: (module: string) => apiFetch<DashboardDetail>(`/api/dashboard/detail/${module}`),
    kpiDetail: (module: string, key: string) =>
      apiFetch<KpiDetail>(`/api/dashboard/detail/${module}/kpi/${key}`),
    getModules: () => apiFetch<{ modules: Record<string, boolean> }>('/api/dashboard/modules'),
    setModules: (modules: Record<string, boolean>) =>
      apiFetch<{ modules: Record<string, boolean> }>('/api/dashboard/modules', {
        method: 'PUT',
        body: JSON.stringify({ modules }),
      }),
    /** Aperçu JSON du rapport conseil (pour un futur écran d'aperçu). */
    exportConseilData: (params: { debut: string; fin: string; modules: string[]; comparaison: string }) =>
      apiFetch<ExportConseilData>(
        `/api/dashboard/export-conseil?debut=${params.debut}&fin=${params.fin}&modules=${params.modules.join(',')}&comparaison=${params.comparaison}`
      ),
    /** Télécharge le rapport conseil en PDF ou Excel. */
    exportConseilDownload: async (
      format: 'pdf' | 'xlsx',
      params: { debut: string; fin: string; modules: string[]; comparaison: string }
    ): Promise<void> => {
      const token = getAccessToken();
      const qs = `debut=${params.debut}&fin=${params.fin}&modules=${params.modules.join(',')}&comparaison=${params.comparaison}`;
      const res = await fetch(`/api/dashboard/export-conseil/${format}?${qs}`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) {
        throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Export impossible' })));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') ?? '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? `rapport-conseil-${params.fin}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
  // L13.18 — messagerie interne mairie (staff-to-staff)
  messagerie: {
    carnet: () =>
      apiFetch<{ items: CarnetEntry[] }>('/api/messagerie/carnet'),

    /** Boîte de réception — filtrable par statut, archives séparées, paginée. */
    inbox: (opts: {
      statut?: 'en_cours' | 'traite';
      archives?: boolean;
      limit?: number;
      offset?: number;
    } = {}) => {
      const q = new URLSearchParams();
      if (opts.statut) q.set('statut', opts.statut);
      if (opts.archives) q.set('archives', '1');
      if (opts.limit !== undefined) q.set('limit', String(opts.limit));
      if (opts.offset !== undefined) q.set('offset', String(opts.offset));
      const qs = q.toString();
      return apiFetch<PageMessages<InboxMessage>>(
        `/api/messagerie/inbox${qs ? `?${qs}` : ''}`);
    },

    /** Envoyés — ou brouillons, selon `statut`. */
    sent: (opts: {
      statut?: 'brouillon' | 'envoye';
      archives?: boolean;
      limit?: number;
      offset?: number;
    } = {}) => {
      const q = new URLSearchParams();
      if (opts.statut) q.set('statut', opts.statut);
      if (opts.archives) q.set('archives', '1');
      if (opts.limit !== undefined) q.set('limit', String(opts.limit));
      if (opts.offset !== undefined) q.set('offset', String(opts.offset));
      const qs = q.toString();
      return apiFetch<PageMessages<SentMessage>>(
        `/api/messagerie/sent${qs ? `?${qs}` : ''}`);
    },

    unreadCount: () =>
      apiFetch<{ count: number }>('/api/messagerie/unread-count'),
    getMessage: (id: string) =>
      apiFetch<MessageDetail>(`/api/messagerie/messages/${id}`),
    send: (body: {
      objet: string;
      corps: string;
      destinataireIds: string[];
      alerteId?: string | null;
      brouillon?: boolean;
    }) =>
      apiFetch<{ id: string }>('/api/messagerie/messages', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /** Le destinataire clôt sa part — jamais l'expéditeur. */
    traite: (id: string, traite: boolean) =>
      apiFetch<{ traite: boolean }>(`/api/messagerie/messages/${id}/traite`, {
        method: 'PATCH', body: JSON.stringify({ traite }),
      }),

    /** Chacun range sa propre vue. */
    archive: (id: string, archive: boolean) =>
      apiFetch<{ archive: boolean }>(`/api/messagerie/messages/${id}/archive`, {
        method: 'PATCH', body: JSON.stringify({ archive }),
      }),

    /** Groupes (item 8.4) — création réservée à l'encadrement. */
    groupes: {
      list: (tous = false) =>
        apiFetch<{ groupes: MessagerieGroupe[] }>(
          `/api/messagerie/groupes${tous ? '?tous=1' : ''}`),
      get: (id: string) =>
        apiFetch<MessagerieGroupeDetail>(`/api/messagerie/groupes/${id}`),
      creer: (body: { nom: string; description?: string; membreIds?: string[] }) =>
        apiFetch<{ id: string }>('/api/messagerie/groupes', {
          method: 'POST', body: JSON.stringify(body),
        }),
      maj: (id: string, body: { nom?: string; description?: string | null; actif?: boolean }) =>
        apiFetch<void>(`/api/messagerie/groupes/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),
      ajouterMembres: (id: string, userIds: string[]) =>
        apiFetch<void>(`/api/messagerie/groupes/${id}/membres`, {
          method: 'POST', body: JSON.stringify({ userIds }),
        }),
      retirerMembre: (id: string, userId: string) =>
        apiFetch<void>(`/api/messagerie/groupes/${id}/membres/${userId}`, {
          method: 'DELETE',
        }),
      sujets: (id: string, opts: { limit?: number; offset?: number } = {}) => {
        const q = new URLSearchParams();
        if (opts.limit !== undefined) q.set('limit', String(opts.limit));
        if (opts.offset !== undefined) q.set('offset', String(opts.offset));
        const qs = q.toString();
        return apiFetch<PageMessages<MessagerieSujet>>(
          `/api/messagerie/groupes/${id}/sujets${qs ? `?${qs}` : ''}`);
      },
      poster: (id: string, body: { objet: string; corps: string }) =>
        apiFetch<{ id: string }>(`/api/messagerie/groupes/${id}/sujets`, {
          method: 'POST', body: JSON.stringify(body),
        }),
    },

    sujets: {
      fil: (id: string) => apiFetch<MessagerieFil>(`/api/messagerie/sujets/${id}`),
      repondre: (id: string, corps: string) =>
        apiFetch<{ id: string }>(`/api/messagerie/sujets/${id}/reponses`, {
          method: 'POST', body: JSON.stringify({ corps }),
        }),
    },

    brouillons: {
      maj: (id: string, body: {
        objet?: string; corps?: string;
        destinataireIds?: string[]; alerteId?: string | null;
      }) =>
        apiFetch<{ id: string }>(`/api/messagerie/brouillons/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),
      envoyer: (id: string) =>
        apiFetch<{ id: string }>(`/api/messagerie/brouillons/${id}/envoyer`, {
          method: 'POST',
        }),
      supprimer: (id: string) =>
        apiFetch<void>(`/api/messagerie/brouillons/${id}`, { method: 'DELETE' }),
    },
  },
  // Module Scolaire (socle familles/enfants + cantine)
  scolaire: {
    // ── Responsables : second parent (12.1) et suspension (12.2), mig 249 ──
    addResponsable: (body: {
      familleId: string; prenom?: string; nom?: string; email?: string;
      telephone?: string; lien?: string; principal?: boolean;
    }) => apiFetch<ScolaireResponsable>('/api/scolaire/responsables', {
      method: 'POST', body: JSON.stringify(body),
    }),
    bloquerResponsable: (id: string, data: { motif: string; dateDebut?: string; dateFin?: string }, piece: File) => {
      const fd = new FormData();
      fd.append('motif', data.motif);
      if (data.dateDebut) fd.append('dateDebut', data.dateDebut);
      if (data.dateFin) fd.append('dateFin', data.dateFin);
      fd.append('piece', piece);
      return apiFetch<ScolaireResponsable>(`/api/scolaire/responsables/${id}/blocage`, {
        method: 'POST', body: fd,
      });
    },
    leverBlocageResponsable: (id: string, motif: string) =>
      apiFetch<ScolaireResponsable>(
        `/api/scolaire/responsables/${id}/blocage?motif=${encodeURIComponent(motif)}`,
        { method: 'DELETE' }),
    historiqueBlocages: (id: string) =>
      apiFetch<{ blocages: Array<{ id: string; motif: string; dateDebut: string | null; dateFin: string | null; pieceNom: string | null; poseParNom: string | null; poseAt: string | null; leveParNom: string | null; leveAt: string | null; leveMotif: string | null }> }>(
        `/api/scolaire/responsables/${id}/blocages`),
    // ── Justificatifs d'absence (12.3) ──
    justificatifs: {
      deposer: (body: { cibleType: string; cibleId: string; motif: string }, piece: File) => {
        const fd = new FormData();
        fd.append('cibleType', body.cibleType);
        fd.append('cibleId', body.cibleId);
        fd.append('motif', body.motif);
        fd.append('piece', piece);
        return apiFetch<AbsenceJustificatif>('/api/scolaire/justificatifs', { method: 'POST', body: fd });
      },
      miens: () => apiFetch<{ justificatifs: AbsenceJustificatif[] }>('/api/scolaire/justificatifs/miens'),
      /** Les absences encore justifiables (recette 20/08) — le menu du dépôt famille. */
      justifiables: () => apiFetch<{ absences: Array<{
        cibleType: string; cibleId: string; dateService: string; personne: string;
      }> }>('/api/scolaire/justificatifs/justifiables'),
      aTraiter: (attenteSeuls = true) =>
        apiFetch<{ justificatifs: AbsenceJustificatif[] }>(`/api/scolaire/justificatifs${attenteSeuls ? '?attente=1' : ''}`),
      decider: (id: string, accepte: boolean, commentaire?: string) =>
        apiFetch<AbsenceJustificatif>(`/api/scolaire/justificatifs/${id}/decision`, {
          method: 'POST', body: JSON.stringify({ accepte, commentaire }),
        }),
      document: (id: string) =>
        apiFetch<{ url: string }>(`/api/scolaire/justificatifs/${id}/document`),
    },
    // ── Repas des adultes (12.5/12.6) : chacun se déclare lui-même ──
    adultes: {
      moi: () => apiFetch<{ convive: CantineConvive | null }>('/api/scolaire/cantine/adultes/moi'),
      inscrire: (body: {
        userId?: string | null; prenom?: string; nom?: string;
        categorie: CategorieConvive; joursSemaine?: number[];
        dateDebut?: string; dateFin?: string;
      }) => apiFetch<CantineConvive & { repasPoses: number }>('/api/scolaire/cantine/adultes/convives', {
        method: 'POST', body: JSON.stringify(body),
      }),
      convives: (tous = false) =>
        apiFetch<{ convives: CantineConvive[] }>(`/api/scolaire/cantine/adultes/convives${tous ? '?tous=1' : ''}`),
      radier: (id: string) =>
        apiFetch<void>(`/api/scolaire/cantine/adultes/convives/${id}`, { method: 'DELETE' }),
      repas: (conviveId: string, from: string, to: string) =>
        apiFetch<{ cutoffHours: number; joursOuverts: number; repas: CantineRepasAdulte[] }>(
          `/api/scolaire/cantine/adultes/convives/${conviveId}/repas?from=${from}&to=${to}`),
      basculer: (body: { conviveId: string; dateService: string; present: boolean }) =>
        apiFetch<CantineRepasAdulte>('/api/scolaire/cantine/adultes/repas', {
          method: 'POST', body: JSON.stringify(body),
        }),
      listeJour: (date: string) =>
        apiFetch<{ dateService: string; total: number; lignes: Array<{ repasId: string; conviveId: string; nomComplet: string; categorie: CategorieConvive; statut: string }> }>(
          `/api/scolaire/cantine/adultes/liste/${date}`),
      etatMensuel: (periode: string) =>
        apiFetch<CantineEtatAdultes>(`/api/scolaire/cantine/adultes/etat/${periode}`),
      pointer: (dateService: string, lignes: Array<{ repasId: string; statut: string }>) =>
        apiFetch<{ misAJour: number }>('/api/scolaire/cantine/adultes/pointage', {
          method: 'PATCH', body: JSON.stringify({ dateService, lignes }),
        }),
      transmettre: (periode: string, commentaire?: string) =>
        apiFetch<CantineEtatTransmis>('/api/scolaire/cantine/adultes/transmissions', {
          method: 'POST', body: JSON.stringify({ periode, commentaire }),
        }),
      transmissions: () =>
        apiFetch<{ etats: CantineEtatTransmis[] }>('/api/scolaire/cantine/adultes/transmissions'),
      retirerTransmission: (periode: string) =>
        apiFetch<void>(`/api/scolaire/cantine/adultes/transmissions/${periode}`, { method: 'DELETE' }),
    },
    listFamilles: () => apiFetch<ScolaireFamille[]>('/api/scolaire/familles'),
    // Effectifs du jour AGRÉGÉS (cantine + périscolaire + ALSH + petite enfance)
    effectifsJour: {
      get: (date: string) => apiFetch<EffectifsJourResult>(`/api/scolaire/effectifs-jour/${date}`),
      excel: async (date: string): Promise<void> => {
        await downloadCompta(`/api/scolaire/effectifs-jour/${date}/excel`, `effectifs-du-jour-${date}.xlsx`);
      },
    },
    getFamille: (id: string) => apiFetch<ScolaireFamilleDetail>(`/api/scolaire/familles/${id}`),
    // Fenêtre d'inscription (lot 3)
    statutInscription: () => apiFetch<InscriptionStatut>('/api/scolaire/inscription/statut'),
    getFenetreInscription: () => apiFetch<InscriptionFenetre>('/api/scolaire/inscription/fenetre'),
    setFenetreInscription: (body: { ouverture: string | null; limite: string | null }) =>
      apiFetch<InscriptionFenetre>('/api/scolaire/inscription/fenetre', { method: 'PUT', body: JSON.stringify(body) }),
    // Menus de cantine
    menus: {
      list: (from: string, to: string) => apiFetch<Menu[]>(`/api/scolaire/menus?from=${from}&to=${to}`),
      get: (date: string) => apiFetch<Menu | null>(`/api/scolaire/menus/${date}`),
      upsert: (date: string, body: MenuUpsertInput) =>
        apiFetch<Menu>(`/api/scolaire/menus/${date}`, { method: 'PUT', body: JSON.stringify(body) }),
      remove: (date: string) => apiFetch<void>(`/api/scolaire/menus/${date}`, { method: 'DELETE' }),
      /** Synchronisation des menus depuis HACCP / Plat Témoin. */
      sync: {
        getConfig: () => apiFetch<MenuSyncConfig>(`/api/scolaire/menus-sync/config`),
        setConfig: (body: MenuSyncConfigInput) =>
          apiFetch<MenuSyncConfig>(`/api/scolaire/menus-sync/config`, { method: 'PUT', body: JSON.stringify(body) }),
        test: () => apiFetch<{ ok: true; menusTrouves: number }>(`/api/scolaire/menus-sync/test`, { method: 'POST' }),
        import: (from: string, to: string) =>
          apiFetch<{ importes: number; ignoresManuels: number; periode: { from: string; to: string } }>(
            `/api/scolaire/menus-sync/import`, { method: 'POST', body: JSON.stringify({ from, to }) }),
        egalim: (from: string, to: string) =>
          apiFetch<{ configure: boolean; pctBio: number | null; pctDurable: number | null; conformeBio: boolean | null; conformeDurable: boolean | null }>(
            `/api/scolaire/menus-sync/egalim?from=${from}&to=${to}`),
      },
      /** Fetch authentifié du PDF de la semaine (lundi YYYY-MM-DD) → Blob.
       *  L'appelant fait URL.createObjectURL(blob) puis window.open / download. */
      pdfBlob: async (lundi: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/scolaire/menus/semaine/${lundi}/pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        }
        return res.blob();
      },
    },

    /** Facturation cantine / périscolaire (lot 5). */
    facturation: {
      // Grille tarifaire
      listTarifs: (service?: ServiceFacturable) =>
        apiFetch<RestaurationTarif[]>(`/api/scolaire/tarifs${service ? `?service=${service}` : ''}`),
      createTarif: (body: RestaurationTarifInput) =>
        apiFetch<RestaurationTarif>('/api/scolaire/tarifs', { method: 'POST', body: JSON.stringify(body) }),
      updateTarif: (id: string, body: RestaurationTarifInput) =>
        apiFetch<RestaurationTarif>(`/api/scolaire/tarifs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      deleteTarif: (id: string) =>
        apiFetch<void>(`/api/scolaire/tarifs/${id}`, { method: 'DELETE' }),
      // Aperçu + émission
      apercu: (familleId: string, service: ServiceFacturable, debut: string, fin: string) =>
        apiFetch<FacturationApercu>(`/api/scolaire/facturation/apercu/${familleId}?service=${service}&debut=${debut}&fin=${fin}`),
      emettre: (familleId: string, body: { service: ServiceFacturable; periodeDebut: string; periodeFin: string }) =>
        apiFetch<Facture>(`/api/scolaire/facturation/emettre/${familleId}`, { method: 'POST', body: JSON.stringify(body) }),
      emettreMulti: (familleId: string, body: { services: ServiceFacturable[]; periodeDebut: string; periodeFin: string }) =>
        apiFetch<Facture>(`/api/scolaire/facturation/emettre-multi/${familleId}`, { method: 'POST', body: JSON.stringify(body) }),
      // Rôle collectif ASAP ORMC : aperçu des factures émises d'un mois + export du rôle.
      roleAsapPreview: (mois: string) =>
        apiFetch<{ factures: Array<{ id: string; numero: string; familleNom: string; service: string; periodeDebut: string | null; periodeFin: string | null; montantTtc: number; statut: string }> }>(
          `/api/scolaire/facturation/role-asap?mois=${encodeURIComponent(mois)}`),
      roleAsapExport: async (mois: string, factureIds: string[]) => {
        const token = getAccessToken();
        const res = await fetch(`/api/scolaire/facturation/role-asap/export`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ mois, factureIds }),
        });
        if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: `HTTP ${res.status}`, code: 'EXPORT_FAILED' })));
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl; a.download = `ASAP_ORMC_${mois}_${factureIds.length}factures.xml`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(objectUrl);
      },
      executerAuto: () =>
        apiFetch<{ famillesTraitees: number; facturesEmises: number; erreurs: number; periode: { debut: string; fin: string }; emailsEnvoyes?: number }>(
          `/api/scolaire/facturation/auto/executer`, { method: 'POST' }),
      envoyerEmail: (factureId: string) =>
        apiFetch<{ sent: boolean; reason?: string }>(`/api/scolaire/grc/factures/${factureId}/envoyer-email`, { method: 'POST' }),
      // Factures
      listFactures: (filtre?: { familleId?: string; statut?: FactureStatut }) => {
        const qs = new URLSearchParams();
        if (filtre?.familleId) qs.set('familleId', filtre.familleId);
        if (filtre?.statut) qs.set('statut', filtre.statut);
        const q = qs.toString();
        return apiFetch<Facture[]>(`/api/scolaire/factures${q ? `?${q}` : ''}`);
      },
      getFacture: (id: string) => apiFetch<Facture>(`/api/scolaire/factures/${id}`),
      annuler: (id: string) => apiFetch<Facture>(`/api/scolaire/factures/${id}/annuler`, { method: 'POST' }),
      encaisser: (id: string, body: { moyen: MoyenEncaissement; montant: number; reference?: string }) =>
        apiFetch<Facture>(`/api/scolaire/factures/${id}/encaissement`, { method: 'POST', body: JSON.stringify(body) }),
      // Paiement en ligne PayFiP (mock derrière flag)
      payer: (id: string) =>
        apiFetch<PayFipDemande>(`/api/scolaire/factures/${id}/payer`, { method: 'POST' }),
      payerRetour: (id: string, resultat: 'paye' | 'refuse' | 'abandon') =>
        apiFetch<{ facture: Facture }>(`/api/scolaire/factures/${id}/payer/retour`, { method: 'POST', body: JSON.stringify({ resultat }) }),
      /** PDF de la facture (Blob authentifié). L'appelant fait URL.createObjectURL(blob). */
      facturePdfBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/scolaire/factures/${id}/pdf?download=1`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        return res.blob();
      },
      // En-tête de facture (config mairie)
      getEntete: () => apiFetch<{ mode: 'auto' | 'image' | 'pdf'; aUneImage: boolean; mentionLegale: string | null; iban: string | null }>('/api/scolaire/facture-entete'),
      setEntete: (body: { mode: 'auto' | 'image' | 'pdf'; mentionLegale?: string | null; iban?: string | null }) =>
        apiFetch<{ mode: string; mentionLegale: string | null; iban: string | null }>('/api/scolaire/facture-entete', { method: 'PUT', body: JSON.stringify(body) }),
      uploadEnteteImage: async (file: File): Promise<{ mode: string; aUneImage: boolean }> => {
        const token = getAccessToken();
        const fd = new FormData();
        fd.append('fichier', file);
        const res = await fetch('/api/scolaire/facture-entete/image', {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'UPLOAD_FAILED' });
        return res.json();
      },
      // Intégrations + QF
      integrationsStatut: () => apiFetch<IntegrationsStatut>('/api/scolaire/integrations/statut'),
      recupererQf: (familleId: string) =>
        apiFetch<QfRecupere>(`/api/scolaire/familles/${familleId}/quotient-familial/recuperer`, { method: 'POST' }),
      // QF via saisie d'identité (modalité indépendante de FranceConnect)
      recupererQfIdentite: (familleId: string, identite: {
        nomNaissance: string; prenoms: string; dateNaissance: string;
        sexeEtatCivil?: 'M' | 'F'; codePostal?: string; communeNaissance?: string;
      }) => apiFetch<QfRecupere>(`/api/scolaire/familles/${familleId}/quotient-familial/identite`, { method: 'POST', body: JSON.stringify(identite) }),
      // FranceConnect : démarre le flux (renvoie l'URL d'autorisation à ouvrir)
      franceConnectLogin: (familleId?: string) =>
        apiFetch<{ url: string; mock: boolean }>(`/api/scolaire/franceconnect/login${familleId ? `?familleId=${familleId}` : ''}`),
    },
    // Dossiers d'inscription scolaire (sous-lot A)
    dossiers: {
      list: (statut?: DossierScolaireStatut) =>
        apiFetch<DossierScolaire[]>(`/api/scolaire/dossiers${statut ? `?statut=${statut}` : ''}`),
      get: (id: string) => apiFetch<DossierScolaireDetail>(`/api/scolaire/dossiers/${id}`),
      valider: (id: string, statut: 'valide' | 'refuse' | 'en_attente' | 'soumis', motifRefus?: string) =>
        apiFetch<DossierScolaire>(`/api/scolaire/dossiers/${id}/valider`, { method: 'POST', body: JSON.stringify({ statut, motifRefus }) }),
      getMien: (familleId: string) =>
        apiFetch<DossierScolaireDetail>(`/api/scolaire/familles/${familleId}/dossier`),
      soumettre: (familleId: string) =>
        apiFetch<DossierScolaire>(`/api/scolaire/familles/${familleId}/dossier/soumettre`, { method: 'POST', body: JSON.stringify({}) }),
      // Pièces (sous-lot B)
      listPiecesDemandees: () => apiFetch<DossierPieceDemandee[]>('/api/scolaire/dossiers/pieces-demandees'),
      createPieceDemandee: (libelle: string, obligatoire = true) =>
        apiFetch<DossierPieceDemandee>('/api/scolaire/dossiers/pieces-demandees', { method: 'POST', body: JSON.stringify({ libelle, obligatoire }) }),
      deletePieceDemandee: (id: string) =>
        apiFetch<void>(`/api/scolaire/dossiers/pieces-demandees/${id}`, { method: 'DELETE' }),
      listPieces: (dossierId: string) => apiFetch<DossierPieceDeposee[]>(`/api/scolaire/dossiers/${dossierId}/pieces`),
      deposerPiece: async (dossierId: string, file: File, pieceDemandeeId?: string): Promise<DossierPieceDeposee> => {
        const token = getAccessToken();
        const fd = new FormData();
        fd.append('fichier', file);
        if (pieceDemandeeId) fd.append('pieceDemandeeId', pieceDemandeeId);
        const res = await fetch(`/api/scolaire/dossiers/${dossierId}/pieces`, {
          method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new ApiException(res.status, { error: b.error ?? `HTTP ${res.status}`, code: b.code ?? 'UPLOAD_FAILED' });
        }
        return res.json();
      },
      pieceUrl: (pieceId: string) => apiFetch<{ url: string }>(`/api/scolaire/dossiers/pieces/${pieceId}/url`),
      cocherPiece: (pieceId: string, conforme: boolean) =>
        apiFetch<DossierPieceDeposee>(`/api/scolaire/dossiers/pieces/${pieceId}/conforme`, { method: 'PATCH', body: JSON.stringify({ conforme }) }),
    },
    createFamille: (body: CreateFamilleInput) =>
      apiFetch<ScolaireFamilleDetail>('/api/scolaire/familles', { method: 'POST', body: JSON.stringify(body) }),
    updateFamille: (id: string, body: UpdateFamilleInput) =>
      apiFetch<ScolaireFamilleDetail>(`/api/scolaire/familles/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteFamille: (id: string) => apiFetch<void>(`/api/scolaire/familles/${id}`, { method: 'DELETE' }),
    createEnfant: (body: CreateEnfantInput) =>
      apiFetch<ScolaireEnfant>('/api/scolaire/enfants', { method: 'POST', body: JSON.stringify(body) }),
    updateEnfant: (id: string, body: UpdateEnfantInput) =>
      apiFetch<ScolaireEnfant>(`/api/scolaire/enfants/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    updateEnfantSensible: (id: string, body: UpdateEnfantSensibleInput) =>
      apiFetch<ScolaireEnfant>(`/api/scolaire/enfants/${id}/sensible`, { method: 'PATCH', body: JSON.stringify(body) }),
    deleteEnfant: (id: string) => apiFetch<void>(`/api/scolaire/enfants/${id}`, { method: 'DELETE' }),
    listEcoles: () => apiFetch<ScolaireEcole[]>('/api/scolaire/ecoles'),
    listClasses: (ecoleId?: string) => apiFetch<ScolaireClasse[]>(`/api/scolaire/classes${ecoleId ? `?ecoleId=${ecoleId}` : ''}`),
    // Cantine
    getParametres: () => apiFetch<ScolaireParametres>('/api/scolaire/parametres'),
    setParametres: (body: {
      cutoffHours: 24 | 48 | 72;
      facturationActive?: boolean;
      annulationHorsDelai?: 'facture' | 'non_facture' | 'facture_sauf_justificatif';
      repasNonReserve?: 'facture' | 'facture_majore' | 'non_facture';
      delaiJustificatifJours?: number;
      delaiPaiementJours?: number;
      mentionPiedFacture?: string | null;
      facturationUniteMinutes?: 15 | 30 | 60;
      facturationArrondi?: 'sup' | 'proche';
      modeFacturationCantine?: 'forfait' | 'temps';
      modeFacturationPeriscolaire?: 'forfait' | 'temps';
      modeFacturationAlsh?: 'forfait' | 'temps';
      autoFacturationActive?: boolean;
      autoFacturationJour?: number;
      autoFacturationHeure?: number;
      autoFacturationPeriode?: 'mois_ecoule' | 'mois_en_cours';
      autoFacturationGroupee?: boolean;
      autoFacturationServices?: ('cantine' | 'periscolaire' | 'alsh')[];
      autoFacturationEnvoiEmail?: boolean;
    }) =>
      apiFetch<ScolaireParametres>('/api/scolaire/parametres', { method: 'PUT', body: JSON.stringify(body) }),
    createInscription: (body: CreateCantineInscriptionInput) =>
      apiFetch<CreateCantineInscriptionResult>('/api/scolaire/cantine/inscriptions', { method: 'POST', body: JSON.stringify(body) }),
    getCalendrier: (enfantId: string, from?: string, to?: string) => {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      const q = qs.toString();
      return apiFetch<CantineCalendrierResponse>(`/api/scolaire/cantine/enfants/${enfantId}/reservations${q ? `?${q}` : ''}`);
    },
    toggleReservation: (body: ToggleReservationInput) =>
      apiFetch<CantineReservation>('/api/scolaire/cantine/reservations/toggle', { method: 'PATCH', body: JSON.stringify(body) }),
    pointer: (body: { reservationId: string; statut: ReservationStatut }) =>
      apiFetch<CantineReservation>('/api/scolaire/cantine/reservations/pointage', { method: 'PATCH', body: JSON.stringify(body) }),
    getEffectifCantine: (date: string) =>
      apiFetch<{ dateService: string; comptes: number; absents: number }>(`/api/scolaire/cantine/effectif/${date}`),
    getEffectifAlsh: (date: string) =>
      apiFetch<{ dateService: string; comptes: number; absents: number; parFormule: { journee: number; matin: number; apresMidi: number } }>(`/api/scolaire/alsh/effectif/${date}`),
    getListeAlsh: (date: string) =>
      apiFetch<{ dateService: string; lignes: { reservationId: string; enfantId: string; prenom: string; nom: string; classe: string | null; ecole: string | null; formule: 'journee' | 'matin' | 'apres_midi'; statut: 'reserve' | 'present' | 'absent' | 'absent_justifie'; heureEntree: string | null; heureSortie: string | null; repas: boolean; mangeCantine: boolean; alshOuverture: string | null; alshFermeture: string | null }[] }>(`/api/scolaire/alsh/liste/${date}`),
    pointerAlsh: (body: { reservationId: string; statut: 'reserve' | 'present' | 'absent' | 'absent_justifie' }) =>
      apiFetch<{ id: string; statut: string }>(`/api/scolaire/alsh/pointer`, { method: 'POST', body: JSON.stringify(body) }),
    horodaterAlsh: (body: { reservationId: string; sens: 'entree' | 'sortie'; heure?: string }) =>
      apiFetch<{ id: string }>(`/api/scolaire/alsh/horodater`, { method: 'POST', body: JSON.stringify(body) }),
    setRepasAlsh: (body: { reservationId: string; repas: boolean }) =>
      apiFetch<{ id: string; repas: boolean }>(`/api/scolaire/alsh/repas`, { method: 'POST', body: JSON.stringify(body) }),
    alsh: {
      createInscription: (body: { enfantId: string; dateDebut: string; dateFin: string; typePeriode: 'mercredis' | 'vacances'; formule: 'journee' | 'matin' | 'apres_midi' }) =>
        apiFetch<{ inscriptionId: string; reservationsCreees: number }>(`/api/scolaire/alsh/inscriptions`, { method: 'POST', body: JSON.stringify(body) }),
      listInscriptions: (enfantId: string) =>
        apiFetch<{ id: string; dateDebut: string; dateFin: string; typePeriode: 'mercredis' | 'vacances'; formule: 'journee' | 'matin' | 'apres_midi'; actif: boolean }[]>(`/api/scolaire/alsh/enfants/${enfantId}/inscriptions`),
    },
    getListeCantine: (date: string) =>
      apiFetch<CantineListeJour>(`/api/scolaire/cantine/liste/${date}`),
    feuilleCuisinePdfBlob: async (date: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/scolaire/cantine/liste/${date}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
    feuilleAdultesPdfBlob: async (date: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/scolaire/cantine/adultes/liste/${date}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
    feuilleAlshPdfBlob: async (date: string): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/scolaire/alsh/liste/${date}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
      return res.blob();
    },
    // Config écoles / classes / jours d'ouverture
    createEcole: (body: { nom: string; type?: string; adresse?: string }) =>
      apiFetch<ScolaireEcole>('/api/scolaire/ecoles', { method: 'POST', body: JSON.stringify(body) }),
    updateEcoleHoraires: (ecoleId: string, body: { ouverture?: string | null; finMatin?: string | null; repriseApresMidi?: string | null; finJournee?: string | null; mercrediOuverture?: string | null; mercrediFin?: string | null; garderieOuvertureMatin?: string | null; garderieFermetureSoir?: string | null; garderieMercrediOuverture?: string | null; garderieMercrediFermeture?: string | null; alshOuverture?: string | null; alshFermeture?: string | null; alshMercrediOuverture?: string | null; alshMercrediFermeture?: string | null }) =>
      apiFetch<ScolaireEcole>(`/api/scolaire/ecoles/${ecoleId}/horaires`, { method: 'PATCH', body: JSON.stringify(body) }),
    /** S4-b : rattacher/détacher une école d'un lieu (partage de bâtiment). */
    updateEcoleLieu: (ecoleId: string, lieuId: string | null) =>
      apiFetch<{ ok: boolean }>(`/api/scolaire/ecoles/${ecoleId}/lieu`, { method: 'PATCH', body: JSON.stringify({ lieuId }) }),
    listEtablissementsHoraires: () =>
      apiFetch<ScolaireEcole[]>('/api/scolaire/etablissements-horaires'),
    createClasse: (body: { ecoleId: string; nom: string; niveau?: string }) =>
      apiFetch<ScolaireClasse>('/api/scolaire/classes', { method: 'POST', body: JSON.stringify(body) }),
    listJoursService: (serviceType: string, from?: string, to?: string) => {
      const qs = new URLSearchParams({ serviceType });
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      return apiFetch<ScolaireJourService[]>(`/api/scolaire/jours-service?${qs.toString()}`);
    },
    setJourService: (body: { serviceType: string; dateService: string; ouvert: boolean; motif?: string }) =>
      apiFetch<ScolaireJourService>('/api/scolaire/jours-service', { method: 'PUT', body: JSON.stringify(body) }),
    bulkJoursService: (body: { serviceType: string; dateDebut: string; dateFin: string; joursSemaine: number[]; ouvert: boolean }) =>
      apiFetch<{ posed: number }>('/api/scolaire/jours-service/bulk', { method: 'PUT', body: JSON.stringify(body) }),
    importCsv: (contenu: string) =>
      apiFetch<ScolaireImportResult>('/api/scolaire/import-csv', { method: 'POST', body: JSON.stringify({ contenu }) }),
    exportEnfantsBlob: async (format: 'csv' | 'pdf'): Promise<Blob> => {
      const token = getAccessToken();
      const res = await fetch(`/api/scolaire/export?format=${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new ApiException(res.status, { error: text || `HTTP ${res.status}`, code: 'EXPORT_FAILED' });
      }
      return res.blob();
    },
    // Périscolaire (créneaux matin / soir)
    periscolaire: {
      createInscription: (body: CreatePeriscolaireInscriptionInput) =>
        apiFetch<CreatePeriscolaireInscriptionResult>('/api/scolaire/periscolaire/inscriptions', { method: 'POST', body: JSON.stringify(body) }),
      listInscriptions: (enfantId: string) =>
        apiFetch<PeriscolaireInscription[]>(`/api/scolaire/periscolaire/enfants/${enfantId}/inscriptions`),
      getCalendrier: (enfantId: string, from?: string, to?: string) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        const q = qs.toString();
        return apiFetch<PeriscolaireCalendrierResponse>(`/api/scolaire/periscolaire/enfants/${enfantId}/reservations${q ? `?${q}` : ''}`);
      },
      toggleReservation: (body: { reservationId: string; reserve: boolean }) =>
        apiFetch<PeriscolaireReservation>('/api/scolaire/periscolaire/reservations/toggle', { method: 'PATCH', body: JSON.stringify(body) }),
      pointer: (body: { reservationId: string; statut: ReservationStatut }) =>
        apiFetch<PeriscolaireReservation>('/api/scolaire/periscolaire/reservations/pointage', { method: 'PATCH', body: JSON.stringify(body) }),
      horodater: (body: { reservationId: string; sens: 'entree' | 'sortie'; heure?: string }) =>
        apiFetch<PeriscolaireReservation>('/api/scolaire/periscolaire/reservations/horodatage', { method: 'PATCH', body: JSON.stringify(body) }),
      getEffectif: (date: string) =>
        apiFetch<PeriscolaireEffectifJour>(`/api/scolaire/periscolaire/effectif/${date}`),
      getListe: (date: string) =>
        apiFetch<PeriscolaireListeJour>(`/api/scolaire/periscolaire/liste/${date}`),
      feuillePdfBlob: async (date: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/scolaire/periscolaire/liste/${date}/pdf`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        return res.blob();
      },
    },
    // Planning des agents (manager crée / agent lit)
    planning: {
      listAgents: () =>
        apiFetch<{ items: { id: string; fullName: string; role: string; couleurMarker: string | null }[] }>('/api/scolaire/agents'),
      setAgentCouleur: (agentUserId: string, couleurMarker: string | null) =>
        apiFetch<void>(`/api/scolaire/planning/agents/${agentUserId}/couleur`, { method: 'PATCH', body: JSON.stringify({ couleurMarker }) }),
      getWeek: (from?: string, to?: string) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        const q = qs.toString();
        return apiFetch<ScolairePlanningCreneau[]>(`/api/scolaire/planning${q ? `?${q}` : ''}`);
      },
      // Présence unifiée (équipe/personne/année) — même format que les autres briques
      presenceSemaine: (weekStart: string) =>
        apiFetch<PlanningWeekResponse>(`/api/scolaire/planning/presence-semaine?weekStart=${weekStart}`),
      presenceAnnee: (annee: number, agentId?: string) =>
        apiFetch<PlanningAnneeResponse>(`/api/scolaire/planning/presence-annee?annee=${annee}${agentId ? `&agentId=${agentId}` : ''}`),
      getMaSemaine: (from?: string, to?: string) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', from);
        if (to) qs.set('to', to);
        const q = qs.toString();
        return apiFetch<ScolairePlanningCreneau[]>(`/api/scolaire/planning/ma-semaine${q ? `?${q}` : ''}`);
      },
      createCreneau: (body: {
        agentUserId: string; dateDebut: string; dateFin: string; titre: string;
        description?: string; type: ScolairePlanningType; plageType?: ScolairePlanningPlage; ecoleId?: string; couleurMarker?: string;
      }) => apiFetch<ScolairePlanningCreneau>('/api/scolaire/planning/creneaux', { method: 'POST', body: JSON.stringify(body) }),
      updateCreneau: (id: string, body: Record<string, unknown>) =>
        apiFetch<ScolairePlanningCreneau>(`/api/scolaire/planning/creneaux/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      updateStatut: (id: string, statut: ScolairePlanningStatut) =>
        apiFetch<ScolairePlanningCreneau>(`/api/scolaire/planning/creneaux/${id}/statut`, { method: 'PATCH', body: JSON.stringify({ statut }) }),
      deleteCreneau: (id: string) =>
        apiFetch<void>(`/api/scolaire/planning/creneaux/${id}`, { method: 'DELETE' }),
      transmettrePaie: (from: string, to: string) =>
        apiFetch<{ transmis: number }>('/api/scolaire/planning/transmettre-paie', { method: 'POST', body: JSON.stringify({ from, to }) }),
      // Profils + génération (sous-lot planning avancé)
      listProfils: () => apiFetch<ScolairePlanningProfil[]>('/api/scolaire/planning/profils'),
      createProfil: (body: { nom: string; type: ScolairePlanningType; plages: ProfilPlage[] }) =>
        apiFetch<ScolairePlanningProfil>('/api/scolaire/planning/profils', { method: 'POST', body: JSON.stringify(body) }),
      updateProfil: (id: string, body: { nom: string; type: ScolairePlanningType; plages: ProfilPlage[] }) =>
        apiFetch<{ profil: ScolairePlanningProfil; supprimes: number; crees: number }>(`/api/scolaire/planning/profils/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deleteProfil: (id: string) =>
        apiFetch<void>(`/api/scolaire/planning/profils/${id}`, { method: 'DELETE' }),
      generer: (body: { profilId: string; agentIds: string[]; from: string; to: string; inclureVacances?: boolean }) =>
        apiFetch<GenererPlanningResult>('/api/scolaire/planning/generer', { method: 'POST', body: JSON.stringify(body) }),
      joursNonTravailles: (from: string, to: string) =>
        apiFetch<JourNonTravaille[]>(`/api/scolaire/planning/jours-non-travailles?from=${from}&to=${to}`),
      listPostes: () => apiFetch<Array<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>>('/api/scolaire/planning/postes'),
      createPoste: (body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>('/api/scolaire/planning/postes', { method: 'POST', body: JSON.stringify(body) }),
      updatePoste: (id: string, body: { titre: string; dureeMin?: number; nbPersonnes?: number; couleur?: string }) =>
        apiFetch<{ id: string; titre: string; dureeMin: number; nbPersonnes: number; type: string; couleur: string | null }>(`/api/scolaire/planning/postes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      deletePoste: (id: string) => apiFetch<void>(`/api/scolaire/planning/postes/${id}`, { method: 'DELETE' }),
    },
  },
  famillePieces: {
    /** Catalogue des natures de pièces (filtrable par module). */
    types: (module?: string) =>
      apiFetch<{ pieceTypes: PieceType[] }>(`/api/familles/piece-types${module ? `?module=${module}` : ''}`),
    createType: (body: { code: string; libelle: string; portee?: 'commun' | 'specifique'; module?: string | null; ordre?: number }) =>
      apiFetch<PieceType>('/api/familles/piece-types', { method: 'POST', body: JSON.stringify(body) }),
    /** Catalogue ADMIN (inclut les pièces désactivées) — pilotage « demandée ou non ». */
    typesAdmin: (module?: string) =>
      apiFetch<{ pieceTypes: PieceType[] }>(`/api/familles/piece-types-admin${module ? `?module=${module}` : ''}`),
    /** Active/désactive une nature de pièce (= demandée ou non aux familles). */
    setTypeActif: (id: string, actif: boolean) =>
      apiFetch<PieceType>(`/api/familles/piece-types/${id}`, { method: 'PATCH', body: JSON.stringify({ actif }) }),
    /** Bibliothèque (pièces courantes) d'une famille. */
    list: (familleId: string) =>
      apiFetch<{ pieces: FamillePiece[] }>(`/api/familles/${familleId}/pieces`),
    /** Liste UNIFIÉE des pièces requises (administré enfance) — une seule liste,
     *  calculée selon l'usage réel de la famille. Toute pièce listée est obligatoire. */
    piecesRequises: (familleId: string) =>
      apiFetch<{ pieces: PieceRequise[] }>(`/api/familles/${familleId}/pieces-requises`),
    /** Dépose / met à jour une pièce (multipart). */
    deposer: (familleId: string, pieceTypeId: string, file: File, dateExpiration?: string) => {
      const form = new FormData();
      form.append('file', file);
      form.append('pieceTypeId', pieceTypeId);
      if (dateExpiration) form.append('dateExpiration', dateExpiration);
      return apiFetchRaw<FamillePiece>(`/api/familles/${familleId}/pieces`, { method: 'POST', body: form });
    },
    url: (familleId: string, pieceId: string) =>
      apiFetch<{ url: string }>(`/api/familles/${familleId}/pieces/${pieceId}/url`),
    cocher: (familleId: string, pieceId: string, conforme: boolean) =>
      apiFetch<void>(`/api/familles/${familleId}/pieces/${pieceId}/conformite`, { method: 'PATCH', body: JSON.stringify({ conforme }) }),
  },
  /** Machine à états (vague 4a, mig 244) : écran « Circuits » + moteur. */
  workflows: {
    presets: () =>
      apiFetch<{ presets: Array<{ code: string; libelle: string; description: string; etats: number; transitions: number }> }>('/api/workflows/presets'),
    definitions: () =>
      apiFetch<{ definitions: WorkflowDefinition[] }>('/api/workflows/definitions'),
    depuisPreset: (code: string) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/depuis-preset/${code}`, { method: 'POST' }),
    dupliquer: (id: string) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/dupliquer`, { method: 'POST' }),
    modifier: (id: string, body: Record<string, unknown>) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}`, {
        method: 'PUT', body: JSON.stringify(body),
      }),
    supprimer: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/workflows/definitions/${id}`, { method: 'DELETE' }),
    ajouterEtat: (id: string, body: Record<string, unknown>) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/etats`, {
        method: 'POST', body: JSON.stringify(body),
      }),
    modifierEtat: (id: string, etatId: string, body: Record<string, unknown>) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/etats/${etatId}`, {
        method: 'PATCH', body: JSON.stringify(body),
      }),
    supprimerEtat: (id: string, etatId: string) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/etats/${etatId}`, { method: 'DELETE' }),
    ajouterTransition: (id: string, body: Record<string, unknown>) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/transitions`, {
        method: 'POST', body: JSON.stringify(body),
      }),
    /** Refonte UX, chantier 5 — modifier une flèche sans la recréer (historique préservé). */
    modifierTransition: (id: string, transitionId: string, body: { libelle?: string; roleMinimum?: 'agent' | 'manager' | 'admin'; serviceRequis?: string | null; pieceRequise?: boolean }) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/transitions/${transitionId}`, { method: 'PUT', body: JSON.stringify(body) }),
    /** Refonte UX, chantier 5 — les règles réelles, écrites en phrases par l'écran Automatismes. */
    automatismes: () => apiFetch<AutomatismesDTO>('/api/workflows/automatismes'),
    supprimerTransition: (id: string, transitionId: string) =>
      apiFetch<WorkflowDefinition>(`/api/workflows/definitions/${id}/transitions/${transitionId}`, { method: 'DELETE' }),
    // ── Le moteur (vague 4c) : accrocher un parcours à une fiche ─────────────
    /** Une instance de circuit (état courant + flèches autorisées pour l'appelant). */
    instance: (instanceId: string) => apiFetch<WorkflowInstance>(`/api/workflows/instances/${instanceId}`),
    circuitsActifs: () =>
      apiFetch<{ circuits: Array<{ id: string; libelle: string; presetSource: string | null }> }>('/api/workflows/circuits-actifs'),
    demarrer: (definitionId: string, entiteType: 'signalement' | 'projet', entiteId: string) =>
      apiFetch<WorkflowInstance>('/api/workflows/instances', {
        method: 'POST', body: JSON.stringify({ definitionId, entiteType, entiteId }),
      }),
    instancePourEntite: (type: 'signalement' | 'projet' | 'rh_suivi', id: string) =>
      apiFetch<{ instance: WorkflowInstance | null }>(`/api/workflows/entites/${type}/${id}`),
    historique: (instanceId: string) =>
      apiFetch<{ evenements: Array<{ id: string; deEtat: string | null; versEtat: string; versEtatId?: string; par: string | null; commentaire: string | null; aPiece: boolean; pieceNom?: string | null; aDocumentSigne?: boolean; documentSigneNom?: string | null; documentSigneLe?: string | null; documentSignePar?: string | null; dateCle: string | null; montant: number | null; createdAt: string }> }>(
        `/api/workflows/instances/${instanceId}/historique`),
    /** M9 — ouvrir la pièce d'une étape (originale ou signée). */
    lienPiece: (instanceId: string, evenementId: string, quelle: 'piece' | 'signe' = 'piece') =>
      apiFetch<{ url: string }>(`/api/workflows/instances/${instanceId}/evenements/${evenementId}/piece${quelle === 'signe' ? '?quelle=signe' : ''}`),
    /** M9 — réintégrer le scan d'un document signé à la main sur une étape passée. */
    reintegrerDocumentSigne: (instanceId: string, evenementId: string, f: File) => {
      const fd = new FormData(); fd.append('document', f);
      return apiFetchRaw<{ ok: true }>(`/api/workflows/instances/${instanceId}/evenements/${evenementId}/document-signe`,
        { method: 'POST', body: fd });
    },
    configurerEnvoi: (defId: string, transitionId: string, config: EnvoiConfig | null) =>
      apiFetch<{ ok: boolean }>(`/api/workflows/definitions/${defId}/transitions/${transitionId}/envoi`, {
        method: 'PUT', body: JSON.stringify({ config }),
      }),
    annuaireDestinataires: () =>
      apiFetch<AnnuaireDestinataires>('/api/workflows/annuaire-destinataires'),
    envoisInstance: (instanceId: string) =>
      apiFetch<{ envois: WorkflowEnvoi[] }>(`/api/workflows/instances/${instanceId}/envois`),
    envoisAValider: () =>
      apiFetch<{ envois: WorkflowEnvoi[] }>('/api/workflows/envois/a-valider'),
    majEnvoi: (id: string, body: { sujet?: string; corps?: string; destinataires?: EnvoiDestinataire[] }) =>
      apiFetch<WorkflowEnvoi>(`/api/workflows/envois/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    soumettreEnvoi: (id: string) =>
      apiFetch<WorkflowEnvoi>(`/api/workflows/envois/${id}/soumettre`, { method: 'POST' }),
    validerEnvoi: (id: string) =>
      apiFetch<WorkflowEnvoi>(`/api/workflows/envois/${id}/valider`, { method: 'POST' }),
    refuserEnvoi: (id: string, commentaire: string) =>
      apiFetch<WorkflowEnvoi>(`/api/workflows/envois/${id}/refuser`, { method: 'POST', body: JSON.stringify({ commentaire }) }),
    annulerEnvoi: (id: string) =>
      apiFetch<WorkflowEnvoi>(`/api/workflows/envois/${id}/annuler`, { method: 'POST' }),
    transiterInstance: (instanceId: string, transitionId: string, opts?: {
      commentaire?: string; dateCle?: string; montant?: number; secteurBudget?: string; piece?: File;
    }) => {
      const fd = new FormData();
      fd.append('transitionId', transitionId);
      if (opts?.commentaire) fd.append('commentaire', opts.commentaire);
      if (opts?.dateCle) fd.append('dateCle', opts.dateCle);
      if (opts?.montant !== undefined) fd.append('montant', String(opts.montant));
      if (opts?.secteurBudget) fd.append('secteurBudget', opts.secteurBudget);
      if (opts?.piece) fd.append('piece', opts.piece);
      return apiFetchRaw<WorkflowInstance>(`/api/workflows/instances/${instanceId}/transitions`, {
        method: 'POST', body: fd,
      });
    },
  },

  /** Budget par secteur (vague 4d, mig 247) : la Comptabilité possède. */
  budgets: {
    suivi: (annee?: number) =>
      apiFetch<BudgetSuivi>(`/api/budgets/suivi${annee ? `?annee=${annee}` : ''}`),
    lister: (annee?: number) =>
      apiFetch<BudgetSuivi>(`/api/budgets${annee ? `?annee=${annee}` : ''}`),
    mouvements: (annee?: number, secteur?: string) => {
      const q = new URLSearchParams();
      if (annee) q.set('annee', String(annee));
      if (secteur) q.set('secteur', secteur);
      const qs = q.toString();
      return apiFetch<{ mouvements: BudgetMouvement[] }>(`/api/budgets/mouvements${qs ? `?${qs}` : ''}`);
    },
    upsert: (body: { annee: number; secteur: string; montantAlloue: number; note?: string | null }) =>
      apiFetch<BudgetSuivi>('/api/budgets', { method: 'PUT', body: JSON.stringify(body) }),
    supprimer: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/budgets/${id}`, { method: 'DELETE' }),
  },

  /** Moteur de projet (vague 4b, mig 245) : rétroplannings à paliers datés. */
  projets: {
    lister: () =>
      apiFetch<{ projets: Array<{
        id: string; intitule: string; secteur: string | null; responsable: string | null;
        etatCourant: { libelle: string; couleur: string | null; estFinal: boolean } | null;
        progression: { faits: number; total: number };
        dateButoirCourante: string | null; enRetard: boolean; createdAt: string;
      }> }>('/api/projets'),
    get: (id: string) => apiFetch<ProjetDetail>(`/api/projets/${id}`),
    preRemplissage: (definitionId: string, datePivot: string) =>
      apiFetch<{ paliers: Array<{ etatId: string; code: string; libelle: string; ordre: number; dateProposee: string | null }> }>(
        `/api/projets/pre-remplissage?definitionId=${definitionId}&datePivot=${datePivot}`),
    creer: (body: Record<string, unknown>) =>
      apiFetch<ProjetDetail>('/api/projets', { method: 'POST', body: JSON.stringify(body) }),
    express: (body: Record<string, unknown>) =>
      apiFetch<ProjetDetail>('/api/projets/express', { method: 'POST', body: JSON.stringify(body) }),
    modifier: (id: string, body: Record<string, unknown>) =>
      apiFetch<ProjetDetail>(`/api/projets/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    setEcheance: (id: string, etatId: string, body: { dateButoir: string | null; note?: string | null }) =>
      apiFetch<ProjetDetail>(`/api/projets/${id}/echeances/${etatId}`, {
        method: 'PUT', body: JSON.stringify(body),
      }),
    transiter: (id: string, transitionId: string, commentaire?: string, piece?: File, opts?: {
      dateCle?: string; montant?: number; secteurBudget?: string;
    }) => {
      const fd = new FormData();
      fd.append('transitionId', transitionId);
      if (commentaire) fd.append('commentaire', commentaire);
      if (opts?.dateCle) fd.append('dateCle', opts.dateCle);
      if (opts?.montant !== undefined) fd.append('montant', String(opts.montant));
      if (opts?.secteurBudget) fd.append('secteurBudget', opts.secteurBudget);
      if (piece) fd.append('piece', piece);
      return apiFetchRaw<ProjetDetail>(`/api/projets/${id}/transitions`, { method: 'POST', body: fd });
    },
    supprimer: (id: string) =>
      apiFetch<{ ok: boolean }>(`/api/projets/${id}`, { method: 'DELETE' }),
  },

  rh: {
    /** Formations (5.20) — parcours inscription → pointage → émargement → clôture. */
    formations: {
      list: (depuis?: string) =>
        apiFetch<{ formations: Array<{
          id: string; intitule: string; organisme: string | null; lieu: string | null;
          dateDebut: string; dateFin: string | null;
          heureDebut: string | null; heureFin: string | null;
          obligatoire: boolean;
          cout: number | null;
          habilitationTypeId: string | null; habilitationTypeLibelle: string | null;
          aEmargementSession: boolean; clotureeAt: string | null;
          inscrits: number; presents: number; emargements: number;
        }> }>(`/api/rh/formations${depuis ? `?depuis=${depuis}` : ''}`),
      creer: (body: Record<string, unknown>) =>
        apiFetch<Record<string, unknown>>('/api/rh/formations', {
          method: 'POST', body: JSON.stringify(body),
        }),
      inscriptions: (id: string) =>
        apiFetch<{ inscriptions: Array<{
          id: string; agentId: string; agentNom: string; statut: string;
          presenceAt: string | null; aEmargement: boolean; commentaire: string | null;
        }> }>(`/api/rh/formations/${id}/inscriptions`),
      inscrire: (id: string, agentIds: string[]) =>
        apiFetch<{ inscrits: number }>(`/api/rh/formations/${id}/inscriptions`, {
          method: 'POST', body: JSON.stringify({ agentIds }),
        }),
      pointer: (inscriptionId: string, present: boolean) =>
        apiFetch<Record<string, unknown>>(
          `/api/rh/formations/inscriptions/${inscriptionId}/pointage`,
          { method: 'PATCH', body: JSON.stringify({ present }) }),
      deposerEmargement: (inscriptionId: string, fichier: File) => {
        const fd = new FormData();
        fd.append('fichier', fichier);
        return apiFetchRaw<Record<string, unknown>>(
          `/api/rh/formations/inscriptions/${inscriptionId}/emargement`,
          { method: 'POST', body: fd });
      },
      emargementUrl: (inscriptionId: string) =>
        apiFetch<{ url: string }>(
          `/api/rh/formations/inscriptions/${inscriptionId}/emargement`),
      /** La feuille de la SESSION — celle que rend l'organisme (mig 242). */
      deposerEmargementSession: (formationId: string, fichier: File) => {
        const fd = new FormData();
        fd.append('fichier', fichier);
        return apiFetchRaw<Record<string, unknown>>(
          `/api/rh/formations/${formationId}/emargement`,
          { method: 'POST', body: fd });
      },
      emargementSessionUrl: (formationId: string) =>
        apiFetch<{ url: string }>(`/api/rh/formations/${formationId}/emargement`),
      /** Clôture « tous présents » — seuls les absents sont listés. */
      cloturer: (formationId: string, absents: string[]) =>
        apiFetch<{
          presents: number; absents: number;
          habilitations: Array<{ agentNom: string; echeance: string | null; creee: boolean }>;
          nonReboucles: string[];
          comptaPrevenue: boolean;
        }>(`/api/rh/formations/${formationId}/cloture`, {
          method: 'POST', body: JSON.stringify({ absents }),
        }),
    },

    /** Arrêtés RH (5.22 / 5.23). */
    arretes: {
      presets: (tous = false) =>
        apiFetch<{ presets: Array<{
          id: string; libelle: string; categorie: string | null;
          corps: string; visa: string | null; actif: boolean; ordre: number;
          alerteDestinataires: string[];
          alerteRappels: Array<{ quantite: number; unite: string }>;
        }> }>(`/api/rh/arretes/presets${tous ? '?tous=1' : ''}`),
      creerPreset: (body: Record<string, unknown>) =>
        apiFetch<Record<string, unknown>>('/api/rh/arretes/presets', {
          method: 'POST', body: JSON.stringify(body),
        }),
      majPreset: (id: string, body: Record<string, unknown>) =>
        apiFetch<Record<string, unknown>>(`/api/rh/arretes/presets/${id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),

      list: (statut?: string) =>
        apiFetch<{ arretes: Array<{
          id: string; reference: string; objet: string; corps: string;
          statut: string; agentNom: string;
          transmisAt: string | null; transmisA: string[];
          signeAt: string | null; signeParNom: string | null;
          signatureMode: string | null; signatureDate: string | null; aPdf: boolean;
        }> }>(`/api/rh/arretes${statut ? `?statut=${statut}` : ''}`),
      creer: (body: Record<string, unknown>) =>
        apiFetch<Record<string, unknown>>('/api/rh/arretes', {
          method: 'POST', body: JSON.stringify(body),
        }),
      transmettre: (id: string, destinataires: string[] = []) =>
        apiFetch<Record<string, unknown>>(`/api/rh/arretes/${id}/transmettre`, {
          method: 'POST', body: JSON.stringify({ destinataires }),
        }),
      /** Voie manuscrite — sans coût d'envoi électronique. */
      deposerSigneManuel: (id: string, fichier: File, signatureDate?: string) => {
        const fd = new FormData();
        fd.append('fichier', fichier);
        if (signatureDate) fd.append('signatureDate', signatureDate);
        return apiFetchRaw<Record<string, unknown>>(
          `/api/rh/arretes/${id}/signature/manuelle`, { method: 'POST', body: fd });
      },
      /** D-03 — le PDF du PROJET, imprimable avant signature. */
      projetPdfBlob: async (id: string): Promise<Blob> => {
        const token = getAccessToken();
        const res = await fetch(`/api/rh/arretes/${id}/projet-pdf`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!res.ok) throw new ApiException(res.status, { error: `HTTP ${res.status}`, code: 'PDF_FETCH_FAILED' });
        return res.blob();
      },
      pdfUrl: (id: string) =>
        apiFetch<{ url: string }>(`/api/rh/arretes/${id}/pdf`),
    },

    /** Délais de relance — réglage de commune (5.16 / 5.17 / 5.19). */
    delaisRelance: () =>
      apiFetch<{
        psc: Array<{ quantite: number; unite: string }>;
        cnas: Array<{ quantite: number; unite: string }>;
        sft: Array<{ quantite: number; unite: string }>;
        seuilRequalificationMois: number;
        sftMontantParEnfantCents: number;
      }>('/api/rh/delais-relance'),
    majDelaisRelance: (body: Record<string, unknown>) =>
      apiFetch<Record<string, unknown>>('/api/rh/delais-relance', {
        method: 'PUT', body: JSON.stringify(body),
      }),

    /** Organigramme déduit des données existantes (5.13). */
    organigramme: (anonyme = true) =>
      apiFetch<{
        racines: Array<{
          id: string; nom: string | null; fonction: string | null;
          grade: string | null; service: string | null; subordonnes: unknown[];
        }>;
        effectifsParService: Array<{ service: string; effectif: number }>;
        orphelins: number;
      }>(`/api/rh/organigramme?anonyme=${anonyme ? '1' : '0'}`),

    /** Langues parlées par les agents (5.24). */
    langues: () =>
      apiFetch<{ langues: Array<{
        langue: string; agents: Array<{ nom: string; service: string | null }>;
      }> }>('/api/rh/langues'),

    /** Carrière : avancements et contrats (items 5.12 / 5.14, mig 237). */
    carriere: {
      avancements: (agentId: string) =>
        apiFetch<{ avancements: Array<{
          id: string; grade: string | null; echelon: number | null;
          dateEffet: string; dateProchainAvancement: string | null;
          commentaire: string | null;
        }> }>(`/api/rh/dossiers-agents/${agentId}/avancements`),
      creerAvancement: (agentId: string, body: {
        grade?: string | null; echelon?: number | null;
        dateEffet: string; dateProchainAvancement?: string | null;
        rappels?: Array<{ quantite: number; unite: string }>;
        commentaire?: string | null;
      }) =>
        apiFetch<{ id: string }>(`/api/rh/dossiers-agents/${agentId}/avancements`, {
          method: 'POST', body: JSON.stringify(body),
        }),
      supprimerAvancement: (id: string) =>
        apiFetch<void>(`/api/rh/avancements/${id}`, { method: 'DELETE' }),

      anciennetes: (agentId: string) =>
        apiFetch<{
          communeMois: number | null; fonctionPubliqueMois: number | null;
          dateEntreeCommune: string | null; dateEntreeFonctionPublique: string | null;
        }>(`/api/rh/dossiers-agents/${agentId}/anciennetes`),

      contrats: (agentId: string) =>
        apiFetch<{ contrats: Array<{
          id: string; typeContrat: string; dateDebut: string; dateFin: string | null;
          motif: string | null; dureeMois: number | null;
        }> }>(`/api/rh/dossiers-agents/${agentId}/contrats`),
      creerContrat: (agentId: string, body: {
        typeContrat: string; dateDebut: string; dateFin?: string | null;
        motif?: string | null;
        rappels?: Array<{ quantite: number; unite: string }>;
      }) =>
        apiFetch<{ id: string }>(`/api/rh/dossiers-agents/${agentId}/contrats`, {
          method: 'POST', body: JSON.stringify(body),
        }),
      supprimerContrat: (id: string) =>
        apiFetch<void>(`/api/rh/contrats/${id}`, { method: 'DELETE' }),

      /** PSC et CNAS (5.16 / 5.17). */
      social: (agentId: string) =>
        apiFetch<{
          pscPrevoyance: boolean; pscPrevoyanceEcheance: string | null;
          pscSante: boolean; pscSanteEcheance: string | null;
          cnasAdherent: boolean; cnasSecteur: string | null; cnasEcheance: string | null;
        }>(`/api/rh/dossiers-agents/${agentId}/social`),
      majSocial: (agentId: string, body: Record<string, unknown>) =>
        apiFetch<Record<string, unknown>>(`/api/rh/dossiers-agents/${agentId}/social`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),

      /** Supplément familial (5.19). */
      sft: (agentId: string) =>
        apiFetch<{
          actif: boolean; nombreEnfants: number | null;
          dateDebut: string | null; dateFin: string | null; arretManuel: string | null;
          montantMensuelCents: number | null; montantParEnfantCents: number;
          dateFinEffective: string | null; enCours: boolean;
        }>(`/api/rh/dossiers-agents/${agentId}/sft`),
      majSft: (agentId: string, body: {
        actif: boolean; nombreEnfants?: number | null;
        dateDebut?: string | null; dateFin?: string | null; arretManuel?: string | null;
      }) =>
        apiFetch<Record<string, unknown>>(`/api/rh/dossiers-agents/${agentId}/sft`, {
          method: 'PATCH', body: JSON.stringify(body),
        }),

      requalification: (agentId: string) =>
        apiFetch<{
          cumulMois: number; seuilMois: number;
          seuilAtteint: boolean; seuilProche: boolean;
        }>(`/api/rh/dossiers-agents/${agentId}/requalification`),
    },
    consoliderPaie: (from: string, to: string, transmisSeulement?: boolean) => {
      const qs = new URLSearchParams({ from, to });
      if (transmisSeulement) qs.set('transmisSeulement', 'true');
      return apiFetch<RhConsolidationResult>(`/api/rh/paie?${qs.toString()}`);
    },
    getParametres: () => apiFetch<RhParametres>('/api/rh/parametres'),
    updateParametres: (body: { baseConges: RhBaseConges; modeDecompte: RhModeDecompte; heuresParJour?: number }) =>
      apiFetch<RhParametres>('/api/rh/parametres', { method: 'PUT', body: JSON.stringify(body) }),
    getSoldes: (annee: number) => apiFetch<RhSoldesResult>(`/api/rh/soldes?annee=${annee}`),
    setSolde: (body: { agentUserId: string; annee: number; soldeInitial: number }) =>
      apiFetch<RhSoldeConges>('/api/rh/soldes', { method: 'PUT', body: JSON.stringify(body) }),
    // Heures supplémentaires
    declarerHeuresSup: (body: { dateJour: string; minutesMatin?: number; minutesApresMidi?: number; motif?: string }) =>
      apiFetch<RhHeuresSup>('/api/rh/heures-sup', { method: 'POST', body: JSON.stringify(body) }),
    mesHeuresSup: () => apiFetch<RhHeuresSup[]>('/api/rh/heures-sup/mes'),
    annulerHeuresSup: (id: string) => apiFetch<void>(`/api/rh/heures-sup/${id}`, { method: 'DELETE' }),
    listerHeuresSup: (statut?: string) => apiFetch<RhHeuresSup[]>(`/api/rh/heures-sup${statut ? `?statut=${statut}` : ''}`),
    compteursHeuresSup: (from?: string, to?: string) => {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from); if (to) qs.set('to', to);
      const q = qs.toString();
      return apiFetch<RhHeuresSupCompteur[]>(`/api/rh/heures-sup/compteurs${q ? `?${q}` : ''}`);
    },
    statuerHeuresSup: (id: string, body: { statut: 'valide' | 'refuse'; destination?: 'paie' | 'rtt'; motifRefus?: string }) =>
      apiFetch<RhHeuresSup>(`/api/rh/heures-sup/${id}/statut`, { method: 'PATCH', body: JSON.stringify(body) }),
    flecherHeuresSup: (id: string, destination: 'paie' | 'rtt') =>
      apiFetch<RhHeuresSup>(`/api/rh/heures-sup/${id}/destination`, { method: 'PATCH', body: JSON.stringify({ destination }) }),
    // Absences
    demanderAbsence: async (body: { dateDebut: string; dateFin: string; demiDebut?: boolean; demiFin?: boolean; typeDemande?: string; motif?: string }, piece?: File): Promise<RhAbsence> => {
      if (!piece) {
        return apiFetch<RhAbsence>('/api/rh/absences', { method: 'POST', body: JSON.stringify(body) });
      }
      const fd = new FormData();
      fd.set('dateDebut', body.dateDebut); fd.set('dateFin', body.dateFin);
      if (body.demiDebut) fd.set('demiDebut', 'true');
      if (body.demiFin) fd.set('demiFin', 'true');
      if (body.typeDemande) fd.set('typeDemande', body.typeDemande);
      if (body.motif) fd.set('motif', body.motif);
      fd.set('piece', piece);
      const token = getAccessToken();
      const res = await fetch('/api/rh/absences', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', body: fd });
      if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Erreur' })));
      return res.json() as Promise<RhAbsence>;
    },
    justificatifAbsence: (id: string) => apiFetch<{ url: string }>(`/api/rh/absences/${id}/justificatif`),
    mesAbsences: () => apiFetch<RhAbsence[]>('/api/rh/absences/mes'),
    // ── M27 — demandes de formation (souhait, pas une absence) ──
    catalogueFormationsActif: () =>
      apiFetch<Array<{ id: string; intitule: string; organisme: string; domaine: string | null; dureeJours: number | null }>>(
        '/api/rh/formations/catalogue-actif'),
    demanderFormation: (body: { catalogueId?: string | null; intituleLibre?: string | null; periodeDu?: string | null; periodeAu?: string | null; commentaire?: string | null }) =>
      apiFetch<DemandeFormationDTO>('/api/rh/demandes-formation', { method: 'POST', body: JSON.stringify(body) }),
    mesDemandesFormation: () => apiFetch<DemandeFormationDTO[]>('/api/rh/mes-demandes-formation'),
    annulerDemandeFormation: (id: string) =>
      apiFetch<void>(`/api/rh/mes-demandes-formation/${id}/annuler`, { method: 'POST' }),
    demandesFormation: (statut?: string) =>
      apiFetch<DemandeFormationDTO[]>(`/api/rh/demandes-formation${statut ? `?statut=${statut}` : ''}`),
    // ── M28 — « Mon déplacement » : formation → ordre de mission → note de frais ──
    preremplirDeplacement: (src: { formationId?: string; demandeFormationId?: string }) =>
      apiFetch<DeplacementInput>(`/api/rh/deplacements/preremplir?${src.formationId ? `formationId=${src.formationId}` : `demandeFormationId=${src.demandeFormationId}`}`),
    creerDeplacement: (body: DeplacementInput) =>
      apiFetch<DeplacementDTO>('/api/rh/deplacements', { method: 'POST', body: JSON.stringify(body) }),
    mesDeplacements: () => apiFetch<DeplacementDTO[]>('/api/rh/mes-deplacements'),
    annulerDeplacement: (id: string) => apiFetch<void>(`/api/rh/mes-deplacements/${id}/annuler`, { method: 'POST' }),
    declarerFraisDeplacement: (id: string) =>
      apiFetch<{ noteId: string; creee: boolean }>(`/api/rh/mes-deplacements/${id}/note-frais`, { method: 'POST' }),
    deplacements: (statut?: string) => apiFetch<DeplacementDTO[]>(`/api/rh/deplacements${statut ? `?statut=${statut}` : ''}`),
    // ── M22 — suivis sur le dossier d'un agent (circuit + date butoir) ──
    suivisAgent: (agentId: string) => apiFetch<SuiviAgentDTO[]>(`/api/rh/dossiers-agents/${agentId}/suivis`),
    creerSuiviAgent: (agentId: string, body: {
      type: TypeSuivi; raison: string; objectif?: string | null; circuitId?: string | null;
      echeance?: { date: string; rappels: Array<{ quantite: number; unite: 'jours' | 'mois' | 'annees' }>; destinataireIds: string[]; prevenirAgent: boolean } | null;
    }) => apiFetch<SuiviAgentDTO>(`/api/rh/dossiers-agents/${agentId}/suivis`, { method: 'POST', body: JSON.stringify(body) }),
    cloreSuivi: (id: string, conclusion?: string | null) =>
      apiFetch<void>(`/api/rh/suivis/${id}/clore`, { method: 'POST', body: JSON.stringify({ conclusion }) }),
    statuerDeplacement: (id: string, body: { statut: 'valide' | 'refuse'; motif?: string | null }) =>
      apiFetch<DeplacementDTO>(`/api/rh/deplacements/${id}/statuer`, { method: 'POST', body: JSON.stringify(body) }),
    statuerDemandeFormation: (id: string, body: { statut: 'acceptee' | 'refusee'; reponse?: string | null }) =>
      apiFetch<DemandeFormationDTO>(`/api/rh/demandes-formation/${id}/statuer`, { method: 'POST', body: JSON.stringify(body) }),
    annulerAbsence: (id: string) => apiFetch<void>(`/api/rh/absences/${id}`, { method: 'DELETE' }),
    listerAbsences: (statut?: string) => apiFetch<RhAbsence[]>(`/api/rh/absences${statut ? `?statut=${statut}` : ''}`),
    statuerAbsence: (id: string, body: { statut: 'validee' | 'refusee'; typeValide?: 'conges_payes' | 'justifiee' | 'injustifiee'; compteFeries?: boolean; motifRefus?: string }) =>
      apiFetch<RhAbsence>(`/api/rh/absences/${id}/statut`, { method: 'PATCH', body: JSON.stringify(body) }),
    // Déclaration directe par un responsable (validée immédiatement)
    declarerAbsence: (body: { agentUserId: string; dateDebut: string; dateFin: string; motif?: string; typeValide?: 'conges_payes' | 'justifiee' | 'injustifiee'; service?: string }) =>
      apiFetch<{ absence: RhAbsence; tachesEnConflit: number }>('/api/rh/absences/declarer', { method: 'POST', body: JSON.stringify(body) }),
    getAlerteConfig: () => apiFetch<{ canal: 'cloche' | 'email' | 'les_deux' }>('/api/rh/alerte-config'),
    setAlerteConfig: (canal: 'cloche' | 'email' | 'les_deux') =>
      apiFetch<{ canal: string }>('/api/rh/alerte-config', { method: 'PUT', body: JSON.stringify({ canal }) }),
    // Compteurs (congés / RTT / récup)
    mesCompteurs: (annee: number) =>
      apiFetch<Array<{ type: string; libelle: string; soldeInitial: number; acquis: number; consomme: number; solde: number; unite: string }>>(`/api/rh/compteurs/mes?annee=${annee}`),
    compteursAgent: (agentId: string, annee: number) =>
      apiFetch<Array<{ type: string; libelle: string; soldeInitial: number; acquis: number; consomme: number; solde: number; unite: string }>>(`/api/rh/compteurs/${agentId}?annee=${annee}`),
    setSoldeInitial: (body: { agentUserId: string; annee: number; type: 'rtt' | 'recup_hs' | 'recup_ferie'; soldeInitial: number }) =>
      apiFetch<Array<{ type: string; libelle: string; solde: number; unite: string }>>('/api/rh/compteurs/solde-initial', { method: 'PUT', body: JSON.stringify(body) }),
    horairesModifies: (from?: string, to?: string) => {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from); if (to) qs.set('to', to);
      const q = qs.toString();
      return apiFetch<RhHoraireModifie[]>(`/api/rh/horaires-modifies${q ? `?${q}` : ''}`);
    },
    transmettreCreneaux: (items: Array<{ id: string; service: string }>) =>
      apiFetch<{ transmis: number }>('/api/rh/transmettre-creneaux', { method: 'POST', body: JSON.stringify({ items }) }),
    // ── Clôture de période de paie (LOT C) ──
    clotureParams: () => apiFetch<RhClotureParams>('/api/rh/cloture/parametres'),
    /**
     * Habilitations & permis (item 5.3).
     *
     * ⚠️ create/update passent en multipart : le document scanné accompagne les
     * champs. En multipart, tout est chaîne — d'où le `join(',')` sur les
     * offsets, que le serveur re-parse.
     */
    habilitations: {
      list: (filtre?: 'tous' | 'a_renouveler') =>
        apiFetch<RhHabilitation[]>(`/api/rh/habilitations${filtre === 'a_renouveler' ? '?filtre=a_renouveler' : ''}`),
      mes: () => apiFetch<RhHabilitation[]>('/api/rh/habilitations/mes'),
      types: () => apiFetch<RhHabilitationType[]>('/api/rh/habilitations/types'),
      createType: (libelle: string, dureeMois?: number | null) =>
        apiFetch<RhHabilitationType>('/api/rh/habilitations/types', {
          method: 'POST', body: JSON.stringify({ libelle, dureeMois }),
        }),
      toggleType: (id: string, actif: boolean) =>
        apiFetch<{ ok: true }>(`/api/rh/habilitations/types/${id}/actif`, {
          method: 'PUT', body: JSON.stringify({ actif }),
        }),
      piece: (id: string) => apiFetch<{ url: string }>(`/api/rh/habilitations/${id}/piece`),
      create: (data: Record<string, unknown>, fichier?: File) =>
        apiFetch<RhHabilitation>('/api/rh/habilitations', {
          method: 'POST', body: habilitationFormData(data, fichier),
        }),
      update: (id: string, data: Record<string, unknown>, fichier?: File) =>
        apiFetch<RhHabilitation>(`/api/rh/habilitations/${id}`, {
          method: 'PUT', body: habilitationFormData(data, fichier),
        }),
      remove: (id: string) =>
        apiFetch<{ ok: true }>(`/api/rh/habilitations/${id}`, { method: 'DELETE' }),
    },
    /**
     * Qui tranche les congés dans cette commune (migration 196).
     * `manager` = le manager valide seul · `rh_maire` = seuls RH/maire tranchent.
     */
    modeValidationConges: () =>
      apiFetch<{ mode: 'manager' | 'rh_maire' }>('/api/rh/absences/mode-validation'),
    setModeValidationConges: (mode: 'manager' | 'rh_maire') =>
      apiFetch<{ mode: 'manager' | 'rh_maire' }>('/api/rh/absences/mode-validation', {
        method: 'PUT', body: JSON.stringify({ mode }),
      }),
    /** Le manager donne son avis sans trancher (mode rh_maire). */
    avisManagerAbsence: (id: string, avis: string) =>
      apiFetch<RhAbsence>(`/api/rh/absences/${id}/avis`, {
        method: 'PUT', body: JSON.stringify({ avis }),
      }),
    updateClotureParams: (patch: Partial<RhClotureParams>) =>
      apiFetch<RhClotureParams>('/api/rh/cloture/parametres', { method: 'PUT', body: JSON.stringify(patch) }),
    periode: (annee: number, mois: number) =>
      apiFetch<RhPeriodePaie>(`/api/rh/cloture/periode?annee=${annee}&mois=${mois}`),
    cloturerService: (service: string, annee: number, mois: number) =>
      apiFetch<{ ok: true }>('/api/rh/cloture/service', { method: 'POST', body: JSON.stringify({ service, annee, mois }) }),
    cloturerDefinitif: (annee: number, mois: number) =>
      apiFetch<{ ok: true }>('/api/rh/cloture/cloturer', { method: 'POST', body: JSON.stringify({ annee, mois }) }),
    rouvrirPeriode: (annee: number, mois: number, motif: string) =>
      apiFetch<{ ok: true }>('/api/rh/cloture/rouvrir', { method: 'POST', body: JSON.stringify({ annee, mois, motif }) }),
    reportsPaie: (annee: number, mois: number) =>
      apiFetch<Array<{ agentNom: string; service: string; typeElement: string; montant: number; libelle: string | null; motif: string | null }>>(`/api/rh/cloture/reports?annee=${annee}&mois=${mois}`),
    // ── Vue agrégée Plannings (LOT B) ──
    agentsFiltrables: () =>
      apiFetch<{ agents: RhAgentFiltrable[]; services: string[]; managers: Array<{ id: string; nom: string }> }>('/api/rh/agents-filtrables'),
    planningsAgreges: (weekStart: string, filtres?: { service?: string; agentId?: string }) => {
      const qs = new URLSearchParams({ weekStart });
      if (filtres?.service) qs.set('service', filtres.service);
      if (filtres?.agentId) qs.set('agentId', filtres.agentId);
      return apiFetch<RhPlanningsAgreges>(`/api/rh/plannings-agreges?${qs.toString()}`);
    },
    /**
     * Mon planning individuel (toutes briques). Le serveur force le filtre sur
     * l'utilisateur connecté : on ne peut pas voir celui d'un autre par ici.
     */
    monPlanning: (weekStart: string) =>
      apiFetch<RhPlanningsAgreges>(`/api/rh/mon-planning?weekStart=${weekStart}`),
    // ── Référentiels RH : grades & cadres d'emploi (mig 232) ──
    /**
     * Les listes cadrées pour la RÉDACTION d'un acte.
     *
     * Distinct de `referentielsRh`, qui est un écran de gestion réservé aux RH :
     * un manager du service technique qui rédige un arrêté a besoin des mêmes
     * grades et des mêmes services, sans accéder aux réglages RH.
     */
    referentielsPourActes: () =>
      apiFetch<{
        grades: Array<{ id: string; libelle: string; echelonsMax: number | null }>;
        cadresEmploi: Array<{ id: string; libelle: string; categorie: string | null }>;
        services: Array<{ id: string; libelle: string; libelleOfficiel: string | null }>;
      }>('/api/rh/referentiels/pour-actes'),

    referentielsRh: (tous = false) =>
      apiFetch<RhReferentiels>(`/api/rh/referentiels${tous ? '?tous=true' : ''}`),
    creerGrade: (body: { libelle: string; cadreEmploiId?: string | null; echelonsMax?: number | null }) =>
      apiFetch<RhGrade>('/api/rh/referentiels/grades', { method: 'POST', body: JSON.stringify(body) }),
    /** Mig 266 — les services de la commune (organigramme, pas les briques). */
    creerService: (body: {
      libelle: string; libelleOfficiel?: string | null;
      brique?: string | null; ordre?: number;
    }) =>
      apiFetch<RhServiceRef>('/api/rh/referentiels/services',
        { method: 'POST', body: JSON.stringify(body) }),
    creerCadreEmploi: (body: { libelle: string; filiere?: string | null }) =>
      apiFetch<RhCadreEmploi>('/api/rh/referentiels/cadres-emploi', { method: 'POST', body: JSON.stringify(body) }),
    /** Item 5.18 (mig 291) — ordres de mission, réutilisés dans les notes de frais. */
    creerOrdreMission: (body: { libelle: string }) =>
      apiFetch<RhOrdreMission>('/api/rh/referentiels/ordres-mission', { method: 'POST', body: JSON.stringify(body) }),
    basculerReferentiel: (type: 'grade' | 'cadre' | 'ordre_mission', id: string, actif: boolean) =>
      apiFetch<{ ok: boolean }>(`/api/rh/referentiels/${type}/${id}/actif`,
        { method: 'PUT', body: JSON.stringify({ actif }) }),
    /**
     * MON dossier — lecture seule, déduite du compte connecté.
     *
     * Aucun identifiant en paramètre : c'est le serveur qui décide de quel
     * dossier il s'agit. Un agent ne peut donc pas viser celui d'un collègue.
     * `agent: null` = le compte n'a pas de dossier agent (un maire qui n'est
     * pas employé de la commune, par exemple).
     */
    monDossier: () =>
      apiFetch<{
        agent: RhAgentDossier | null;
        anciennetes: {
          communeMois: number | null; fonctionPubliqueMois: number | null;
          dateEntreeCommune: string | null; dateEntreeFonctionPublique: string | null;
        } | null;
        avancements: Array<{
          id: string; grade: string | null; echelon: number | null;
          dateEffet: string; dateProchainAvancement: string | null;
          commentaire: string | null;
        }>;
        contrats: Array<{
          id: string; typeContrat: string; dateDebut: string; dateFin: string | null;
          motif: string | null; dureeMois: number | null;
        }>;
      }>('/api/rh/mon-dossier'),

    // ── Dossiers agents : le socle RH (mig 230/231) ──
    dossiersAgents: (inactifs = false) =>
      apiFetch<RhAgentDossier[]>(`/api/rh/dossiers-agents${inactifs ? '?inactifs=true' : ''}`),
    dossierAgent: (id: string) => apiFetch<RhAgentDossier>(`/api/rh/dossiers-agents/${id}`),
    creerDossierAgent: (body: RhAgentDossierInput) =>
      apiFetch<RhAgentDossier>('/api/rh/dossiers-agents', { method: 'POST', body: JSON.stringify(body) }),
    modifierDossierAgent: (id: string, body: Partial<RhAgentDossierInput>) =>
      apiFetch<RhAgentDossier>(`/api/rh/dossiers-agents/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    supprimerDossierAgent: (id: string) =>
      apiFetch<void>(`/api/rh/dossiers-agents/${id}`, { method: 'DELETE' }),
    telechargerAgentsExcel: () =>
      downloadCompta('/api/rh/dossiers-agents/export.xlsx', 'dossiers-agents.xlsx'),
    telechargerModeleAgents: () =>
      downloadCompta('/api/rh/dossiers-agents/modele.xlsx', 'modele-agents.xlsx'),
    /** Import Excel — le point de départ d'une commune : dossiers en masse
     *  + rattachement des comptes existants + invitations envoyées d'un coup. */
    importerAgentsExcel: (fichier: File) => {
      const fd = new FormData();
      fd.append('fichier', fichier);
      return apiFetchRaw<{
        total: number; crees: number; majs: number; comptesCrees: number;
        habilitations: number;
        erreurs: Array<{ ligne: number; message: string }>;
        invitationsEnvoyees: number;
        invitationsErreurs: Array<{ email: string; raison: string }>;
      }>('/api/rh/dossiers-agents/import', { method: 'POST', body: fd });
    },
    // ── Visites médicales & entretien professionnel (item 5.5) ──
    visitesMedicales: (agentUserId?: string) =>
      apiFetch<RhVisiteMedicale[]>(`/api/rh/visites-medicales${agentUserId ? `?agentUserId=${agentUserId}` : ''}`),
    creerVisiteMedicale: (body: RhVisiteMedicaleInput) =>
      apiFetch<RhVisiteMedicale>('/api/rh/visites-medicales', { method: 'POST', body: JSON.stringify(body) }),
    modifierVisiteMedicale: (id: string, body: Partial<RhVisiteMedicaleInput>) =>
      apiFetch<RhVisiteMedicale>(`/api/rh/visites-medicales/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    supprimerVisiteMedicale: (id: string) =>
      apiFetch<void>(`/api/rh/visites-medicales/${id}`, { method: 'DELETE' }),
    suiviReglages: () => apiFetch<RhSuiviReglages>('/api/rh/suivi-reglages'),
    setSuiviReglages: (body: Partial<RhSuiviReglages>) =>
      apiFetch<RhSuiviReglages>('/api/rh/suivi-reglages', { method: 'PUT', body: JSON.stringify(body) }),
    synchroniserEntretiensPro: () =>
      apiFetch<{ programmes: number; alertes: number }>('/api/rh/entretiens-pro/synchroniser', { method: 'POST', body: JSON.stringify({}) }),
    presenceAnneeAgregee: (agentId: string, annee: number) =>
      apiFetch<RhPresenceAnnee>(`/api/rh/presence-annee-agregee?agentId=${agentId}&annee=${annee}`),
    /**
     * MON année, toutes briques confondues — pour la vue année de l'espace RH
     * personnel. Pas d'`agentId` : l'identité vient du token côté serveur, un
     * agent ne peut pas demander l'année d'un collègue.
     *
     * Adapte le format de l'agrégat RH vers `PlanningAnneeResponse`, celui que
     * `PlanningPresence` sait afficher. Les deux ne divergent que sur deux
     * libellés de statut (`presence`/`present`, `vide`/`non_planifie`) : la
     * conversion vit ICI plutôt que dans le composant, pour que la vue année
     * reste identique quelle que soit sa source (brique ou RH agrégé).
     */
    monAnnee: async (annee: number): Promise<PlanningAnneeResponse> => {
      const r = await apiFetch<RhPresenceAnnee>(`/api/rh/mon-annee?annee=${annee}`);
      const MAP: Record<string, PlanningAnneeResponse['jours'][number]['statut']> = {
        presence: 'present', conge: 'conge', absence: 'absence',
        ferie: 'ferie', weekend: 'weekend', vide: 'non_planifie',
      };
      return {
        annee: r.annee,
        agent: { id: r.agent.id, fullName: r.agent.nom, role: 'agent', service: null },
        jours: r.jours.map((j) => ({
          date: j.date,
          statut: MAP[j.statut] ?? 'non_planifie',
          libelle: j.libelle || null,
        })),
        compteurs: {
          presence: r.compteurs.presence ?? 0,
          conges: r.compteurs.conges ?? 0,
          absences: r.compteurs.absences ?? 0,
        },
      };
    },
    /** Télécharge MON planning annuel en PDF (charte mairie). */
    telechargerMonAnneePdf: async (annee: number): Promise<void> => {
      await downloadCompta(`/api/rh/mon-annee.pdf?annee=${annee}`, `mon-planning-${annee}.pdf`);
    },
    // ── Export paie + clés API (LOT D) ──
    telechargerExportPaie: async (annee: number, mois: number, service?: string): Promise<void> => {
      const qs = new URLSearchParams({ annee: String(annee), mois: String(mois) });
      if (service) qs.set('service', service);
      const token = getAccessToken();
      const res = await fetch(`/api/rh/export-paie.xlsx?${qs.toString()}`, {
        method: 'GET', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include',
      });
      if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Export échoué' })));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `paie_${annee}_${String(mois).padStart(2, '0')}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    },
    apiKeys: () => apiFetch<Array<{ id: string; label: string; prefixe: string; actif: boolean; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }>>('/api/rh/api-keys'),
    creerApiKey: (label: string) => apiFetch<{ id: string; secret: string; prefixe: string }>('/api/rh/api-keys', { method: 'POST', body: JSON.stringify({ label }) }),
    revoquerApiKey: (id: string) => apiFetch<{ ok: true }>(`/api/rh/api-keys/${id}`, { method: 'DELETE' }),
    // ── Profils horaires agrégés (LOT E) ──
    profilsAgreges: () => apiFetch<RhProfilAgrege[]>('/api/rh/profils-agreges'),
    profilsGouvernance: () => apiFetch<{ modifiablesParBriques: boolean }>('/api/rh/profils-gouvernance'),
    setProfilsGouvernance: (modifiablesParBriques: boolean) =>
      apiFetch<{ modifiablesParBriques: boolean }>('/api/rh/profils-gouvernance', { method: 'PUT', body: JSON.stringify({ modifiablesParBriques }) }),
    verifierProfil: (plages: Array<{ jour: number; debut: string; fin: string; titre?: string }>) =>
      apiFetch<{ conforme: boolean; motifs: string[]; heuresSemaine: number }>('/api/rh/profils-verifier', { method: 'POST', body: JSON.stringify({ plages }) }),
    creerProfilRh: (service: string, nom: string, plages: Array<{ jour: number; debut: string; fin: string; titre?: string }>) =>
      apiFetch<{ id: string }>('/api/rh/profils', { method: 'POST', body: JSON.stringify({ service, nom, plages }) }),
    // ── Justificatifs d'arrêts + documents (LOT F) ──
    transmettreJustificatif: (body: { type: string; dateDebut: string; dateFin: string; commentaire?: string }) =>
      apiFetch<{ id: string }>('/api/rh/justificatifs', { method: 'POST', body: JSON.stringify(body) }),
    listJustificatifs: (statut?: string) =>
      apiFetch<RhJustificatif[]>(`/api/rh/justificatifs${statut ? `?statut=${statut}` : ''}`),
    mesJustificatifs: () => apiFetch<RhJustificatif[]>('/api/rh/mes-justificatifs'),
    statuerJustificatif: (id: string, statut: 'valide' | 'refuse', commentaire?: string) =>
      apiFetch<{ ok: true; absenceId?: string }>(`/api/rh/justificatifs/${id}/statuer`, { method: 'POST', body: JSON.stringify({ statut, commentaire }) }),
    /** Filtres 5.11 : catégorie, statut, service, recherche par titre. */
    listDocuments: (filtres: {
      categorie?: string;
      statut?: 'en_vigueur' | 'abroge';
      service?: string;
      recherche?: string;
    } = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(filtres)) if (v) q.set(k, v);
      const qs = q.toString();
      return apiFetch<RhDocument[]>(`/api/rh/documents${qs ? `?${qs}` : ''}`);
    },

    /** Diffusion ciblée d'une note de service (5.9). */
    diffuserDocument: (id: string) =>
      apiFetch<{ destinataires: number }>(`/api/rh/documents/${id}/diffuser`, {
        method: 'POST',
      }),
    accuserLectureDocument: (id: string) =>
      apiFetch<{ ok: true }>(`/api/rh/documents/${id}/lu`, { method: 'POST' }),
    lecturesDocument: (id: string) =>
      apiFetch<{ lus: Array<{ nom: string; luAt: string }>; nonLus: string[] }>(
        `/api/rh/documents/${id}/lectures`),

    /** Abroger sans remplacer (5.10). */
    abrogerDocument: (id: string, dateAbrogation?: string) =>
      apiFetch<{ ok: true }>(`/api/rh/documents/${id}/abroger`, {
        method: 'POST', body: JSON.stringify({ dateAbrogation }),
      }),
    creerDocument: (body: {
      titre: string; type: string;
      visibilite: 'agents' | 'rh';
      modeAcces: 'telechargeable' | 'lecture_seule';
      // Mig 239 — catégorie, date d'effet, ciblage, remplacement.
      categorie?: string; dateEffet?: string;
      services?: string[]; remplaceId?: string;
    }, fichier?: File | null) => {
      const fd = new FormData();
      fd.append('titre', body.titre); fd.append('type', body.type);
      fd.append('visibilite', body.visibilite); fd.append('modeAcces', body.modeAcces);
      if (body.categorie) fd.append('categorie', body.categorie);
      if (body.dateEffet) fd.append('dateEffet', body.dateEffet);
      if (body.remplaceId) fd.append('remplaceId', body.remplaceId);
      // Un tableau ne survit pas tel quel à FormData : on l'encode en JSON, et
      // la route sait le relire.
      if (body.services?.length) fd.append('services', JSON.stringify(body.services));
      if (fichier) fd.append('fichier', fichier);
      const token = getAccessToken();
      return fetch('/api/rh/documents', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include', body: fd })
        .then(async (res) => { if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Erreur' }))); return res.json() as Promise<{ id: string }>; });
    },
    supprimerDocument: (id: string) => apiFetch<{ ok: true }>(`/api/rh/documents/${id}`, { method: 'DELETE' }),
    documentFichier: (id: string) => apiFetch<{ url: string }>(`/api/rh/documents/${id}/fichier`),
    telechargerDocumentEntete: async (): Promise<void> => {
      const token = getAccessToken();
      const res = await fetch('/api/rh/modeles/document-entete.docx', { method: 'GET', headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' });
      if (!res.ok) throw new ApiException(res.status, await res.json().catch(() => ({ error: 'Erreur' })));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'document-entete-mairie.docx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    },
  },
  health: () =>
    apiFetch<{ status: string; database: string; redis: string; uptime: number }>('/health'),

  associations: {
    /**
     * 9.3 — les accompagnements proposés par la mairie.
     *
     * `catalogue` rend l'offre du jour ; `parametrage` rend tout, y compris ce
     * qui a été retiré. Deux routes distinctes, pas un drapeau : une route dont
     * le contenu dépend du demandeur est une route dont on ne sait plus ce
     * qu'elle rend.
     */
    services: {
      catalogue: () =>
        apiFetch<{ services: ServiceMairieDTO[] }>('/api/associations/services'),
      parametrage: () =>
        apiFetch<{ services: ServiceMairieDTO[] }>('/api/associations/services/parametrage'),
      creer: (body: OffreMairieInput) =>
        apiFetch<{ id: string }>('/api/associations/services',
          { method: 'POST', body: JSON.stringify(body) }),
      maj: (id: string, body: OffreMairieInput) =>
        apiFetch<{ ok: true }>(`/api/associations/services/${id}`,
          { method: 'PUT', body: JSON.stringify(body) }),
      ajouterPiece: (serviceId: string, body: { libelle: string; obligatoire?: boolean; ordre?: number }) =>
        apiFetch<{ id: string }>(`/api/associations/services/${serviceId}/pieces`,
          { method: 'POST', body: JSON.stringify(body) }),
      supprimerPiece: (id: string) =>
        apiFetch<void>(`/api/associations/services/pieces/${id}`, { method: 'DELETE' }),

      demandesRecues: (statut?: string) =>
        apiFetch<{ demandes: DemandeServiceDTO[] }>(
          `/api/associations/services/demandes${statut ? `?statut=${statut}` : ''}`),
      mesDemandes: () =>
        apiFetch<{ demandes: DemandeServiceDTO[] }>('/api/associations/services/mes-demandes'),
      demander: (body: { serviceId: string; message?: string | null; beneficiaireType?: string; beneficiaireId?: string | null; beneficiaireNom?: string | null }) =>
        apiFetch<{ id: string }>('/api/associations/services/demandes',
          { method: 'POST', body: JSON.stringify(body) }),
      deposerPiece: (demandeId: string, file: File, pieceId?: string) => {
        const fd = new FormData();
        fd.append('fichier', file, file.name);
        if (pieceId) fd.append('pieceId', pieceId);
        return apiFetchRaw<{ id: string; nomFichier: string }>(
          `/api/associations/services/demandes/${demandeId}/pieces`,
          { method: 'POST', body: fd });
      },
      soumettre: (id: string) =>
        apiFetch<{ ok: true }>(`/api/associations/services/demandes/${id}/soumettre`,
          { method: 'POST' }),
      traiter: (id: string, body: { statut: 'acceptee' | 'refusee' | 'cloturee'; reponse?: string | null; motifRefus?: string | null }) =>
        apiFetch<{ ok: true }>(`/api/associations/services/demandes/${id}/traiter`,
          { method: 'POST', body: JSON.stringify(body) }),
      urlPiece: (id: string) =>
        apiFetch<{ url: string }>(`/api/associations/services/pieces/${id}/url`),
    },
    // Acteurs locaux & producteurs (migration 131).
    acteurs: {
      etat: () => apiFetch<ActeursEtatMairie>('/api/associations/acteurs/etat'),
      list: (params?: { categorie?: string; q?: string; statutValidation?: string }) => {
        const qs = new URLSearchParams();
        if (params?.categorie) qs.set('categorie', params.categorie);
        if (params?.q) qs.set('q', params.q);
        if (params?.statutValidation) qs.set('statutValidation', params.statutValidation);
        const q = qs.toString();
        return apiFetch<{ acteurs: ActeurLocal[] }>(`/api/associations/acteurs${q ? `?${q}` : ''}`);
      },
      get: (id: string) => apiFetch<ActeurLocal>(`/api/associations/acteurs/${id}`),
      create: (body: Record<string, unknown>) =>
        apiFetch<ActeurLocal>('/api/associations/acteurs', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: Record<string, unknown>) =>
        apiFetch<ActeurLocal>(`/api/associations/acteurs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      remove: (id: string) =>
        apiFetch<void>(`/api/associations/acteurs/${id}`, { method: 'DELETE' }),
      valider: (id: string) =>
        apiFetch<ActeurLocal>(`/api/associations/acteurs/${id}/valider`, { method: 'POST' }),
      refuser: (id: string, motif?: string) =>
        apiFetch<ActeurLocal>(`/api/associations/acteurs/${id}/refuser`, { method: 'POST', body: JSON.stringify({ motif: motif ?? null }) }),
      addProduit: (acteurId: string, body: Record<string, unknown>) =>
        apiFetch<ActeurProduit>(`/api/associations/acteurs/${acteurId}/produits`, { method: 'POST', body: JSON.stringify(body) }),
      updateProduit: (id: string, body: Record<string, unknown>) =>
        apiFetch<ActeurProduit>(`/api/associations/acteurs/produits/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
      removeProduit: (id: string) =>
        apiFetch<void>(`/api/associations/acteurs/produits/${id}`, { method: 'DELETE' }),
    },
    // API Entreprise (données certifiées de l'État) : vérif identité + subventions.
    verificationEntreprise: (id: string, siret?: string) => {
      const qs = siret ? `?siret=${encodeURIComponent(siret)}` : '';
      return apiFetch<AssociationVerifiee>(`/api/associations/${id}/verification-entreprise${qs}`);
    },
    subventionsEntreprise: (id: string, opts?: { siret?: string; rna?: string }) => {
      const q = new URLSearchParams();
      if (opts?.siret) q.set('siret', opts.siret);
      if (opts?.rna) q.set('rna', opts.rna);
      const qs = q.toString();
      return apiFetch<SubventionsAssoRecuperees>(`/api/associations/${id}/subventions-entreprise${qs ? `?${qs}` : ''}`);
    },
    list: (params?: { domaine?: string; actif?: boolean; q?: string; statutValidation?: 'active' | 'en_attente' | 'refusee' | 'all' }) => {
      const qs = new URLSearchParams();
      if (params?.domaine) qs.set('domaine', params.domaine);
      if (params?.actif !== undefined) qs.set('actif', String(params.actif));
      if (params?.q) qs.set('q', params.q);
      if (params?.statutValidation) qs.set('statutValidation', params.statutValidation);
      const q = qs.toString();
      return apiFetch<AssociationResume[]>(`/api/associations${q ? `?${q}` : ''}`);
    },
    get: (id: string) => apiFetch<AssociationDetail>(`/api/associations/${id}`),
    create: (body: UpsertAssociationInput) =>
      apiFetch<AssociationDetail>('/api/associations', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: UpsertAssociationInput) =>
      apiFetch<AssociationDetail>(`/api/associations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    desactiver: (id: string) =>
      apiFetch<void>(`/api/associations/${id}`, { method: 'DELETE' }),
    reactiver: (id: string) =>
      apiFetch<void>(`/api/associations/${id}/reactiver`, { method: 'POST' }),
    supprimerDefinitif: (id: string) =>
      apiFetch<void>(`/api/associations/${id}/definitif`, { method: 'DELETE' }),
    // Représentants (L-ASSO-1.2)
    mesAssociations: () => apiFetch<MonAssociation[]>('/api/associations/mes-associations'),
    demanderRattachement: (id: string, fonction: RepresentantFonction) =>
      apiFetch<AssociationRepresentant>(`/api/associations/${id}/representants/demande`, {
        method: 'POST', body: JSON.stringify({ fonction }),
      }),
    ajouterRepresentant: (id: string, email: string, fonction: RepresentantFonction) =>
      apiFetch<AssociationRepresentant>(`/api/associations/${id}/representants`, {
        method: 'POST', body: JSON.stringify({ email, fonction }),
      }),
    statuerRepresentant: (id: string, repId: string, decision: 'valide' | 'refuse') =>
      apiFetch<AssociationRepresentant>(`/api/associations/${id}/representants/${repId}`, {
        method: 'PATCH', body: JSON.stringify({ decision }),
      }),
    retirerRepresentant: (id: string, repId: string) =>
      apiFetch<void>(`/api/associations/${id}/representants/${repId}`, { method: 'DELETE' }),
    // Adhésion validée par le BUREAU de l'asso (étape 3)
    demandesAdhesion: (id: string) =>
      apiFetch<AssociationRepresentant[]>(`/api/associations/${id}/demandes-adhesion`),
    statuerAdhesion: (id: string, repId: string, decision: 'valide' | 'refuse') =>
      apiFetch<AssociationRepresentant>(`/api/associations/${id}/adhesions/${repId}`, {
        method: 'PATCH', body: JSON.stringify({ decision }),
      }),
    changerPosteMembre: (id: string, repId: string, fonction: RepresentantFonction) =>
      apiFetch<AssociationRepresentant>(`/api/associations/${id}/membres/${repId}/poste`, {
        method: 'PATCH', body: JSON.stringify({ fonction }),
      }),
    setMembrePeutPublier: (id: string, repId: string, peutPublier: boolean) =>
      apiFetch<AssociationRepresentant>(`/api/associations/${id}/membres/${repId}/peut-publier`, {
        method: 'PATCH', body: JSON.stringify({ peutPublier }),
      }),
    retirerMembre: (id: string, repId: string) =>
      apiFetch<void>(`/api/associations/${id}/membres/${repId}`, { method: 'DELETE' }),
    setContactVisible: (id: string, visible: boolean) =>
      apiFetch<void>(`/api/associations/${id}/contact-visible`, { method: 'PATCH', body: JSON.stringify({ visible }) }),
    // Demande de création par un citoyen (L-ASSO-1.3)
    demanderCreation: (body: UpsertAssociationInput & { maFonction: RepresentantFonction; coRepresentants?: { email: string; fonction: RepresentantFonction }[] }) =>
      apiFetch<AssociationDetail>('/api/associations/demande-creation', { method: 'POST', body: JSON.stringify(body) }),
    statuerCreation: (id: string, decision: 'valide' | 'refuse') =>
      apiFetch<void>(`/api/associations/${id}/statuer-creation`, { method: 'POST', body: JSON.stringify({ decision }) }),
    // Dossier de validation & pièces (chantier A)
    dossiers: {
      list: (statut?: AssociationDossierStatut) =>
        apiFetch<AssociationDossier[]>(`/api/associations/dossiers${statut ? `?statut=${statut}` : ''}`),
      valider: (dossierId: string, decision: 'valide' | 'refuse', motifRefus?: string) =>
        apiFetch<AssociationDossier>(`/api/associations/dossiers/${dossierId}/valider`, { method: 'POST', body: JSON.stringify({ decision, motifRefus }) }),
      getDossier: (associationId: string) =>
        apiFetch<AssociationDossierDetail>(`/api/associations/${associationId}/dossier`),
      soumettre: (associationId: string) =>
        apiFetch<AssociationDossier>(`/api/associations/${associationId}/dossier/soumettre`, { method: 'POST', body: JSON.stringify({}) }),
      listPiecesDemandees: () => apiFetch<AssociationPieceDemandee[]>('/api/associations/dossiers/pieces-demandees'),
      createPieceDemandee: (libelle: string, obligatoire = true) =>
        apiFetch<AssociationPieceDemandee>('/api/associations/dossiers/pieces-demandees', { method: 'POST', body: JSON.stringify({ libelle, obligatoire }) }),
      deletePieceDemandee: (id: string) =>
        apiFetch<void>(`/api/associations/dossiers/pieces-demandees/${id}`, { method: 'DELETE' }),
      deposerPiece: async (associationId: string, file: File, pieceDemandeeId?: string): Promise<AssociationPieceDeposee> => {
        const token = getAccessToken();
        const fd = new FormData();
        fd.append('fichier', file);
        if (pieceDemandeeId) fd.append('pieceDemandeeId', pieceDemandeeId);
        const res = await fetch(`/api/associations/${associationId}/dossier/pieces`, {
          method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new ApiException(res.status, { error: b.error ?? `HTTP ${res.status}`, code: b.code ?? 'UPLOAD_FAILED' });
        }
        return res.json();
      },
      pieceUrl: (pieceId: string) => apiFetch<{ url: string }>(`/api/associations/dossiers/pieces/${pieceId}/url`),
      cocherPiece: (pieceId: string, conforme: boolean) =>
        apiFetch<AssociationPieceDeposee>(`/api/associations/dossiers/pieces/${pieceId}/conforme`, { method: 'PATCH', body: JSON.stringify({ conforme }) }),
    },
    // Subventions (Lot 2)
    subventions: {
      list: (statut?: SubventionStatut, exercice?: number) => {
        const qs = new URLSearchParams();
        if (statut) qs.set('statut', statut);
        if (exercice) qs.set('exercice', String(exercice));
        const q = qs.toString();
        return apiFetch<Subvention[]>(`/api/associations/subventions${q ? `?${q}` : ''}`);
      },
      mesSubventions: (associationId: string) => apiFetch<Subvention[]>(`/api/associations/${associationId}/subventions`),
      // ── Campagne de subventions (item 9.6) ──
      campagnes: () => apiFetch<SubventionCampagne[]>('/api/associations/subventions/campagnes'),
      enregistrerCampagne: (body: SubventionCampagneInput) =>
        apiFetch<SubventionCampagne>('/api/associations/subventions/campagnes', {
          method: 'PUT', body: JSON.stringify(body),
        }),
      /** Marque le compte rendu financier comme reçu — coupe la relance. */
      marquerJustificatif: (id: string, dateRecu?: string) =>
        apiFetch<void>(`/api/associations/subventions/${id}/justificatif`, {
          method: 'PATCH', body: JSON.stringify({ dateRecu: dateRecu ?? null }),
        }),
      create: (body: SubventionUpsertInput) => apiFetch<Subvention>('/api/associations/subventions', { method: 'POST', body: JSON.stringify(body) }),
      update: (id: string, body: SubventionUpsertInput) => apiFetch<Subvention>(`/api/associations/subventions/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
      soumettre: (id: string) => apiFetch<Subvention>(`/api/associations/subventions/${id}/soumettre`, { method: 'POST', body: JSON.stringify({}) }),
      instruire: (id: string) => apiFetch<Subvention>(`/api/associations/subventions/${id}/instruire`, { method: 'POST', body: JSON.stringify({}) }),
      decision: (id: string, decision: 'accordee' | 'refusee', montantAccorde?: number, motifDecision?: string) =>
        apiFetch<Subvention>(`/api/associations/subventions/${id}/decision`, { method: 'POST', body: JSON.stringify({ decision, montantAccorde, motifDecision }) }),
      versement: (id: string, verseLe: string, posteId: string, referencePaiement?: string) =>
        apiFetch<Subvention>(`/api/associations/subventions/${id}/versement`, { method: 'POST', body: JSON.stringify({ verseLe, posteId, referencePaiement }) }),
      annulerVersement: (id: string, motif?: string) =>
        apiFetch<Subvention>(`/api/associations/subventions/${id}/annuler-versement`, { method: 'POST', body: JSON.stringify({ motif }) }),
      exportScdlCsv: (exercice?: number) =>
        downloadCompta(`/api/associations/subventions/export-scdl.csv${exercice ? `?exercice=${exercice}` : ''}`, `subventions-scdl${exercice ? `-${exercice}` : ''}.csv`),
      exportScdlJson: (exercice?: number) =>
        downloadCompta(`/api/associations/subventions/export-scdl.json${exercice ? `?exercice=${exercice}` : ''}`, `subventions-scdl${exercice ? `-${exercice}` : ''}.json`),
    },
    // Annuaire citoyen + abonnements + annonces (chantier E)
    annuaire: {
      list: (recherche?: string, domaine?: string) => {
        const qs = new URLSearchParams();
        if (recherche) qs.set('recherche', recherche);
        if (domaine) qs.set('domaine', domaine);
        const q = qs.toString();
        return apiFetch<AssociationAnnuaireItem[]>(`/api/associations/annuaire${q ? `?${q}` : ''}`);
      },
      fiche: (id: string) => apiFetch<AssociationAnnuaireItem>(`/api/associations/annuaire/${id}`),
      toggleAbonnement: (id: string) => apiFetch<{ abonne: boolean }>(`/api/associations/annuaire/${id}/abonnement`, { method: 'POST', body: JSON.stringify({}) }),
      annonces: (id: string) => apiFetch<AssociationAnnonce[]>(`/api/associations/annuaire/${id}/annonces`),
      publierAnnonce: (id: string, payload: {
        titre: string; corps: string;
        typePublication?: AssociationPublicationType;
        dateEvent?: string | null; lieu?: string | null; horaires?: string | null;
        imageMediaKey?: string | null; avecPresence?: boolean;
      }) =>
        apiFetch<AssociationAnnonce>(`/api/associations/annuaire/${id}/annonces`, { method: 'POST', body: JSON.stringify(payload) }),
      uploadFlyer: (id: string, file: File) => {
        const fd = new FormData();
        fd.append('fichier', file);
        return apiFetchRaw<{ imageMediaKey: string; imageUrl: string | null }>(
          `/api/associations/annuaire/${id}/annonces/flyer`, { method: 'POST', body: fd });
      },
      repondrePresence: (annonceId: string, reponse: AssociationPresenceReponse) =>
        apiFetch<{ reponse: AssociationPresenceReponse }>(
          `/api/associations/annuaire/annonces/${annonceId}/presence`, { method: 'POST', body: JSON.stringify({ reponse }) }),
      recapPresences: (id: string, annonceId: string) =>
        apiFetch<AssociationPresenceRecap>(`/api/associations/annuaire/${id}/annonces/${annonceId}/presences`),
    },
    dashboard: () => apiFetch<AssociationsDashboard>('/api/associations/dashboard'),
  },
  // ---------- RGPD — conservation & purge (migration 140) ----------
  rgpd: {
    getConfig: () => apiFetch<RgpdConfig>('/api/rgpd/config'),
    putConfig: (cfg: RgpdConfig) =>
      apiFetch<RgpdConfig>('/api/rgpd/config', { method: 'PUT', body: JSON.stringify(cfg) }),
    journal: () => apiFetch<{ entries: RgpdPurgeLogEntry[] }>('/api/rgpd/journal'),
    preview: () => apiFetch<{ results: RgpdPurgeResult[] }>('/api/rgpd/preview', { method: 'POST' }),
    run: () => apiFetch<{ skipped: boolean; results: RgpdPurgeResult[] }>('/api/rgpd/run', { method: 'POST' }),
  },
};

export interface RgpdConfig {
  purgeActive: boolean;
  signalementsMois: number;
  piecesScolaireMois: number;
  piecesAssoMois: number;
  piecesDechetterieMois: number;
  comptesInactifsMois: number;
  comptesInactifsActive: boolean;
  notesFraisPiecesMois: number;
  notesFraisPiecesActive: boolean;
  justificatifsRhMois: number;
  justificatifsRhActive: boolean;
}
export interface RgpdPurgeResult {
  regle: string;
  cibleTable: string;
  objetsPurges: number;
  lignesTraitees: number;
  detail?: string;
}
export interface RgpdPurgeLogEntry {
  id: string;
  regle: string;
  cible_table: string;
  objets_purges: number;
  lignes_traitees: number;
  detail: string | null;
  created_at: string;
}

/** Alias public de l'utilitaire de téléchargement authentifié. */
export { downloadCompta as downloadFichier };
